var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defaults } from '../../defaults';
import { callTypes } from '../../kaylee/CallType';
import { isVideoInputInternal, } from '../../kaylee/DriverCaps';
import { channelsConfigNames, } from '../../kaylee/EquipmentConfig';
import { isValid, } from '../../kaylee/EquipmentStates';
import { Log } from '../../log';
import { Sutl } from '../../sutl';
import { CallUpdateManager } from './CallUpdateManager';
import { DirectoryManager } from './DirectoryManager';
import Drivers from './Drivers';
import { EquipmentServiceDriverAdapter, getDriver, } from './drivers/framework/EquipmentServiceDriverAdapter';
import { Timeouts } from './drivers/framework/Timeouts';
import Simulator from './drivers/Simulator';
import { evaluateParsedRoomControlCommand, parseInterpolatedRoomControlCommands, } from './RoomMacroParser';
import { dialStringIsInSynergySkyFormat, manuallyProvisionMeeting, translateSynergySkyCall, } from './SynergySky';
export class EquipmentServiceImpl {
    constructor(kayleeService, roomService, 
    // Passing in AutomationService is a temporary hack to handle room power off events.
    // Eventually we will add room events to the database and AutomationService will subscribe
    // to them. For now though, we have EquipmentService manually notify AutomationService when
    // room power off happens.
    automationService, translator, onDeviceShutdown, onBeforeUnload, isBrowser) {
        // It is common to use the foo(() => callback) syntax when specifying callback functions
        // to ensure that this is properly bound. However, many of the callback registrations we
        // use (e.g,, FirebaseObject) require the function to be used as the key for deregistration.
        // Thus we must explicitly bind our callback functions below. We do this for all callbacks
        // in order to be consistent.
        this.kayleeService = kayleeService;
        this.roomService = roomService;
        this.automationService = automationService;
        this.translator = translator;
        this.isBrowser = isBrowser;
        this.addEquipmentTimeouts = new Timeouts();
        this.callExtendedTypeOverrides = {};
        this.cameraSwitchersByCameraId = {};
        this.callUpdateManager = new CallUpdateManager();
        this.caps = {};
        this.configuredCaps = {};
        this.connectStates = {};
        // contactClosureTimeouts[equipmentId][displayMotionId][actionId]
        this.contactClosureTimeouts = {};
        this.contentStateValues = [];
        this.devices = {};
        this.devicesByAudioInputSourceId = {};
        this.devicesByCallId = {};
        this.devicesByCallType = {};
        this.devicesByCameraId = {};
        this.devicesByCameraPresetId = {};
        this.devicesByLightingControlId = {};
        this.devicesByLightingPresetId = {};
        this.devicesByOverflowRoomId = {};
        this.devicesBySoundReinforcementId = {};
        this.dialNumberAfterPrefixTimeouts = new Timeouts();
        this.dialStringOverrides = {};
        this.directoryManager = new DirectoryManager();
        this.driverValidated = {};
        this.equipmentIdsInRoomConfig = [];
        this.extraCallInfo = {};
        this.hangupSynchronizedCallTimeouts = new Timeouts();
        this.initialSpotlightSent = {};
        this.isOnline = false;
        this.log = Log.channel('EquipmentServiceImpl');
        this.mutes = {}; // equipmentId -> mute
        this.muteSyncDevices = [];
        this.offlineDevices = {};
        this.onlineDevices = {};
        this.parsedRoomControlCommands = {};
        this.pendingCallExtendedTypes = {};
        this.powerByEqId = {};
        this.removeEquipmentTimeouts = new Timeouts();
        this.savedCaps = {};
        this.sourceMenus = {}; // eqId -> menus
        this.synchronizedCallDevices = {
            googleMeet: {},
            pstn: {},
            teams: {},
            video: {},
            voip: {},
            zoom: {},
        };
        this.synchronizedCalls = {};
        this.synchronizedCallsPlaced = {}; // callId -> boolean
        this.timeouts = new Timeouts();
        this.uiPreviewControllerId = Sutl.generateFirebaseId(); // only for mal ui preview
        this.uiPreviewRan = false; // only let mal ui preview mode run once
        this.unassignedCameraIds = [];
        this.updateCapsAndOnlineStatusFull = false;
        this.videoSwitcherStates = {};
        this.cleanupCallsTimeout = this.timeouts.create();
        this.roomVolumeSaveTimeout = this.timeouts.create();
        this.uiPreviewTimeout = this.timeouts.create();
        this.updateCapsAndOnlineStatusTimeout = this.timeouts.create();
        this.cleanupCalls = this.cleanupCalls.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
        this.isRunningUiPreview = this.isRunningUiPreview.bind(this);
        this.onBeforeUnload = this.onBeforeUnload.bind(this);
        this.onCompanyChanged = this.onCompanyChanged.bind(this);
        this.onConnectStatus = this.onConnectStatus.bind(this);
        this.onControlStatus = this.onControlStatus.bind(this);
        this.onDeviceShutdown = this.onDeviceShutdown.bind(this);
        this.onEquipmentAdded = this.onEquipmentAdded.bind(this);
        this.onEquipmentChanged = this.onEquipmentChanged.bind(this);
        this.onEquipmentRemoved = this.onEquipmentRemoved.bind(this);
        this.onPairedControllersChanged = this.onPairedControllersChanged.bind(this);
        this.onRoomChanged = this.onRoomChanged.bind(this);
        this.onRoomConfigChanged = this.onRoomConfigChanged.bind(this);
        this.onRoomControlCommandAdded = this.onRoomControlCommandAdded.bind(this);
        this.onRoomServiceStateChanged = this.onRoomServiceStateChanged.bind(this);
        this.onRoomVolumeSaveTimeout = this.onRoomVolumeSaveTimeout.bind(this);
        this.onSiteChanged = this.onSiteChanged.bind(this);
        this.onUiPreviewTimeout = this.onUiPreviewTimeout.bind(this);
        this.onUpdateCapsAndOnlineStatusTimeout = this.onUpdateCapsAndOnlineStatusTimeout.bind(this);
        this.roomService.onStateChanged(this.onRoomServiceStateChanged);
        onDeviceShutdown(this.onDeviceShutdown);
        if (isBrowser) {
            onBeforeUnload(this.onBeforeUnload);
        }
    }
    addDevice(equipmentId, doInteractiveAuthentication) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.log.debug(`addDevice: ${equipmentId}`);
            if (!this.equipment)
                throw new Error(`equipment undefined`);
            const eq = this.equipment.$recordAt(equipmentId);
            if (!eq)
                throw new Error(`eq undefined for id ${equipmentId}`);
            this.log.debug(`addDevice: eq = ${eq}`);
            if (this.devices[equipmentId] !== undefined) {
                // This can happen during a transition from network offline mode back to online.
                this.log.info(`already watching ${eq}`);
                return;
            }
            const dev = yield this.createDevice(equipmentId, doInteractiveAuthentication);
            if (dev !== undefined) {
                this.log.debug(`addDevice: inserting device ${dev} at key ${equipmentId}`);
                this.devices[equipmentId] = dev;
                for (const callType of callTypes) {
                    if ((_a = dev.config.synchronizedCalls) === null || _a === void 0 ? void 0 : _a[callType]) {
                        this.synchronizedCallDevices[callType][equipmentId] = dev;
                    }
                    else {
                        delete this.synchronizedCallDevices[callType][equipmentId];
                    }
                }
            }
            else {
                this.log.error(`addDevice: failed creating device`);
                return;
            }
            dev.receivedConnectStatus = false;
            dev.connect();
        });
    }
    allDevicesHaveReceivedConnectStatus() {
        for (const dev of Object.values(this.devices)) {
            if (!dev.hasOwnProperty('receivedConnectStatus') || !dev.receivedConnectStatus) {
                return false;
            }
        }
        return true;
    }
    augmentChildSynchronizedCall(childDev, childCall, userIdOrParticipants) {
        var _a, _b;
        let updated = false;
        if (!this.room)
            return updated;
        const calls = this.room.$getCalls(defaults.inara.numRecentCalls);
        // Augment synchronized call status with parent call ID.
        if (!childCall.parentCallId) {
            const parentEquipmentId = (_b = (_a = childDev.config.synchronizedCalls) === null || _a === void 0 ? void 0 : _a[childCall.callType]) === null || _b === void 0 ? void 0 : _b.parentEquipmentId;
            if (!parentEquipmentId)
                return updated;
            for (const call of calls) {
                if (call.callState !== 'disconnected' &&
                    call.callType === childCall.callType &&
                    this.devicesByCallId[call.$id].eqId === parentEquipmentId) {
                    childCall.parentCallId = call.$id;
                    updated = true;
                    break;
                }
            }
        }
        // Augment synchronized call status with its user ID.
        if (typeof userIdOrParticipants === 'string') {
            childCall.userId = userIdOrParticipants;
        }
        else if (userIdOrParticipants !== undefined) {
            for (const participant of Object.values(userIdOrParticipants)) {
                if (participant.isMe && participant.userId) {
                    childCall.userId = participant.userId;
                    updated = true;
                    break;
                }
            }
        }
        return updated;
    }
    // If any actions for this display motion group are active, then stop them by cancelling their
    // timeouts and setting their contact closures to their inactive state.
    cancelDisplayMotion(displayId, displayMotionId) {
        var _a, _b;
        const displayDev = this.devices[displayId];
        if (!displayDev) {
            this.log.error(`unknown displayId: ${displayId}`);
            return;
        }
        for (const [contactClosureEquipmentId, displayMotionTimeouts] of Object.entries(this.contactClosureTimeouts)) {
            const contactClosureDev = this.devices[contactClosureEquipmentId];
            if (!contactClosureDev) {
                this.log.error(`no device for equipmentId = ${contactClosureEquipmentId}`);
                continue;
            }
            for (const [actionId, contactClosureTimeout] of Object.entries((_a = displayMotionTimeouts[displayMotionId]) !== null && _a !== void 0 ? _a : {})) {
                if (contactClosureTimeout.isRunning) {
                    contactClosureTimeout.clear();
                    const controls = (_b = displayDev.config.displayMotion) === null || _b === void 0 ? void 0 : _b[displayMotionId].controls;
                    let action;
                    for (const control of Object.values(controls !== null && controls !== void 0 ? controls : {})) {
                        if (!control)
                            continue;
                        if (actionId in control.actions) {
                            action = control.actions[actionId];
                            break;
                        }
                    }
                    if (action && action.type === 'contactClosure') {
                        const value = !(action.behavior.activeState === 'closed');
                        contactClosureDev.set('contactClosure', {
                            contactClosureId: actionId,
                            value,
                        });
                    }
                }
            }
        }
    }
    checkDisplayPathSignalTypes(sourcePath) {
        var _a, _b, _c, _d;
        for (let i = 0; i < sourcePath.length; i++) {
            const entry = sourcePath[i];
            const dev = this.devices[entry.equipmentId];
            const eq = (_a = this.equipment) === null || _a === void 0 ? void 0 : _a.$recordAt(entry.equipmentId);
            if (!eq)
                continue;
            const videoInput = (_b = eq.$getAvailableVideoInputs()) === null || _b === void 0 ? void 0 : _b[entry.videoInputId];
            if (!videoInput)
                continue;
            const nextEntry = sourcePath[i + 1];
            if (!nextEntry)
                continue;
            const nextDev = this.devices[nextEntry.equipmentId];
            const nextEq = (_c = this.equipment) === null || _c === void 0 ? void 0 : _c.$recordAt(nextEntry.equipmentId);
            if (!nextEq)
                continue;
            const nextVideoOutput = (_d = nextDev.caps.videoOutputs) === null || _d === void 0 ? void 0 : _d[nextEntry.videoOutputId];
            if (nextVideoOutput === undefined)
                continue;
            if (!eq.$areVideoSignalsCompatible(videoInput, nextVideoOutput)) {
                this.log.warning(`incompatible video signals for ${dev}: input = ${entry.videoInputId} and ${nextDev}: output = ${nextEntry.videoOutputId}`);
            }
        }
    }
    cleanupCalls() {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            this.log.debug(`cleanupCalls`);
            if (!this.room)
                throw new Error(`room undefined`);
            const recentCalls = this.room.$getCalls(defaults.inara.numRecentCalls);
            yield recentCalls.$loaded();
            for (const call of recentCalls) {
                const dev = this.devicesByCallId[call.$id];
                if (((_b = (_a = dev === null || dev === void 0 ? void 0 : dev.caps.maxCalls) === null || _a === void 0 ? void 0 : _a[call.callType]) !== null && _b !== void 0 ? _b : 0) === 0) {
                    if (call.callState !== 'disconnected') {
                        this.log.info(`marking orphaned call as disconnected: ${call}`);
                        const values = call.$getValues();
                        values.callState = 'disconnected';
                        values.end = Date.now();
                        this.callUpdateManager.updateCall(call.$id, values);
                    }
                }
            }
            const onAir = Object.keys(this.devicesByCallId).length > 0;
            if (onAir !== ((_c = this.room.states) === null || _c === void 0 ? void 0 : _c.onAir)) {
                const roomValues = this.room.$getValues();
                Sutl.setProp(roomValues, 'states.onAir', onAir);
                this.room.$set(roomValues, false, false);
            }
        });
    }
    computeCaps(current) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        return __awaiter(this, void 0, void 0, function* () {
            this.log.debug(`computeCaps (current = ${current})`);
            const caps = {};
            // Initialize device lookup directories and separate devices into online and offline
            // collections.
            if (current) {
                this.offlineDevices = {};
                this.onlineDevices = {};
                for (const [equipmentId, dev] of Object.entries(this.devices)) {
                    const connectState = (_b = this.connectStates[(_a = dev.eqId) !== null && _a !== void 0 ? _a : '']) !== null && _b !== void 0 ? _b : 'offline';
                    this.log.debug(`${dev} connectState = ${connectState}`);
                    if (connectState === 'online') {
                        this.onlineDevices[equipmentId] = dev;
                    }
                    else {
                        this.offlineDevices[equipmentId] = dev;
                    }
                }
            }
            // Compute capabilities and device info.
            const devs = current ? this.onlineDevices : this.devices;
            const hasCompanyDirectories = ((_d = (_c = this.room) === null || _c === void 0 ? void 0 : _c.settings) === null || _d === void 0 ? void 0 : _d.companyDirectories) !== undefined;
            const hasEquipmentDirectories = Object.values(devs).some(dev => dev.config.useDirectory);
            caps.hasCalendar = ((_f = (_e = this.room) === null || _e === void 0 ? void 0 : _e.settings) === null || _f === void 0 ? void 0 : _f.calendarSource) !== undefined;
            caps.hasDirectory = hasCompanyDirectories || hasEquipmentDirectories;
            caps.hasRoomPowerOff = (_j = (_h = (_g = this.site) === null || _g === void 0 ? void 0 : _g.settings) === null || _h === void 0 ? void 0 : _h.roomPowerOffEnabled) !== null && _j !== void 0 ? _j : false;
            caps.whoCanHearMeIcon = (_m = (_l = (_k = this.room) === null || _k === void 0 ? void 0 : _k.settings) === null || _l === void 0 ? void 0 : _l.whoCanHearMeIcon) !== null && _m !== void 0 ? _m : 'megaphone';
            caps.whoCanHearMeLabel = (_q = (_p = (_o = this.room) === null || _o === void 0 ? void 0 : _o.settings) === null || _p === void 0 ? void 0 : _p.whoCanHearMeLabel) !== null && _q !== void 0 ? _q : 'Who Can Hear Me';
            this.computeMuteCaps(devs, caps, current);
            this.computeVolumeCaps(devs, caps, current);
            this.computeAssistiveListeningCaps(devs, caps, current);
            yield this.computeCallCaps(devs, caps, current, true);
            this.computeCameraCaps(devs, caps, current);
            this.computeAudioInputSourcesCaps(devs, caps, current);
            this.computeOverflowRoomsCaps(devs, caps, current);
            this.computeSoundReinforcementCaps(devs, caps, current);
            this.computeMuteSyncDevices(devs, caps, current);
            this.computeLightingCaps(devs, caps, current);
            yield this.computeShadeCaps(devs, caps, current);
            if (current)
                this.initializeVideoSwitcherStates(devs, caps);
            this.computeDisplayCaps(devs, caps, current);
            this.computeUsbCaps(devs, caps, current);
            this.computeVirtualCameraSwitcherCaps(devs, caps, current);
            this.computeZoomConnectorCaps(devs, caps, current);
            return caps;
        });
    }
    computeAssistiveListeningCaps(devs, caps, current) {
        if (current)
            this.assistiveListeningDevice = undefined;
        for (const dev of Object.values(devs)) {
            if (dev.caps.assistiveListening) {
                caps.assistiveListening = {
                    hasMute: dev.caps.assistiveListening.hasMute ? true : false,
                    hasVolume: dev.caps.assistiveListening.hasVolume ? true : false,
                };
                if (current)
                    this.assistiveListeningDevice = dev;
                break;
            }
        }
    }
    computeAudioInputSourcesCaps(devs, caps, current) {
        if (current)
            this.devicesByAudioInputSourceId = {};
        const audioInputSources = {};
        for (const dev of Object.values(devs)) {
            if (dev.caps.audioInputSources) {
                for (const [key, value] of Object.entries(dev.caps.audioInputSources)) {
                    audioInputSources[key] = Sutl.deepCopy(value);
                    if (current)
                        this.devicesByAudioInputSourceId[key] = dev;
                }
            }
        }
        if (Object.keys(audioInputSources).length > 0)
            caps.audioInputSources = audioInputSources;
    }
    computeCallCaps(devs, caps, current, resetDevicesByCallId) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        return __awaiter(this, void 0, void 0, function* () {
            const siteSettings = (_c = this.kayleeService.getSite((_b = (_a = this.room) === null || _a === void 0 ? void 0 : _a.siteId) !== null && _b !== void 0 ? _b : '')) === null || _c === void 0 ? void 0 : _c.settings;
            const synergySkyEnabled = siteSettings === null || siteSettings === void 0 ? void 0 : siteSettings.synergySkyEnabled;
            const synergySkyCallTypes = Object.keys((_d = siteSettings === null || siteSettings === void 0 ? void 0 : siteSettings.synergySkyCallTypes) !== null && _d !== void 0 ? _d : {});
            caps.callSupportsInvitation = {};
            caps.disabledCalls = {};
            caps.maxCalls = {};
            if (this.room === undefined) {
                this.log.error(`room undefined`);
                return;
            }
            if (current) {
                yield this.cleanupCalls();
                if (resetDevicesByCallId)
                    this.devicesByCallId = {};
                this.synchronizedCalls = {};
            }
            for (const callType of callTypes) {
                let disabledCalls = 0;
                let maxCalls = 0;
                const callDevices = [];
                for (const dev of Object.values(devs)) {
                    if ((_e = dev.config.synchronizedCalls) === null || _e === void 0 ? void 0 : _e[callType])
                        continue;
                    let devMaxCalls = (_g = (_f = dev.caps.maxCalls) === null || _f === void 0 ? void 0 : _f[callType]) !== null && _g !== void 0 ? _g : 0;
                    let devDisabledCalls = (_j = (_h = dev.caps.disabledCalls) === null || _h === void 0 ? void 0 : _h[callType]) !== null && _j !== void 0 ? _j : 0;
                    if (synergySkyEnabled && synergySkyCallTypes.includes(callType)) {
                        devMaxCalls = (_l = (_k = dev.caps.maxCalls) === null || _k === void 0 ? void 0 : _k.video) !== null && _l !== void 0 ? _l : 0;
                        devDisabledCalls = (_o = (_m = dev.caps.disabledCalls) === null || _m === void 0 ? void 0 : _m.video) !== null && _o !== void 0 ? _o : 0;
                    }
                    else if ((_p = dev.caps.callSupportsInvitation) === null || _p === void 0 ? void 0 : _p[callType]) {
                        caps.callSupportsInvitation[callType] = true;
                    }
                    if (devMaxCalls > 0) {
                        maxCalls += devMaxCalls;
                        if (current)
                            callDevices.push(dev);
                    }
                    disabledCalls += devDisabledCalls;
                }
                caps.disabledCalls[callType] = disabledCalls;
                caps.maxCalls[callType] = maxCalls;
                if (current)
                    this.devicesByCallType[callType] = callDevices;
            }
        });
    }
    computeCameraCaps(devs, caps, current) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8;
        const cameraSwitchers = {};
        if (current) {
            this.cameraSwitchersByCameraId = {};
            this.devicesByCameraId = {};
            this.devicesByCameraPresetId = {};
        }
        for (const dev of Object.values(devs)) {
            if (!this.equipment)
                throw new Error(`equipment undefined`);
            if (dev.eqId === undefined)
                throw new Error(`equipmentId undefined`);
            const eq = this.equipment.$recordAt(dev.eqId);
            if (!eq) {
                this.log.error(`eq undefined for id ${dev.eqId}`);
                continue;
            }
            const eqType = this.kayleeService.getEquipmentTypes().$recordAt((_a = eq.type) !== null && _a !== void 0 ? _a : '');
            if ((eqType === null || eqType === void 0 ? void 0 : eqType.type) !== 'camera' && Object.keys((_b = dev.caps.cameras) !== null && _b !== void 0 ? _b : {}).length > 0) {
                const cameraSwitcherCaps = {};
                let maxIndex = 0;
                if (dev.caps.cameraPresets) {
                    cameraSwitcherCaps.cameraPresets = Sutl.deepCopy(dev.caps.cameraPresets);
                    for (const [presetId, cameraPreset] of Object.entries(dev.caps.cameraPresets)) {
                        if (current)
                            this.devicesByCameraPresetId[presetId] = dev;
                        if (cameraPreset.index > maxIndex)
                            maxIndex = cameraPreset.index;
                    }
                }
                const cameras = {};
                const cameraPresets = {};
                for (const [cameraId, camera] of Object.entries((_c = dev.caps.cameras) !== null && _c !== void 0 ? _c : {})) {
                    cameras[cameraId] = Object.assign(Object.assign({}, (camera.name && { name: camera.name })), { hasCameraTracking: (_d = camera.hasCameraTracking) !== null && _d !== void 0 ? _d : false, hasPan: (_e = camera.hasPan) !== null && _e !== void 0 ? _e : false, hasTilt: (_f = camera.hasTilt) !== null && _f !== void 0 ? _f : false, hasZoom: (_g = camera.hasZoom) !== null && _g !== void 0 ? _g : false, location: camera.location });
                    // Old way: drivers set hasCameraTrackingUi themselves. New way: drivers just
                    // set hasCameraTracking, and EquipmentService sets hasCameraTrackingUi.
                    if ('hasCameraTrackingUi' in camera) {
                        cameras[cameraId].hasCameraTrackingUi = camera.hasCameraTrackingUi;
                    }
                    else {
                        if (cameras[cameraId].hasCameraTracking) {
                            cameras[cameraId].hasCameraTrackingUi =
                                (_j = (_h = eq.config) === null || _h === void 0 ? void 0 : _h.cameraTrackingUiEnabled) !== null && _j !== void 0 ? _j : true;
                        }
                    }
                    const extCameraEqId = (_m = (_l = (_k = dev.caps.cameras) === null || _k === void 0 ? void 0 : _k[cameraId]) === null || _l === void 0 ? void 0 : _l.extControl) === null || _m === void 0 ? void 0 : _m.equipmentId;
                    if (!extCameraEqId)
                        continue;
                    const extCameraId = (_q = (_p = (_o = dev.caps.cameras) === null || _o === void 0 ? void 0 : _o[cameraId]) === null || _p === void 0 ? void 0 : _p.extControl) === null || _q === void 0 ? void 0 : _q.configId;
                    if (extCameraId === undefined) {
                        this.log.error(`couldn't find extControl.configId for camera`);
                        continue;
                    }
                    cameras[cameraId].equipmentId = extCameraEqId;
                    cameras[cameraId].hasCameraTracking =
                        (_v = (_u = (_t = (_s = (_r = this.devices[extCameraEqId]) === null || _r === void 0 ? void 0 : _r.caps) === null || _s === void 0 ? void 0 : _s.cameras) === null || _t === void 0 ? void 0 : _t[extCameraId]) === null || _u === void 0 ? void 0 : _u.hasCameraTracking) !== null && _v !== void 0 ? _v : false;
                    // Drivers set hasCameraTracking and EquipmentService sets hasCameraTrackingUi.
                    if (cameras[cameraId].hasCameraTracking) {
                        cameras[cameraId].hasCameraTrackingUi =
                            (_y = (_x = (_w = this.equipment.$recordAt(extCameraEqId)) === null || _w === void 0 ? void 0 : _w.config) === null || _x === void 0 ? void 0 : _x.cameraTrackingUiEnabled) !== null && _y !== void 0 ? _y : true;
                    }
                    const extCameraPresets = (_1 = (_0 = (_z = this.devices[extCameraEqId]) === null || _z === void 0 ? void 0 : _z.caps) === null || _0 === void 0 ? void 0 : _0.cameraPresets) !== null && _1 !== void 0 ? _1 : {};
                    const cameraPresetIds = Object.keys(extCameraPresets).sort(); // sort for index
                    for (const presetId of cameraPresetIds) {
                        cameraPresets[presetId] = Sutl.deepCopy(extCameraPresets[presetId]);
                        cameraPresets[presetId].index = ++maxIndex;
                        if (current) {
                            this.devicesByCameraPresetId[presetId] = this.devices[extCameraEqId];
                        }
                    }
                }
                if (Object.keys(cameras).length > 0)
                    cameraSwitcherCaps.cameras = cameras;
                if (Object.keys(cameraPresets).length > 0) {
                    cameraSwitcherCaps.cameraPresets = cameraPresets;
                }
                if ((_2 = eq.config) === null || _2 === void 0 ? void 0 : _2.name)
                    cameraSwitcherCaps.name = eq.config.name;
                if ((_3 = eq.config) === null || _3 === void 0 ? void 0 : _3.hideCameraSwitcher) {
                    cameraSwitcherCaps.isHidden = (_4 = eq.config) === null || _4 === void 0 ? void 0 : _4.hideCameraSwitcher;
                }
                cameraSwitcherCaps.hasSelfView = (_5 = dev.caps.hasSelfView) !== null && _5 !== void 0 ? _5 : false;
                cameraSwitcherCaps.hasSelfViewAuto = (_6 = dev.caps.hasSelfViewAuto) !== null && _6 !== void 0 ? _6 : false;
                cameraSwitcherCaps.hasSelfViewUi = (_7 = dev.caps.hasSelfViewUi) !== null && _7 !== void 0 ? _7 : false;
                if (dev.caps.selfViewPositions !== undefined) {
                    cameraSwitcherCaps.selfViewPositions = dev.caps.selfViewPositions;
                }
                cameraSwitchers[dev.eqId] = cameraSwitcherCaps;
                if (current) {
                    for (const cameraId of Object.keys((_8 = dev.caps.cameras) !== null && _8 !== void 0 ? _8 : {})) {
                        this.devicesByCameraId[cameraId] = dev;
                        this.cameraSwitchersByCameraId[cameraId] = dev;
                    }
                }
            }
        }
        if (Object.keys(cameraSwitchers).length > 0)
            caps.cameraSwitchers = cameraSwitchers;
    }
    // This is necessary because the controlFailed connectState for drivers doesn't always align
    // with the connectState for equipment. For example, consider a display configured with display
    // motion and a gpio device handling the relays for the screen motion. The display's
    // displayMotion config gets translated into a contactClosures config for the gpio device
    // driver. If there is an error in the contact closure config, the gpio device will report
    // controlFailed, while the display could be reporting online. In this case, we want the
    // display to report controlFailed while the gpio device reports online.
    //
    // Note: the validated objects are translated elsewhere but are used in this computation. It is
    // assumed that the validated status is sent by drivers before the connect state.
    computeConnectState(dev) {
        // Pass through connect state unless we're online or controlFailed.
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        if (dev.connectState !== 'online' && dev.connectState !== 'controlFailed') {
            this.connectStates[(_a = dev.eqId) !== null && _a !== void 0 ? _a : ''] = (_b = dev.connectState) !== null && _b !== void 0 ? _b : 'connectFailed';
            return this.connectStates[(_c = dev.eqId) !== null && _c !== void 0 ? _c : ''];
        }
        // If the device is online or controlFailed, then we need to recompute that state by
        // checking both new and old validated structures.
        if (!dev.eqId) {
            this.log.error(`no eqId for device`);
            this.connectStates[(_d = dev.eqId) !== null && _d !== void 0 ? _d : ''] = 'connectFailed';
            return this.connectStates[(_e = dev.eqId) !== null && _e !== void 0 ? _e : ''];
        }
        const eq = (_f = this.equipment) === null || _f === void 0 ? void 0 : _f.$recordAt(dev.eqId); // assume already $loaded()
        if (!eq) {
            // this can happen right after equipment is removed
            this.log.info(`equipment undefined for id ${dev.eqId}`);
            this.connectStates[(_g = dev.eqId) !== null && _g !== void 0 ? _g : ''] = 'connectFailed';
            return this.connectStates[(_h = dev.eqId) !== null && _h !== void 0 ? _h : ''];
        }
        // old (DSP) validation in equipment
        if (dev.configName &&
            ((_j = eq.config) === null || _j === void 0 ? void 0 : _j[dev.configName]) &&
            !isValid(Sutl.getProp(eq, ['config', dev.configName, 'validated'], {}), false, false)) {
            this.connectStates[(_k = dev.eqId) !== null && _k !== void 0 ? _k : ''] = 'controlFailed';
            return this.connectStates[(_l = dev.eqId) !== null && _l !== void 0 ? _l : ''];
        }
        // new validation in equipmentStates
        const eqStates = eq.$getStates(); // assume already $loaded()
        if (!isValid((_m = eqStates.validated) !== null && _m !== void 0 ? _m : {}, false, false)) {
            this.connectStates[(_o = dev.eqId) !== null && _o !== void 0 ? _o : ''] = 'controlFailed';
            return this.connectStates[(_p = dev.eqId) !== null && _p !== void 0 ? _p : ''];
        }
        // Otherwise, we're online.
        this.connectStates[(_q = dev.eqId) !== null && _q !== void 0 ? _q : ''] = 'online';
        return this.connectStates[(_r = dev.eqId) !== null && _r !== void 0 ? _r : ''];
    }
    computeDisplayCaps(devs, caps, current) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
        const tmpSourceMenus = {};
        caps.displays = {};
        if (current)
            this.contentStateValues.length = 0;
        // Copy manually configured source menus so we can modify them if necessary.
        for (const [equipmentId, dev] of Object.entries(devs)) {
            if (dev.config.sourceMenu && Object.keys((_a = dev.caps.videoInputs) !== null && _a !== void 0 ? _a : {}).length > 0) {
                tmpSourceMenus[equipmentId] = Sutl.deepCopy(dev.config.sourceMenu);
            }
        }
        // Some devices add dynamically generated video inputs that were not available for
        // configuration on Mal. Here, we add the dynamically generated video inputs to the source
        // menus where appropriate.
        for (const dev of Object.values(devs)) {
            if (!dev.eqId)
                throw new Error(`dev.eqId undefined`);
            const videoInputs = (_b = dev.caps.videoInputs) !== null && _b !== void 0 ? _b : {};
            for (const [videoInputId, videoInput] of Object.entries(videoInputs)) {
                if (!isVideoInputInternal(videoInput) || !videoInput.isDynamic)
                    continue;
                for (const sourceMenuDev of Object.values(devs)) {
                    if (!sourceMenuDev.eqId)
                        throw new Error(`sourceMenuDev.eqId undefined`);
                    const sourceMenu = tmpSourceMenus[sourceMenuDev.eqId];
                    if (!sourceMenu)
                        continue;
                    let lastKey;
                    let lastIndex;
                    const sourceMenuKeys = Object.keys(sourceMenu).sort((a, b) => sourceMenu[b].index - sourceMenu[a].index);
                    // Find the key and index of the last source menu entry for this device.
                    for (const sourceMenuKey of sourceMenuKeys) {
                        const entry = sourceMenu[sourceMenuKey];
                        if (entry.equipmentId === dev.eqId) {
                            lastKey = sourceMenuKey;
                            lastIndex = entry.index;
                        }
                    }
                    if (lastKey === undefined || lastIndex === undefined)
                        continue;
                    // Shift index of source menu entries following the new entry.
                    for (const sourceMenuKey of sourceMenuKeys) {
                        const entry = sourceMenu[sourceMenuKey];
                        if (entry.index <= lastIndex)
                            continue;
                        entry.index++;
                    }
                    // Add dynamic video input to source menu. Use the video input id for the
                    // source menu id rather than generate a new one. This keeps the ids the same
                    // between the current and non-current caps.
                    sourceMenu[videoInputId] = {
                        equipmentId: dev.eqId,
                        index: lastIndex + 1,
                        videoInputId: videoInputId,
                    };
                }
            }
        }
        // Some devices may not have the video inputs that were configured on Mal. For those cases,
        // we remove them from the source menus. We only do this for the current caps so Inara can
        // show the configured but missing input as grayed out.
        if (current) {
            for (const [equipmentId, sourceMenu] of Object.entries(tmpSourceMenus)) {
                const missingIds = [];
                for (const [sourceId, source] of Object.entries(sourceMenu)) {
                    const outId = this.getVideoOutputIds(equipmentId)[0];
                    if (!this.getPathToDisplaySource(source, equipmentId, outId)) {
                        missingIds.push(sourceId);
                    }
                }
                for (const sourceId of missingIds) {
                    delete sourceMenu[sourceId];
                }
            }
        }
        if (current)
            this.sourceMenus = tmpSourceMenus;
        // Create display entries.
        for (const [equipmentId, sourceMenu] of Object.entries(tmpSourceMenus)) {
            const dev = devs[equipmentId];
            if (!dev)
                throw new Error(`dev undefined`);
            if (!this.equipment)
                throw new Error(`equipment undefined`);
            const eq = this.equipment.$recordAt(equipmentId);
            if (!eq)
                throw new Error(`eq undefined for id ${equipmentId}`);
            caps.displays[equipmentId] = {
                name: (_c = eq.config) === null || _c === void 0 ? void 0 : _c.name,
                hasBlank: dev.caps.hasDisplayBlank,
                hasBlankUi: dev.caps.hasDisplayBlankUi,
                hasFreeze: dev.caps.hasDisplayFreeze,
                hasFreezeUi: dev.caps.hasDisplayFreezeUi,
                hasPowerControl: dev.caps.hasPowerControl,
                hasVolume: dev.caps.hasVolume,
                hasVolumeMute: dev.caps.hasVolumeMute,
                hasVolumeUi: dev.caps.hasVolume && ((_e = (_d = eq.config) === null || _d === void 0 ? void 0 : _d.displayVolumeUiEnabled) !== null && _e !== void 0 ? _e : false),
                isBlankStateless: dev.caps.isDisplayBlankStateless,
                isFreezeStateless: dev.caps.isDisplayFreezeStateless,
                isHidden: (_f = eq.config) === null || _f === void 0 ? void 0 : _f.hideDisplay,
                isPowerStateless: dev.caps.isPowerStateless,
                isPowerToggle: dev.caps.isPowerToggle,
                isVideoInputStateless: false,
                isVolumeIncDec: dev.caps.isVolumeIncDec,
                isVolumeStateless: dev.caps.isVolumeStateless,
            };
            if (((_g = eq.config) === null || _g === void 0 ? void 0 : _g.displayIndex) !== undefined) {
                caps.displays[equipmentId].index = eq.config.displayIndex;
            }
            if (((_h = eq.config) === null || _h === void 0 ? void 0 : _h.displayBlankIndex) !== undefined) {
                caps.displays[equipmentId].blankIndex = eq.config.displayBlankIndex;
            }
            if (((_j = eq.config) === null || _j === void 0 ? void 0 : _j.displayFreezeIndex) !== undefined) {
                caps.displays[equipmentId].freezeIndex = eq.config.displayFreezeIndex;
            }
            // Add display motion caps if necessary.
            let displayMotion = {};
            for (const [motionId, motionConfig] of Object.entries((_k = dev.config.displayMotion) !== null && _k !== void 0 ? _k : {})) {
                const entry = Object.assign(Object.assign(Object.assign({ controls: {} }, (motionConfig.index !== undefined
                    ? { index: motionConfig.index }
                    : undefined)), { label: motionConfig.label }), (motionConfig.showManualControls !== undefined
                    ? { showManualControls: motionConfig.showManualControls }
                    : undefined));
                for (const [controlId, control] of Object.entries(motionConfig.controls)) {
                    if (!control)
                        continue;
                    let canIncludeControl = !current;
                    if (current) {
                        for (const [actionId, action] of Object.entries((_l = control.actions) !== null && _l !== void 0 ? _l : {})) {
                            const actionDev = this.devices[action.equipmentId];
                            if (this.connectStates[(_m = actionDev === null || actionDev === void 0 ? void 0 : actionDev.eqId) !== null && _m !== void 0 ? _m : ''] !== 'online')
                                continue;
                            if ((_o = actionDev.caps.contactClosures) === null || _o === void 0 ? void 0 : _o[actionId]) {
                                canIncludeControl = true;
                                break;
                            }
                        }
                    }
                    if (canIncludeControl) {
                        entry.controls[controlId] = {
                            automateWithPower: control.automateWithPower === true,
                            icon: control.icon,
                        };
                    }
                }
                displayMotion[motionId] = entry;
            }
            if (Object.keys(displayMotion).length > 0) {
                caps.displays[equipmentId].displayMotion = displayMotion;
            }
            // Create a source entry for each item in the sourceMenu.
            caps.displays[equipmentId].sources = {};
            for (const [sourceId, source] of Object.entries(sourceMenu)) {
                const sourceDev = devs[source.equipmentId];
                // Handle offline/missing source devices.
                if (!sourceDev) {
                    if (current) {
                        // It's okay, equipment could be offline.
                        this.log.debug(`no equipment with id ${source.equipmentId} for sourceMenu of ${dev}`);
                    }
                    else {
                        // Not okay, equipment not in room.
                        this.log.error(`no equipment with id ${source.equipmentId} for sourceMenu of ${dev}`);
                    }
                    continue;
                }
                // Make sure the video input source exists.
                if (!sourceDev.caps.hasVideoInputs && !((_p = sourceDev.caps.videoInputs) === null || _p === void 0 ? void 0 : _p.videoInputId)) {
                    this.log.error(`no video source with id ${source.videoInputId} in device ${sourceDev} for sourceMenu of ${dev}`);
                    continue;
                }
                const outId = this.getVideoOutputIds(equipmentId)[0];
                const sourcePath = this.getPathToDisplaySource(source, equipmentId, outId);
                if (current) {
                    // If we're computing the current caps, make sure all the devices in the path
                    // to this source are online.
                    if (!sourcePath) {
                        this.log.error(`sourcePath undefined`);
                        continue;
                    }
                    if (!this.isDisplayPathOnline(sourcePath))
                        continue;
                    // Display message in log if video signal types aren't compatible.
                    this.checkDisplayPathSignalTypes(sourcePath);
                }
                else {
                    if (sourcePath) {
                        if (this.isDisplayPathVideoInputStateless(sourcePath)) {
                            caps.displays[equipmentId].isVideoInputStateless = true;
                        }
                    }
                }
                // Create the source entry.
                //
                // In most cases, we use sourceDev.caps.videoInputs to create the source entry.
                // However, if we are not computing the current caps and the video input has been
                // dynamically removed by the device, then we use the information from
                // sourceDev.config.videoInputs.
                const videoInput = (_r = (_q = sourceDev.caps.videoInputs) === null || _q === void 0 ? void 0 : _q[source.videoInputId]) !== null && _r !== void 0 ? _r : (_s = sourceDev.config.videoInputs) === null || _s === void 0 ? void 0 : _s[source.videoInputId];
                if (!videoInput) {
                    this.log.warning(`can't find videoInput`);
                    continue;
                }
                if (!isVideoInputInternal(videoInput)) {
                    this.log.error(`videoInput isn't internal`);
                    continue;
                }
                // A source entry can get media operations caps in one of two ways.
                //
                // 1. If the device is a standalone media player or tuner, it will have
                // caps.mediaOperations. If so, its media caps get assigned to any source entry
                // for which it is the leaf equipment.
                //
                // 2. If the device is has a built-in media player on one or more of its video
                // sources, it will include the media caps for each source on the video input caps.
                const sourceCaps = Object.assign(Object.assign(Object.assign(Object.assign({ name: videoInput.name, icon: videoInput.icon, description: videoInput.description, bypassFarContent: (_t = source.bypassFarContent) !== null && _t !== void 0 ? _t : false, farContent: sourcePath && this.isDisplayPathFarContent(sourcePath), index: source.index, equipmentId: source.equipmentId }, (videoInput.unavailableReason
                    ? { unavailableReason: videoInput.unavailableReason }
                    : undefined)), { videoInputId: source.videoInputId }), (videoInput.instructions
                    ? { instructions: videoInput.instructions }
                    : undefined)), { channelKeyboardType: videoInput.channelKeyboardType, channelFavorites: videoInput.channelFavorites, channelTuners: videoInput.channelTuners, isChannelStateless: videoInput.isChannelStateless, isChannelTunerStateless: videoInput.isChannelTunerStateless, isClosedCaptioningStateless: videoInput.isClosedCaptioningStateless });
                Sutl.setProp(caps, ['displays', equipmentId, 'sources', sourceId], sourceCaps);
                if (videoInput.mediaOperations !== undefined) {
                    sourceCaps.mediaOperations = videoInput.mediaOperations.slice();
                    if (sourceDev.config.hasOwnProperty('channelFavorites')) {
                        sourceCaps.channelFavorites = Sutl.deepCopy(sourceDev.config.channelFavorites);
                    }
                    if (videoInput.channelKeyboardType) {
                        sourceCaps.channelKeyboardType = videoInput.channelKeyboardType;
                    }
                    if (videoInput.hasOwnProperty('channelTuners')) {
                        sourceCaps.channelTuners = videoInput.channelTuners;
                    }
                    if (videoInput.hasOwnProperty('isChannelStateless')) {
                        sourceCaps.isChannelStateless = videoInput.isChannelStateless;
                    }
                    if (videoInput.hasOwnProperty('isChannelTunerStateless')) {
                        sourceCaps.isChannelTunerStateless = videoInput.isChannelTunerStateless;
                    }
                    if ('isMediaPlayerStateless' in videoInput) {
                        sourceCaps.isMediaPlayerStateless = videoInput.isMediaPlayerStateless;
                    }
                    if ('isMediaRecorderStateless' in videoInput) {
                        sourceCaps.isMediaRecorderStateless = videoInput.isMediaRecorderStateless;
                    }
                    if ('isMediaStreamerStateless' in videoInput) {
                        sourceCaps.isMediaStreamerStateless = videoInput.isMediaStreamerStateless;
                    }
                }
                // Add entries for devices along the source path that have power toggles.
                if (current) {
                    if (!sourcePath)
                        throw new Error(`sourcePath undefined`);
                    const toggleDevices = [];
                    for (let i = 0; i < sourcePath.length - 1; i++) {
                        const sourcePathEntry = sourcePath[i];
                        const toggleDev = this.devices[sourcePathEntry.equipmentId];
                        if (!toggleDev) {
                            this.log.error(`toggleDev undefined`);
                            continue;
                        }
                        if (!toggleDev.eqId) {
                            this.log.error(`toggleDev.eqId undefined`);
                            continue;
                        }
                        if (toggleDev.caps.hasPowerControl && toggleDev.caps.isPowerToggle) {
                            const toggleEq = this.equipment.$recordAt(toggleDev.eqId);
                            const eqTypeName = toggleEq
                                ? `${toggleEq.$getManufacturer()} ${toggleEq.$getModel()}`
                                : 'unknown';
                            const name = (_u = toggleDev.config.name) !== null && _u !== void 0 ? _u : eqTypeName;
                            toggleDevices.push({
                                equipmentId: toggleDev.eqId,
                                name,
                            });
                        }
                    }
                    if (toggleDevices.length > 0) {
                        toggleDevices.reverse();
                        sourceCaps.togglePowerDevices = Sutl.arrayToObj(toggleDevices, 'a');
                    }
                }
                // Clean up undefined properties so optional properties like 'description' are
                // processed correctly when deleted in the equipment configuration.
                for (const [prop, value] of Object.entries(sourceCaps)) {
                    if (value === undefined) {
                        delete sourceCaps[prop];
                    }
                }
            }
            // Do this after all display source caps have been computed for the device.
            for (const [sourceId, sourceCaps] of Object.entries((_x = (_w = (_v = caps.displays) === null || _v === void 0 ? void 0 : _v[equipmentId]) === null || _w === void 0 ? void 0 : _w.sources) !== null && _x !== void 0 ? _x : {})) {
                // Special case for codecs. On secondary displays, people will likely put the
                // "Video Conference" entry, but not any of the content sharing entries for the
                // codec. When a user selects the "Video Conference" entry on the secondary
                // display's menu, we want to switch to that video input, but not send a
                // contentSource command to the codec (since that could potentially stop content
                // sharing).
                // SNTY-2878: Only set dontSwitchLeaf for video codecs.
                // If this entry is "mustInclude" in the equipmentTypes and no other entries
                // exist in sourceMenu for non-mustInclude items for the same piece of equipment.
                if (current &&
                    eq.$getType() === 'videoCodec' &&
                    this.isOnlyMustInclude(eq, sourceId, caps)) {
                    sourceCaps.dontSwitchLeaf = true;
                }
            }
        }
        if (Object.keys(caps.displays).length <= 0)
            delete caps.displays;
    }
    computeDisplaySources() {
        var _a, _b, _c, _d, _e, _f;
        if (!this.room)
            throw new Error(`room undefined`);
        const debug = false;
        const roomValues = this.room.$getValues();
        if (debug) {
            this.log.debug(`computeDisplaySources: caps.displays = ${JSON.stringify(this.caps.displays)}`);
        }
        for (const [displayId, display] of Object.entries((_a = this.caps.displays) !== null && _a !== void 0 ? _a : {})) {
            const dontSwitchLeafEqIds = [];
            for (const source of Object.values((_b = display.sources) !== null && _b !== void 0 ? _b : {})) {
                if (source.dontSwitchLeaf && !dontSwitchLeafEqIds.includes(source.equipmentId)) {
                    dontSwitchLeafEqIds.push(source.equipmentId);
                }
            }
            const selectedSource = this.getSelectedSource(displayId, this.getVideoOutputIds(displayId)[0], dontSwitchLeafEqIds);
            let newSourceId;
            if (debug) {
                this.log.debug(`computeDisplaySources: ${display.name} (${displayId})`);
                this.log.debug(`computeDisplaySources: selectedSource = ${JSON.stringify(selectedSource)}`);
            }
            if (selectedSource) {
                // find source id for the selected source
                for (const [sourceId, source] of Object.entries((_c = display.sources) !== null && _c !== void 0 ? _c : {})) {
                    if (source.equipmentId === selectedSource.equipmentId &&
                        source.videoInputId === selectedSource.videoInputId) {
                        newSourceId = sourceId;
                        break;
                    }
                    if (source.dontSwitchLeaf &&
                        source.equipmentId === selectedSource.equipmentId) {
                        newSourceId = sourceId;
                        break;
                    }
                }
            }
            if (debug) {
                this.log.debug(`computeDisplaySources: newSourceId = ${newSourceId}`);
            }
            const currSourceId = (_f = (_e = (_d = this.room.states) === null || _d === void 0 ? void 0 : _d.displays) === null || _e === void 0 ? void 0 : _e[displayId]) === null || _f === void 0 ? void 0 : _f.videoInput;
            if (debug) {
                this.log.debug(`computeDisplaySources: currSourceId = ${currSourceId}`);
            }
            if (currSourceId !== newSourceId) {
                if (debug) {
                    this.log.debug(`computeDisplaySources: source changed`);
                }
                Sutl.setNode(roomValues, ['states', 'displays', displayId, 'videoInput'], newSourceId);
            }
        }
        this.room.$set(roomValues, false, false);
    }
    computeLightingCaps(devs, caps, current) {
        var _a, _b;
        const lightingControls = {};
        const lightingPresets = {};
        if (current) {
            this.devicesByLightingControlId = {};
            this.devicesByLightingPresetId = {};
        }
        for (const dev of Object.values(devs)) {
            const devLightingControls = (_a = dev.caps.lightingControls) !== null && _a !== void 0 ? _a : {};
            for (const [lightingId, lightingCaps] of Object.entries(devLightingControls)) {
                if (current)
                    this.devicesByLightingControlId[lightingId] = dev;
                lightingControls[lightingId] = Sutl.deepCopy(lightingCaps);
            }
            const devLightingPresets = (_b = dev.caps.lightingPresets) !== null && _b !== void 0 ? _b : {};
            for (const [presetId, preset] of Object.entries(devLightingPresets)) {
                if (current)
                    this.devicesByLightingPresetId[presetId] = dev;
                lightingPresets[presetId] = Sutl.deepCopy(preset);
            }
        }
        if (Object.keys(lightingControls).length > 0)
            caps.lightingControls = lightingControls;
        if (Object.keys(lightingPresets).length > 0)
            caps.lightingPresets = lightingPresets;
    }
    computeMuteCaps(devs, caps, current) {
        var _a, _b, _c;
        caps.hasRoomMute = false;
        caps.hasMicrophoneMute = caps.hasRoomMute; // deprecated in SNTY-2915
        if (current)
            this.roomMuteDevice = undefined;
        const roomMuteDevicePriorities = {
            audioDsp: '1',
            userDriver: '2',
            avReceiver: '3',
            videoCodec: '4',
            videoSwitcher: '5',
            amplifier: '6',
            camera: '7',
        };
        // Find all possible mute devices, sort them based on priority then choose the highest
        // priority device to be the roomMuteDevice.
        //
        // Do this calculation with all configured devices, even if they are offline. The mute and
        // volume devices shouldn't change if the primary device is offline.
        const roomMuteDevices = Object.values(this.devices)
            .filter(dev => {
            const eqClass = this.getEquipmentClass(dev);
            return dev.caps.hasRoomMute && eqClass in roomMuteDevicePriorities;
        })
            .sort((devA, devB) => {
            // Sort based on priority, but also include the equipment id to get a repeatable
            // sort order in case there are multiple devices of the same type.
            var _a, _b, _c, _d;
            const eqClassA = this.getEquipmentClass(devA);
            const eqClassB = this.getEquipmentClass(devB);
            const isRestrictA = ((_a = devA.caps.roomMutePowerOffRestrictions) !== null && _a !== void 0 ? _a : []).length > 0;
            const isRestrictB = ((_b = devB.caps.roomMutePowerOffRestrictions) !== null && _b !== void 0 ? _b : []).length > 0;
            const priorityA = isRestrictA ? '9' : (_c = roomMuteDevicePriorities[eqClassA]) !== null && _c !== void 0 ? _c : '8';
            const priorityB = isRestrictB ? '9' : (_d = roomMuteDevicePriorities[eqClassB]) !== null && _d !== void 0 ? _d : '8';
            const nameA = priorityA + devA.eqId;
            const nameB = priorityB + devB.eqId;
            if (nameA < nameB)
                return -1;
            if (nameA > nameB)
                return 1;
            return 0;
        });
        if (roomMuteDevices.length > 0) {
            const tmpRoomMuteDevice = roomMuteDevices[0];
            if (current) {
                caps.hasRoomMute = this.onlineDevices[(_a = tmpRoomMuteDevice.eqId) !== null && _a !== void 0 ? _a : ''] !== undefined;
                caps.hasMicrophoneMute = caps.hasRoomMute; // deprecated in SNTY-2915
                if (caps.hasRoomMute) {
                    caps.isRoomMuteStateless = (_b = tmpRoomMuteDevice.caps.isRoomMuteStateless) !== null && _b !== void 0 ? _b : false;
                    caps.isMicrophoneMuteStateless = caps.isRoomMuteStateless; // deprectated in SNTY-2915
                    caps.isRoomMuteToggle = (_c = tmpRoomMuteDevice.caps.isRoomMuteToggle) !== null && _c !== void 0 ? _c : false;
                    caps.isMicrophoneMuteToggle = caps.isRoomMuteToggle; // deprectated in SNTY-2915
                    this.roomMuteDevice = tmpRoomMuteDevice;
                }
                else {
                    caps.isRoomMuteStateless = false;
                    caps.isMicrophoneMuteStateless = false; // deprectated in SNTY-2915
                    caps.isRoomMuteToggle = false;
                    caps.isMicrophoneMuteToggle = false; // deprectated in SNTY-2915
                }
            }
            else {
                caps.hasRoomMute = tmpRoomMuteDevice !== undefined;
                caps.hasMicrophoneMute = caps.hasRoomMute; // deprecated in SNTY-2915
                caps.isRoomMuteStateless = false;
                caps.isMicrophoneMuteStateless = false; // deprectated in SNTY-2915
                caps.isRoomMuteToggle = false;
                caps.isMicrophoneMuteToggle = false; // deprectated in SNTY-2915
            }
        }
    }
    computeMuteSyncDevices(devs, caps, current) {
        if (current) {
            this.mutes = {};
            this.muteSyncDevices = [];
        }
        if (this.roomMuteDevice === undefined)
            return;
        for (const [equipmentId, dev] of Object.entries(devs)) {
            if (current && dev.caps.hasRoomMute && equipmentId !== this.roomMuteDevice.eqId) {
                this.muteSyncDevices.push(dev);
            }
        }
    }
    computeOverflowRoomsCaps(devs, caps, current) {
        if (current)
            this.devicesByOverflowRoomId = {};
        const overflowRooms = {};
        for (const dev of Object.values(devs)) {
            if (dev.caps.overflowRooms) {
                for (const [roomId, roomCaps] of Object.entries(dev.caps.overflowRooms)) {
                    overflowRooms[roomId] = Sutl.deepCopy(roomCaps);
                    if (current)
                        this.devicesByOverflowRoomId[roomId] = dev;
                }
            }
        }
        if (Object.keys(overflowRooms).length > 0)
            caps.overflowRooms = overflowRooms;
    }
    computeShadeCaps(devs, caps, current) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        return __awaiter(this, void 0, void 0, function* () {
            const shadeControls = {};
            const shadePresets = {};
            const roomId = (_b = (_a = this.roomService.getRoom()) === null || _a === void 0 ? void 0 : _a.$id) !== null && _b !== void 0 ? _b : '';
            const roomConfig = this.roomConfig;
            const roomConfigValidated = this.kayleeService.getRoomConfigValidated(roomId); // already $loaded()
            if (roomConfig === undefined)
                throw new Error(`roomConfig undefined`);
            for (const [shadeControlId, shadeControl] of Object.entries((_c = roomConfig.shadeControls) !== null && _c !== void 0 ? _c : {})) {
                if (shadeControl.type === 'equipment') {
                    if (current) {
                        if (this.onlineDevices[shadeControl.equipmentId] === undefined) {
                            continue;
                        }
                        if (!isValid((_d = roomConfigValidated.shadeControls) === null || _d === void 0 ? void 0 : _d[shadeControlId])) {
                            continue;
                        }
                    }
                    const shadeControlCaps = {
                        index: shadeControl.index,
                        name: shadeControl.name,
                    };
                    if (shadeControl.description !== undefined) {
                        shadeControlCaps.description = shadeControl.description;
                    }
                    if ((_e = shadeControl.position) === null || _e === void 0 ? void 0 : _e.icons) {
                        const actions = {};
                        for (const [motion, icon] of Object.entries(shadeControl.position.icons)) {
                            if (icon === undefined)
                                continue;
                            actions[motion] = { icon };
                        }
                        if (Object.keys(actions).length > 0) {
                            shadeControlCaps.position = { actions, hasState: true };
                            shadeControlCaps.showPositionControls =
                                (_f = shadeControl.showPositionControls) !== null && _f !== void 0 ? _f : true;
                        }
                    }
                    if ((_g = shadeControl.tilt) === null || _g === void 0 ? void 0 : _g.icons) {
                        const actions = {};
                        for (const [motion, icon] of Object.entries(shadeControl.tilt.icons)) {
                            if (icon === undefined)
                                continue;
                            actions[motion] = { icon };
                        }
                        if (Object.keys(actions).length > 0) {
                            shadeControlCaps.tilt = { actions, hasState: true };
                            shadeControlCaps.showTiltControls = (_h = shadeControl.showTiltControls) !== null && _h !== void 0 ? _h : true;
                        }
                    }
                    shadeControls[shadeControlId] = shadeControlCaps;
                }
                else if (shadeControl.type === 'emittedAction') {
                    const shadeControlCaps = {
                        index: shadeControl.index,
                        name: shadeControl.name,
                    };
                    let isOffline = false;
                    if (shadeControl.position) {
                        const actions = {};
                        for (const [motion, emitted] of Object.entries(shadeControl.position)) {
                            if (emitted === undefined)
                                continue;
                            actions[motion] = { icon: emitted.icon };
                            for (const action of Object.values(emitted.actions)) {
                                if (devs[action.equipmentId] === undefined)
                                    isOffline = true;
                            }
                        }
                        if (Object.keys(actions).length > 0) {
                            shadeControlCaps.position = { actions, hasState: false };
                            shadeControlCaps.showPositionControls =
                                (_j = shadeControl.showPositionControls) !== null && _j !== void 0 ? _j : true;
                        }
                    }
                    if (shadeControl.tilt) {
                        const actions = {};
                        for (const [motion, emitted] of Object.entries(shadeControl.tilt)) {
                            if (emitted === undefined)
                                continue;
                            actions[motion] = { icon: emitted.icon };
                            for (const action of Object.values(emitted.actions)) {
                                if (devs[action.equipmentId] === undefined)
                                    isOffline = true;
                            }
                        }
                        if (Object.keys(actions).length > 0) {
                            shadeControlCaps.tilt = { actions, hasState: false };
                            shadeControlCaps.showTiltControls = (_k = shadeControl.showTiltControls) !== null && _k !== void 0 ? _k : true;
                        }
                    }
                    if (!isValid((_m = (_l = roomConfigValidated.shadeControls) === null || _l === void 0 ? void 0 : _l[shadeControlId]) !== null && _m !== void 0 ? _m : 'offline')) {
                        isOffline = true;
                    }
                    if (!current || !isOffline)
                        shadeControls[shadeControlId] = shadeControlCaps;
                }
                else {
                    this.log.error(`unhandled shade control type`);
                }
            }
            for (const [shadePresetId, shadePreset] of Object.entries((_o = roomConfig.shadePresets) !== null && _o !== void 0 ? _o : {})) {
                if (shadePreset.type === 'equipment') {
                    if (current && this.onlineDevices[shadePreset.equipmentId] === undefined)
                        continue;
                    const shadePresetCaps = {
                        icon: shadePreset.icon,
                        index: shadePreset.index,
                        name: shadePreset.name,
                    };
                    if (shadePreset.description !== undefined) {
                        shadePresetCaps.description = shadePreset.description;
                    }
                    shadePresets[shadePresetId] = shadePresetCaps;
                }
                else {
                    this.log.error(`unhandled shade preset type`);
                }
            }
            if (Object.keys(shadeControls).length > 0)
                caps.shadeControls = shadeControls;
            if (Object.keys(shadePresets).length > 0)
                caps.shadePresets = shadePresets;
        });
    }
    computeSoundReinforcementCaps(devs, caps, current) {
        if (current)
            this.devicesBySoundReinforcementId = {};
        const soundReinforcement = {};
        for (const dev of Object.values(devs)) {
            if (dev.caps.soundReinforcement) {
                for (const [zoneId, zoneCaps] of Object.entries(dev.caps.soundReinforcement)) {
                    soundReinforcement[zoneId] = Sutl.deepCopy(zoneCaps);
                    if (current)
                        this.devicesBySoundReinforcementId[zoneId] = dev;
                }
            }
        }
        if (Object.keys(soundReinforcement).length > 0)
            caps.soundReinforcement = soundReinforcement;
    }
    computeUsbCaps(devs, caps, current) {
        var _a;
        const usbSwitchers = {};
        for (const dev of Object.values(devs)) {
            if (dev.caps.usbHosts) {
                const usbHosts = {};
                for (const [hostId, host] of Object.entries(dev.caps.usbHosts)) {
                    usbHosts[hostId] = Object.assign(Object.assign({}, (host.description ? { description: host.description } : undefined)), { icon: host.icon, index: host.index, name: host.name });
                }
                if (Object.keys(usbHosts).length > 0) {
                    usbSwitchers[(_a = dev.eqId) !== null && _a !== void 0 ? _a : ''] = {
                        hasPowerControl: dev.caps.hasPowerControl,
                        isPowerStateless: dev.caps.isPowerStateless,
                        isPowerToggle: dev.caps.isPowerToggle,
                        name: dev.config.name,
                        hosts: usbHosts,
                    };
                }
            }
        }
        if (Object.keys(usbSwitchers).length > 0)
            caps.usbSwitchers = usbSwitchers;
    }
    computeVirtualCameraSwitcherCaps(devs, caps, current) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        let index = 0;
        if (current) {
            this.unassignedCameraIds = [];
        }
        for (const dev of Object.values(devs)) {
            // If there are any unassigned cameras, assign them to a virtual camera switcher device.
            const eq = (_a = this.equipment) === null || _a === void 0 ? void 0 : _a.$recordAt((_b = dev.eqId) !== null && _b !== void 0 ? _b : '');
            const eqType = this.kayleeService.getEquipmentTypes().$recordAt((_c = eq === null || eq === void 0 ? void 0 : eq.type) !== null && _c !== void 0 ? _c : '');
            if ((eqType === null || eqType === void 0 ? void 0 : eqType.type) === 'camera' && Object.keys((_d = dev.caps.cameras) !== null && _d !== void 0 ? _d : {}).length > 0) {
                for (const [cameraId, camera] of Object.entries((_e = dev.caps.cameras) !== null && _e !== void 0 ? _e : {})) {
                    let foundCamera = false;
                    for (const cameraSwitcher of Object.values((_f = caps.cameraSwitchers) !== null && _f !== void 0 ? _f : {})) {
                        for (const assignedCameraId of Object.keys((_g = cameraSwitcher.cameras) !== null && _g !== void 0 ? _g : {})) {
                            const actualCamera = this.getActualCamera(assignedCameraId);
                            if (actualCamera && cameraId === actualCamera.cameraId) {
                                foundCamera = true;
                                break;
                            }
                        }
                        if (foundCamera)
                            break;
                    }
                    if (!foundCamera) {
                        Sutl.setProp(caps, ['cameraSwitchers', '-virtualCameraSwitcher-', 'cameras', cameraId], Sutl.deepCopy(camera));
                        Sutl.setProp(caps, [
                            'cameraSwitchers',
                            '-virtualCameraSwitcher-',
                            'cameras',
                            cameraId,
                            'hasCameraTrackingUi',
                        ], camera.hasCameraTracking &&
                            ((_j = (_h = eq === null || eq === void 0 ? void 0 : eq.config) === null || _h === void 0 ? void 0 : _h.cameraTrackingUiEnabled) !== null && _j !== void 0 ? _j : true));
                        if (dev.caps.cameraPresets) {
                            for (const presetId of Object.keys(dev.caps.cameraPresets)) {
                                const preset = Sutl.deepCopy(dev.caps.cameraPresets[presetId]);
                                preset.index = index++;
                                Sutl.setProp(caps, [
                                    'cameraSwitchers',
                                    '-virtualCameraSwitcher-',
                                    'cameraPresets',
                                    presetId,
                                ], preset);
                                if (current) {
                                    this.devicesByCameraPresetId[presetId] = dev;
                                }
                            }
                        }
                        if (current) {
                            this.unassignedCameraIds.push(cameraId);
                            this.devicesByCameraId[cameraId] = dev;
                            // Don't add to cameraSwitchersByCameraId since the camera switcher is
                            // virtual.
                        }
                    }
                }
            }
        }
    }
    computeVolumeCaps(devs, caps, current) {
        var _a, _b;
        caps.hasRoomVolume = false;
        caps.hasRoomVolumeMute = false;
        if (current)
            this.roomVolumeDevice = undefined;
        const roomVolumeConfig = (_a = this.roomConfig) === null || _a === void 0 ? void 0 : _a.roomVolume;
        // Assume room volume config is undefined or { type: 'none' }. See SNTY-3149.
        if (roomVolumeConfig === undefined) {
            const roomVolumeDevicePriorities = {
                audioDsp: '1',
                userDriver: '2',
                avReceiver: '3',
                amplifier: '4',
                videoCodec: '5',
                videoSwitcher: '6',
                camera: '7',
                display: '8',
                tuner: '9',
            };
            // Find all possible volume devices, sort them based on priority, then choose the highest
            // priority device to be the roomVolumeDevice.
            //
            // Do this calculation with all configured devices, even if they are offline. The mute and
            // volume devices shouldn't change if the primary device is offline.
            const roomVolumeDevices = Object.values(this.devices)
                .filter(dev => {
                const eqClass = this.getEquipmentClass(dev);
                return dev.caps.hasRoomVolume && eqClass in roomVolumeDevicePriorities;
            })
                .sort((devA, devB) => {
                // Sort based on priority, but also include the equipment id to get a repeatable
                // sort order in case there are multiple devices of the same type.
                var _a, _b;
                const eqClassA = this.getEquipmentClass(devA);
                const eqClassB = this.getEquipmentClass(devB);
                const nameA = (_a = roomVolumeDevicePriorities[eqClassA]) !== null && _a !== void 0 ? _a : '9' + devA.eqId;
                const nameB = (_b = roomVolumeDevicePriorities[eqClassB]) !== null && _b !== void 0 ? _b : '9' + devB.eqId;
                if (nameA < nameB)
                    return -1;
                if (nameA > nameB)
                    return 1;
                return 0;
            });
            if (roomVolumeDevices.length > 0) {
                const tmpRoomVolumeDevice = roomVolumeDevices[0];
                if (current) {
                    caps.hasRoomVolume =
                        this.onlineDevices[(_b = tmpRoomVolumeDevice.eqId) !== null && _b !== void 0 ? _b : ''] !== undefined;
                    if (caps.hasRoomVolume)
                        this.roomVolumeDevice = tmpRoomVolumeDevice;
                }
                else {
                    caps.hasRoomVolume = tmpRoomVolumeDevice !== undefined;
                }
                caps.hasRoomVolumeMute = tmpRoomVolumeDevice.caps.hasRoomVolumeMute;
                caps.isRoomVolumeIncDec = tmpRoomVolumeDevice.caps.isRoomVolumeIncDec;
                caps.isRoomVolumeStateless = tmpRoomVolumeDevice.caps.isRoomVolumeStateless;
            }
        }
    }
    computeZoomConnectorCaps(devs, caps, current) {
        caps.hasZoomConnector = false;
        for (const dev of Object.values(devs)) {
            if (dev.caps.hasZoomConnector) {
                caps.hasZoomConnector = true;
                break;
            }
        }
    }
    createDevice(equipmentId, doInteractiveAuthentication) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.equipment)
                throw new Error(`equipment undefined`);
            const eq = this.equipment.$recordAt(equipmentId);
            let dev;
            if (!eq) {
                this.log.error(`createDevice: invalid equipmentId (${equipmentId})`);
                return undefined;
            }
            // Make sure equipment states are loaded so we can use them elsewhere without waiting. One
            // place they are used is internally to KayleeEquipment by $getAvailableVideoInputs and
            // $getVideoOutputs. Other places are $setDetectedState, $setEquipmentInfoState,
            // $setStorage, and $setValidatedState.
            yield eq.$getStates().$loaded();
            const config = Object.assign(Object.assign({}, Sutl.deepCopy((_a = eq.config) !== null && _a !== void 0 ? _a : {})), { equipmentId, equipmentType: eq.type, ipAddress: eq.ipAddress, simulate: eq.simulate });
            if (eq.modelNumber)
                config.modelNumber = eq.modelNumber;
            // Augment the config with the outputs from the equipment type (but not detected outputs).
            const videoOutputs = eq.$getVideoOutputs(false);
            if (videoOutputs) {
                if (Array.isArray(videoOutputs)) {
                    config.videoOutputs = Sutl.arrayToObj(videoOutputs);
                }
                else {
                    config.videoOutputs = Sutl.deepCopy(videoOutputs);
                }
            }
            const driverId = this.getDriverId(eq);
            if (driverId === undefined) {
                this.log.error(`no driver id found for equipment`);
                return;
            }
            const simulate = eq.simulate || this.isBrowser;
            const storage = yield eq.$getStorage().$loaded();
            if (!driverId) {
                this.log.error(`no driver found for driverId ${driverId}`);
                return undefined;
            }
            if (this.roomConfig === undefined)
                throw new Error(`roomConfig undefined`);
            try {
                dev = new EquipmentServiceDriverAdapter(driverId, equipmentId, eq.type, simulate, eq.ipAddress, eq.username, eq.password, doInteractiveAuthentication, this.onConnectStatus, this.onControlStatus, config, storage, this.roomConfig, this.equipment, this.translator);
                this.log.debug(`new driver created for driverId = ${driverId}`);
            }
            catch (error) {
                const driver = Drivers.find(d => d.prototype.key === driverId);
                if (driver === undefined) {
                    this.log.error(`${error.message}\n${error.stack}`);
                    let msg = `createDevice: no driver found for equipment type ${eq.type}`;
                    if (eq.modelNumber) {
                        msg += ` (model number ${eq.modelNumber})`;
                    }
                    msg += ` for eq ${eq}`;
                    this.log.error(msg);
                    return undefined;
                }
                this.log.debug(`old driver created for driverId = ${driverId}`);
                if (simulate) {
                    dev = new Simulator(driver, eq.ipAddress, eq.username, eq.password, this.onConnectStatus, this.onControlStatus, config, this.translator);
                    if (!dev)
                        throw new Error(`dev undefined`);
                    dev.key = driverId;
                }
                else {
                    dev = new driver(eq.ipAddress, eq.username, eq.password, this.onConnectStatus, this.onControlStatus, config, this.translator);
                }
                // Augment device with equipment type and equipment key.
                dev.eqType = eq.type;
                dev.simulate = simulate;
                dev.eqId = equipmentId;
            }
            return dev;
        });
    }
    dialNumberAfterPrefix(callId, dialString) {
        if (!this.isOnline || !this.room)
            return;
        this.room
            .$getCalls(defaults.inara.numRecentCalls)
            .$loaded()
            .then(calls => {
            const call = calls.$recordAt(callId);
            if (!call || call.callState !== 'connected')
                return;
            const dev = this.devicesByCallId[callId];
            if (!dev) {
                this.log.error(`no device for  callId (${callId})`);
                return;
            }
            this.dialStringOverrides[callId] = dialString;
            dev.set('dial', { callId: callId, dialString: dialString });
        });
    }
    didOnlyDisabledCallsChange(dev) {
        if (!dev.eqId)
            throw new Error(`dev.eqId undefined`);
        if (!this.savedCaps[dev.eqId])
            return false;
        const oldCaps = Sutl.deepCopy(this.savedCaps[dev.eqId]);
        const newCaps = Sutl.deepCopy(dev.caps);
        delete oldCaps.disabledCalls;
        delete newCaps.disabledCalls;
        return Sutl.objEquals(oldCaps, newCaps);
    }
    didOnlyVideoInputIncidentalsChange(dev) {
        var _a;
        if (!dev.eqId)
            throw new Error(`dev.eqId undefined`);
        if (!this.savedCaps[dev.eqId])
            return false;
        const oldCaps = Sutl.deepCopy(this.savedCaps[dev.eqId]);
        const newCaps = Sutl.deepCopy(dev.caps);
        for (const caps of [oldCaps, newCaps]) {
            if (caps.videoInputs) {
                for (const input of Object.values((_a = caps.videoInputs) !== null && _a !== void 0 ? _a : {})) {
                    delete input.instructions;
                    delete input.unavailableReason;
                }
            }
        }
        return Sutl.objEquals(oldCaps, newCaps);
    }
    displayPathContainsEquipment(sourcePath, equipmentId, farContent = false) {
        return sourcePath.some(entry => entry.equipmentId === equipmentId && (!farContent || entry.farContent));
    }
    displayPathContainsInput(sourcePath, equipmentId, inputId) {
        return sourcePath.some(entry => entry.equipmentId === equipmentId && entry.videoInputId === inputId);
    }
    doContactClosureBehavior(dev, configId, actionId, behavior, manual) {
        var _a, _b, _c;
        this.log.debug(`doContactClosureBehavior: configId = ${configId}, actionId = ${actionId}, manual = ${manual}`);
        const equipmentId = (_a = dev.eqId) !== null && _a !== void 0 ? _a : '';
        let timeout = (_c = (_b = this.contactClosureTimeouts[equipmentId]) === null || _b === void 0 ? void 0 : _b[configId]) === null || _c === void 0 ? void 0 : _c[actionId];
        if (!timeout) {
            timeout = this.timeouts.create();
            const path = [equipmentId, configId, actionId];
            Sutl.setProp(this.contactClosureTimeouts, path, timeout);
        }
        if (behavior.type === 'held') {
            if (manual !== 'stop') {
                const value = behavior.activeState === 'closed';
                dev.set('contactClosure', { contactClosureId: actionId, value });
                if (manual === undefined) {
                    timeout.set(() => {
                        dev.set('contactClosure', { contactClosureId: actionId, value: !value });
                    }, behavior.automationDurationMs);
                }
            }
            else {
                const value = behavior.activeState === 'open';
                dev.set('contactClosure', { contactClosureId: actionId, value });
            }
        }
        else if (behavior.type === 'latched') {
            if (manual !== 'stop') {
                dev.set('contactClosure', {
                    contactClosureId: actionId,
                    value: behavior.activeState === 'closed',
                });
            }
        }
        else if (behavior.type === 'pulsed') {
            if (manual !== 'stop') {
                const value = behavior.activeState === 'closed';
                dev.set('contactClosure', { contactClosureId: actionId, value });
                timeout.set(() => {
                    dev.set('contactClosure', { contactClosureId: actionId, value: !value });
                }, behavior.durationMs);
            }
        }
    }
    doDisplayMotion(displayId, displayMotionId, motion, manual) {
        var _a, _b, _c, _d, _e, _f, _g;
        const dev = this.devices[displayId];
        if (!dev) {
            this.log.error(`unknown displayId: ${displayId}`);
            return;
        }
        if (displayMotionId === '*') {
            for (const id of Object.keys((_b = (_a = dev.config) === null || _a === void 0 ? void 0 : _a.displayMotion) !== null && _b !== void 0 ? _b : {})) {
                this.doDisplayMotion(displayId, id, motion, manual);
            }
            return;
        }
        const control = (_f = (_e = (_d = (_c = dev.config) === null || _c === void 0 ? void 0 : _c.displayMotion) === null || _d === void 0 ? void 0 : _d[displayMotionId]) === null || _e === void 0 ? void 0 : _e.controls) === null || _f === void 0 ? void 0 : _f[motion];
        if (!control) {
            this.log.error(`control not defined for displayMotionId: ${displayMotionId}`);
            return;
        }
        if (manual !== 'stop')
            this.cancelDisplayMotion(displayId, displayMotionId);
        for (const [actionId, action] of Object.entries(control.actions)) {
            if (action.type === 'contactClosure') {
                const ccDev = this.devices[action.equipmentId];
                if (this.connectStates[(_g = ccDev === null || ccDev === void 0 ? void 0 : ccDev.eqId) !== null && _g !== void 0 ? _g : ''] !== 'online')
                    continue;
                this.doContactClosureBehavior(ccDev, displayMotionId, actionId, action.behavior, manual);
            }
        }
    }
    findCameraByName(cameraSwitcherId, cameraName) {
        var _a, _b;
        const switcherCaps = (_b = (_a = this.caps
            .cameraSwitchers) === null || _a === void 0 ? void 0 : _a[cameraSwitcherId]) === null || _b === void 0 ? void 0 : _b.cameras;
        for (const [cameraId, camera] of Object.entries(switcherCaps !== null && switcherCaps !== void 0 ? switcherCaps : {})) {
            if (camera.name === cameraName) {
                return cameraId;
            }
        }
        return undefined;
    }
    // Some equipment (e.g., Zoom) is a camera switcher, but camera movement may be done directly
    // on a camera equipment (e.g., VISCA). This function returns the actual device and cameraId
    // that should be used.
    getActualCamera(cameraId, controlType) {
        var _a;
        const dev = this.devicesByCameraId[cameraId];
        if (!dev)
            return undefined;
        const extControl = (_a = dev.caps.cameras) === null || _a === void 0 ? void 0 : _a[cameraId].extControl;
        if (!extControl || (controlType !== undefined && !extControl[controlType])) {
            return { dev: dev, cameraId: cameraId };
        }
        const extDev = this.devices[extControl.equipmentId];
        if (!extDev) {
            this.log.error(`external camera device ${extControl.equipmentId} not found`);
            return undefined;
        }
        return { dev: extDev, cameraId: extControl.configId };
    }
    // Given an equipment ID and a cameraId for a camera in that device's config, return the
    // cameraSwitcherId and cameraId within the camera switcher.
    getCameraInSwitcher(equipmentId, cameraId) {
        // First, see if the camera belongs to the virtual camera switcher.
        var _a;
        if (this.unassignedCameraIds.includes(cameraId)) {
            return { cameraSwitcherId: '-virtualCameraSwitcher-', cameraId };
        }
        // Next, see if the camera is managed by another device (e.g., Zoom extControl).
        for (const [devEquipmentId, dev] of Object.entries(this.devices)) {
            for (const [devCameraId, devCameraCaps] of Object.entries((_a = dev.caps.cameras) !== null && _a !== void 0 ? _a : {})) {
                const extControl = devCameraCaps === null || devCameraCaps === void 0 ? void 0 : devCameraCaps.extControl;
                if ((extControl === null || extControl === void 0 ? void 0 : extControl.configId) === cameraId) {
                    return { cameraSwitcherId: devEquipmentId, cameraId: devCameraId };
                }
            }
        }
        // Otherwise, the camera belongs to the device (e.g, a video codec), so there is no
        // translation to do.
        return { cameraSwitcherId: equipmentId, cameraId };
    }
    getCameraSwitcherId(equipmentId) {
        var _a, _b, _c;
        const cameraSwitcherIds = [];
        // First, see if the equipmentId matches a cameraSwitcherId. This is the case for equipment
        // that contains its own cameras.
        if (Object.keys((_a = this.configuredCaps.cameraSwitchers) !== null && _a !== void 0 ? _a : {}).includes(equipmentId)) {
            cameraSwitcherIds.push(equipmentId);
        }
        // If not, then look at the cameras in each of the switchers to see if they specify external
        // control for this equipmentId. This is the case for visca cameras that are configured to
        // be selected by another piece of equipment (e.g., Zoom).
        if (cameraSwitcherIds.length === 0) {
            for (const [switcherId, caps] of Object.entries((_b = this.configuredCaps.cameraSwitchers) !== null && _b !== void 0 ? _b : {})) {
                for (const camera of Object.values((_c = caps.cameras) !== null && _c !== void 0 ? _c : {})) {
                    if (camera.equipmentId === equipmentId) {
                        cameraSwitcherIds.push(switcherId);
                    }
                }
            }
        }
        // Otherwise, assume this is a camera that is part of the virtual camera switcher.
        if (cameraSwitcherIds.length === 0) {
            cameraSwitcherIds.push('-virtualCameraSwitcher-');
        }
        if (cameraSwitcherIds.length === 0) {
            this.log.error(`failed finding camera switcher id for camera with equipmentId = "${equipmentId}`);
        }
        return cameraSwitcherIds;
    }
    getDriverId(equipmentOrEquipmentTypeId, modelNumber) {
        let equipmentTypeId;
        if (typeof equipmentOrEquipmentTypeId === 'string') {
            equipmentTypeId = equipmentOrEquipmentTypeId;
        }
        else if (typeof equipmentOrEquipmentTypeId !== 'string') {
            equipmentTypeId = equipmentOrEquipmentTypeId.type;
            modelNumber = equipmentOrEquipmentTypeId.modelNumber;
        }
        else {
            Log.channel('EquipmentServiceDriverAdapter').error(`getDriverId: invalid arguments`);
            return undefined;
        }
        const equipmentType = this.kayleeService.getEquipmentTypes().$recordAt(equipmentTypeId);
        if (!equipmentType)
            return undefined;
        if ('driverIds' in equipmentType && equipmentType.driverIds !== undefined) {
            if (typeof equipmentType.driverIds === 'string') {
                return equipmentType.driverIds;
            }
            if (modelNumber !== undefined && equipmentType.driverIds[modelNumber] !== undefined) {
                return equipmentType.driverIds[modelNumber];
            }
            const familyId = this.getFamilyId(modelNumber, equipmentType);
            if (familyId !== undefined && equipmentType.driverIds[familyId] !== undefined) {
                return equipmentType.driverIds[familyId];
            }
        }
        return equipmentTypeId;
    }
    getEquipmentClass(dev) {
        var _a, _b;
        const eqType = this.kayleeService.getEquipmentTypes().$recordAt((_a = dev.eqType) !== null && _a !== void 0 ? _a : '');
        if (!eqType) {
            this.log.error(`eqType is ${eqType}`);
            return 'display';
        }
        if (eqType.type === 'videoSwitcher' &&
            Object.keys((_b = dev.config.extronVolumeControlControlChannels) !== null && _b !== void 0 ? _b : {}).length > 0) {
            return 'audioDsp';
        }
        return eqType.type;
    }
    getFamilyId(modelNumberOrEquipment, equipmentType) {
        let modelNumber;
        if (modelNumberOrEquipment === undefined || typeof modelNumberOrEquipment === 'string') {
            modelNumber = modelNumberOrEquipment;
        }
        else {
            modelNumber = modelNumberOrEquipment.modelNumber;
        }
        if (modelNumber === undefined)
            return undefined;
        if (!('modelFamilies' in equipmentType) || equipmentType.modelFamilies === undefined) {
            return undefined;
        }
        for (const [familyId, family] of Object.entries(equipmentType.modelFamilies)) {
            for (const familyModelNumber of family.modelNumbers) {
                if (modelNumber === familyModelNumber)
                    return familyId;
            }
        }
        return undefined;
    }
    getMustIncludeEquipmentId(equipmentId) {
        var _a;
        for (const [sourceId, source] of Object.entries((_a = this.sourceMenus[equipmentId]) !== null && _a !== void 0 ? _a : {})) {
            if (this.isMustInclude(equipmentId, sourceId))
                return source.equipmentId;
        }
        return undefined;
    }
    getPathToDisplaySource(destSource, equipmentId, outId, seenOuts = {}, depth = 0, farContentEquipmentId = undefined) {
        var _a, _b, _c, _d, _e, _f, _g;
        const debug = false;
        const fn = 'getPathToDisplaySource';
        const indent = Array(depth + 2).join('> ');
        const outTag = equipmentId + ':' + outId;
        if (seenOuts[outTag])
            return undefined; // don't do infinite loops
        seenOuts[outTag] = true;
        const dev = this.devices[equipmentId];
        if (!dev) {
            this.log.error(`no device with equipmentId = ${equipmentId}`);
            return undefined;
        }
        if (!((_a = dev.caps) === null || _a === void 0 ? void 0 : _a.videoInputs)) {
            this.log.error(`device equipmentId = ${equipmentId} does not have videoInputs`);
            return undefined;
        }
        if (debug) {
            this.log.debug(`${fn}: ${indent} destSource = ${JSON.stringify(destSource)}, ` +
                `equipmentId = ${equipmentId} (${dev}), outId = ${outId}`);
        }
        let sourcePath;
        let sourcePathLength = 1000;
        if (debug) {
            this.log.debug(`${fn}: ${indent}: videoInputs = ${JSON.stringify(dev.caps.videoInputs, null, 4)}`);
        }
        if (depth === 0) {
            if (!((_b = destSource.bypassFarContent) !== null && _b !== void 0 ? _b : false)) {
                farContentEquipmentId = this.getMustIncludeEquipmentId(equipmentId);
            }
        }
        for (const inId of Object.keys(dev.caps.videoInputs)) {
            const source = dev.caps.videoInputs[inId];
            let newPath;
            let foundLeaf = false;
            if (debug) {
                this.log.debug(`${fn}: ${indent}trying input ${inId}: ${JSON.stringify(source)}`);
            }
            if (source.source) {
                if (depth >= 5) {
                    this.log.error(`maximum call depth reached searching for path to display source`);
                    return undefined;
                }
                newPath = this.getPathToDisplaySource(destSource, source.source.equipmentId, source.source.videoOutputId, seenOuts, depth + 1, farContentEquipmentId);
            }
            else if (destSource.equipmentId === equipmentId && destSource.videoInputId === inId) {
                foundLeaf = true;
                newPath = [];
            }
            if (newPath) {
                let newPathLength = 1000;
                // Historically, video codecs don't include videoOutputs in their caps, and they
                // aren't copied into the config so we need the extra checks here to assume the
                // output is valid if the device is a codec. We should update the code to put
                // videoOutputs in the caps and config for codecs, that will require updating all
                // the codec drivers in the old framework, updating DriverAdapter in the new
                // framework, and auditing the EquipmentService code to make sure all our tests
                // and assumptions are correct when dealing with devices with video inputs.
                const isOutputValid = outId === '-display-' ||
                    dev.caps.videoInputsAreContent ||
                    (!dev.caps.videoInputsAreContent && outId in ((_c = dev.caps.videoOutputs) !== null && _c !== void 0 ? _c : {}));
                if (foundLeaf && destSource.dontSwitchLeaf) {
                    newPathLength = newPath.length + 1;
                }
                else {
                    newPath.push({
                        equipmentId: equipmentId,
                        isOnlineAndValid: this.connectStates[(_d = dev.eqId) !== null && _d !== void 0 ? _d : ''] === 'online' && isOutputValid,
                        isPowered: (_f = (_e = this.videoSwitcherStates[equipmentId]) === null || _e === void 0 ? void 0 : _e.power) !== null && _f !== void 0 ? _f : false,
                        videoOutputId: outId,
                        videoInputId: inId,
                        farContent: (_g = source.farContent) !== null && _g !== void 0 ? _g : false,
                    });
                    newPathLength = newPath.length;
                }
                const oldPathHasFarContent = farContentEquipmentId !== undefined &&
                    sourcePath !== undefined &&
                    this.displayPathContainsEquipment(sourcePath, farContentEquipmentId, true);
                const newPathHasFarContent = farContentEquipmentId !== undefined &&
                    this.displayPathContainsEquipment(newPath, farContentEquipmentId, true);
                if ((newPathHasFarContent && !oldPathHasFarContent) ||
                    (((newPathHasFarContent && oldPathHasFarContent) ||
                        (!newPathHasFarContent && !oldPathHasFarContent)) &&
                        newPathLength < sourcePathLength)) {
                    sourcePath = newPath;
                    sourcePathLength = newPathLength;
                    if (debug) {
                        this.log.debug(`${fn}: ${indent}found better path`);
                    }
                }
                else if (debug) {
                    this.log.debug(`${fn}: ${indent}found worse or equal path`);
                }
            }
            else if (debug) {
                this.log.debug(`${fn}: ${indent}no path`);
            }
        }
        if (debug && depth === 0) {
            this.log.debug(`${fn}: sourcePath = ${JSON.stringify(sourcePath, null, 4)}`);
        }
        return sourcePath;
    }
    getSelectedSource(equipmentId, outId, dontSwitchLeafEqIds, seenOuts = {}, depth = 0) {
        var _a, _b, _c;
        const debug = false;
        const fn = 'getSelectedSource';
        const indent = Array(depth + 2).join('> ');
        if (debug) {
            this.log.debug(`${fn}: ${indent}equipmentId = ${equipmentId}, outId = ${outId}`);
        }
        if (!this.videoSwitcherStates[equipmentId]) {
            // This can happen if the equipment is offline.
            return undefined;
        }
        const inId = (_b = (_a = this.videoSwitcherStates[equipmentId]) === null || _a === void 0 ? void 0 : _a.routes) === null || _b === void 0 ? void 0 : _b[outId];
        if (inId === undefined) {
            // This can happen if a dynamic config is invalid.
            return undefined;
        }
        if (dontSwitchLeafEqIds.includes(equipmentId)) {
            return { equipmentId: equipmentId, videoInputId: null };
        }
        if (inId === null)
            return undefined;
        const dev = this.devices[equipmentId];
        if (!dev) {
            this.log.error(`equipmentId = ${equipmentId} not present in devices`);
            return undefined;
        }
        const inSource = (_c = dev.caps.videoInputs) === null || _c === void 0 ? void 0 : _c[inId];
        if (!inSource) {
            this.log.error(`videoInputId ${inId} not present in caps for equipmentId ${equipmentId}`);
            return undefined;
        }
        if (debug) {
            this.log.debug(`${fn}: ${indent}${inSource}: ${JSON.stringify(inSource, null, 4)}`);
        }
        if (!inSource.source) {
            if (debug) {
                this.log.debug(`${fn}: ${indent}found leaf: inId = ${inId}`);
            }
            return { equipmentId: equipmentId, videoInputId: inId };
        }
        const outTag = inSource.source.equipmentId + inSource.source.videoOutputId;
        if (seenOuts.hasOwnProperty(outTag)) {
            this.log.error(`infinite loop in video network detected at ` +
                `equipmentId ${inSource.source.equipmentId} ` +
                `videoOutputId ${inSource.source.videoOutputId}`);
            return undefined;
        }
        seenOuts[outTag] = true;
        if (depth >= 5) {
            this.log.error(`video network depth exceeded, possible loop`);
            return undefined;
        }
        return this.getSelectedSource(inSource.source.equipmentId, inSource.source.videoOutputId, dontSwitchLeafEqIds, seenOuts, depth + 1);
    }
    getVideoOutputIds(equipmentId) {
        if (!this.equipment)
            throw new Error(`equipment undefined`);
        const eq = this.equipment.$recordAt(equipmentId);
        if (!eq)
            throw new Error(`eq undefined for id ${equipmentId}`);
        const outIds = eq.$getVideoOutputs();
        // For displays, which have no outputs defined, we use an internal ID of "-display-".
        return outIds ? Object.keys(outIds).sort() : ['-display-'];
    }
    handleSaveError(error) {
        // Permission denied errors can happen during $save calls when a controller is unpaired
        // while it is in the middle of a $save. If this happens, log the error via the debug
        // channel since it is an accepted case.
        if ('code' in error && error.code === 'PERMISSION_DENIED') {
            this.log.debug(JSON.stringify(error));
            return;
        }
        this.log.error(error.toString());
    }
    hangupSynchronizedCalls(callType, parentEquipmentId) {
        var _a, _b, _c;
        if (!this.room)
            return;
        const calls = this.room.$getCalls(defaults.inara.numRecentCalls);
        // Make sure the parent hasn't started a new call of this type.
        for (const call of calls) {
            if (call.callType === callType &&
                call.callState !== 'disconnected' &&
                ((_a = this.devicesByCallId[call.$id]) === null || _a === void 0 ? void 0 : _a.eqId) === parentEquipmentId)
                return;
        }
        // Hangup any calls of this type on synchronized children.
        for (const [callId, call] of Object.entries(this.synchronizedCalls)) {
            const dev = this.devicesByCallId[callId];
            if (((_c = (_b = dev === null || dev === void 0 ? void 0 : dev.config.synchronizedCalls) === null || _b === void 0 ? void 0 : _b[callType]) === null || _c === void 0 ? void 0 : _c.parentEquipmentId) ===
                parentEquipmentId &&
                call.callState !== 'disconnected') {
                dev.set('hangup', callType === 'zoom' ? { callId, endMeeting: false } : callId);
            }
        }
    }
    initializeVideoSwitcherStates(devs, caps) {
        this.videoSwitcherStates = {};
        for (const [equipmentId, dev] of Object.entries(devs)) {
            if (dev.caps.hasVideoInputs) {
                this.videoSwitcherStates[equipmentId] = {
                    routes: {},
                };
                if (dev.caps.hasPowerControl) {
                    this.videoSwitcherStates[equipmentId].power = false;
                }
                const outIds = this.getVideoOutputIds(equipmentId);
                for (const inId of outIds) {
                    this.videoSwitcherStates[equipmentId].routes[inId] = null;
                }
            }
        }
    }
    isDisplayPathFarContent(sourcePath) {
        return sourcePath.some(entry => entry.farContent);
    }
    isDisplayPathOnline(sourcePath) {
        return sourcePath.every(entry => entry.isOnlineAndValid);
    }
    isDisplayPathVideoInputStateless(sourcePath) {
        return sourcePath.some(entry => { var _a, _b, _c; return (_c = (_b = (_a = this.devices[entry.equipmentId]) === null || _a === void 0 ? void 0 : _a.caps) === null || _b === void 0 ? void 0 : _b.isVideoInputStateless) !== null && _c !== void 0 ? _c : false; });
    }
    isMustInclude(equipmentId, sourceId) {
        var _a, _b, _c, _d;
        if (!this.equipment)
            throw new Error(`equipment undefined`);
        const destSource = this.sourceMenus[equipmentId][sourceId];
        const destEq = this.equipment.$recordAt(destSource.equipmentId);
        const destSources = destEq === null || destEq === void 0 ? void 0 : destEq.$getAvailableVideoInputs();
        if (!destSources)
            return false;
        // Find the correct source definition by matching the code property.
        const code = (_c = (_b = (_a = this.devices) === null || _a === void 0 ? void 0 : _a[destSource.equipmentId].caps.videoInputs) === null || _b === void 0 ? void 0 : _b[destSource.videoInputId]) === null || _c === void 0 ? void 0 : _c.code;
        if (code === undefined) {
            this.log.error(`invalid videoInput: ${JSON.stringify(destSource)}`);
            return false;
        }
        let mustInclude;
        for (const ds of Object.values(destSources)) {
            if (code === ds.code) {
                mustInclude = (_d = ds.mustInclude) !== null && _d !== void 0 ? _d : false;
                break;
            }
        }
        if (mustInclude === undefined) {
            this.log.warning(`no source with matching code for ${JSON.stringify(destSource)}`);
            return false;
        }
        return mustInclude;
    }
    isOnlyMustInclude(eq, sourceId, caps = this.caps) {
        var _a, _b, _c;
        if (!this.isMustInclude(eq.$id, sourceId))
            return false;
        // We are a "mustInclude". Now see if there are any other inputs from this equipment in the
        // sourceMenu.
        const destSource = this.sourceMenus[eq.$id][sourceId];
        let foundEquipment = false;
        for (const sourceMenuId of Object.keys(this.sourceMenus[eq.$id])) {
            if (sourceMenuId === sourceId)
                continue;
            const source = (_c = (_b = (_a = caps.displays) === null || _a === void 0 ? void 0 : _a[eq.$id]) === null || _b === void 0 ? void 0 : _b.sources) === null || _c === void 0 ? void 0 : _c[sourceMenuId];
            if (!source) {
                this.log.error(`source undefined`);
                continue;
            }
            if (source.bypassFarContent)
                continue;
            const sourcePath = this.getPathToDisplaySource(source, eq.$id, this.getVideoOutputIds(eq.$id)[0]);
            if (!sourcePath) {
                this.log.error(`sourcePath undefined`);
                continue;
            }
            if (this.displayPathContainsEquipment(sourcePath, destSource.equipmentId)) {
                foundEquipment = true;
                break;
            }
        }
        return !foundEquipment;
    }
    isRunningUiPreview() {
        var _a, _b, _c, _d;
        return (this.isBrowser &&
            this.roomService.isRoomLoaded() &&
            ((_b = (_a = this.roomService.getRoom()) === null || _a === void 0 ? void 0 : _a.hasOwnProperty('uiPreview')) !== null && _b !== void 0 ? _b : false) &&
            ((_d = (_c = this.roomService.getRoom()) === null || _c === void 0 ? void 0 : _c.uiPreview) === null || _d === void 0 ? void 0 : _d.controllerId) === this.uiPreviewControllerId);
    }
    isShutdown() {
        return this.equipment === undefined;
    }
    onBeforeUnload() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.room)
                return;
            // If this was a ui preview session, deactivate the session when the tab/window is closed.
            if (((_a = this.room.uiPreview) === null || _a === void 0 ? void 0 : _a.controllerId) === this.uiPreviewControllerId) {
                yield this.room.$deactivateUiPreview();
                return;
            }
            // Else if this was a temporary remote control session, remove the remote control session
            // when the tab/window is closed.
            const remoteControlSessions = yield this.room.$getRemoteControlSessions().$loaded();
            const sessionId = yield remoteControlSessions.$getThisSessionId();
            if (!sessionId)
                return;
            yield ((_b = remoteControlSessions.$recordAt(sessionId)) === null || _b === void 0 ? void 0 : _b.$remove());
        });
    }
    onCompanyChanged(company) {
        var _a;
        if (this.room && ((_a = this.room.info) === null || _a === void 0 ? void 0 : _a.organizationName) !== company.name) {
            const roomValues = this.room.$getValues();
            Sutl.setNode(roomValues, ['info', 'organizationName'], company.name);
            this.room.$set(roomValues, false, false);
        }
    }
    onConnectStatus(dev) {
        var _a, _b, _c, _d, _e;
        if (!this.equipment)
            throw new Error(`equipment undefined`);
        const prevReceivedConnectStatus = (_a = dev.receivedConnectStatus) !== null && _a !== void 0 ? _a : false;
        dev.receivedConnectStatus = true;
        const eq = this.equipment.$recordAt((_b = dev.eqId) !== null && _b !== void 0 ? _b : '');
        if (!eq) {
            this.log.error(`onConnectStatus: invalid equipment key (${dev.eqId})`);
            return;
        }
        const prevOnline = eq.connectState === 'online';
        this.updateConnectState(dev); // updates eq and connectStates
        const connectState = (_d = this.connectStates[(_c = dev.eqId) !== null && _c !== void 0 ? _c : '']) !== null && _d !== void 0 ? _d : 'connectFailed';
        const currOnline = connectState === 'online';
        this.log.debug(`${eq}: connect state = ${connectState}, driver connect state = ${dev.connectState}`);
        if (!prevReceivedConnectStatus || prevOnline !== currOnline) {
            if (!currOnline)
                delete this.driverValidated[(_e = dev.eqId) !== null && _e !== void 0 ? _e : ''];
            this.updateCapsAndOnlineStatus();
        }
    }
    onControlStatus(dev, param, value) {
        this.timeouts.set(() => this._onControlStatus(dev, param, value), 1);
    }
    _onControlStatus(dev, param, value) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12;
        if (param === 'callInList') {
            const civ = value;
            const numEntries = (_b = (_a = civ.values) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
            this.log.debug(`${dev}: ${param} = { id: ${civ.id}, values: { ${numEntries} entries }`);
        }
        else if (param === 'directory') {
            this.log.debug(`${dev}: ${param} = { ${Object.keys(value).length} entries }`);
        }
        else if (param !== 'ping') {
            // only old framework has ping
            this.log.debug(`${dev}: ${param} = ${JSON.stringify(value)}`);
        }
        // We can get a stray command (usually power) when the room is offline under some
        // some conditions, such as unpairing the controller. If that happens, we just igore them.
        if (this.room === undefined) {
            this.log.debug(`ignoring command since room is offline`);
            return;
        }
        // Drivers shouldn't send control commands unless queried, but some do (due to legacy
        // reasons). EquipmentService queries all the information it needs from the devices
        // when the room goes online. Some device commands (config and caps) are required when the
        // room is offline though. So, we allow those through, but throw away other control
        // commands when the room is offline. In the long-term, the drivers should be fixed and
        // this code removed.
        if (!this.isOnline &&
            param !== 'caps' &&
            param !== 'configDetected' &&
            param !== 'configValidated' &&
            param !== 'detected' &&
            param !== 'equipmentInfo' &&
            param !== 'storage' &&
            param !== 'validated') {
            // only old framework has ping
            if (param !== 'ping') {
                this.log.debug(`ignoring command since no devices are online`);
            }
            return;
        }
        if (dev.eqId === undefined)
            throw new Error(`equipmentId undefined`);
        if (this.equipment === undefined)
            throw new Error(`equipment undefined`);
        const roomValues = this.room.$getValues();
        switch (param) {
            case 'assistiveListeningMute':
                if (dev === this.assistiveListeningDevice) {
                    Sutl.setProp(roomValues, 'states.assistiveListening.mute', value);
                    this.room.$set(roomValues, false, false);
                }
                break;
            case 'assistiveListeningVolume':
                if (dev === this.assistiveListeningDevice) {
                    Sutl.setProp(roomValues, 'states.assistiveListening.volume', value);
                    this.room.$set(roomValues, false, false);
                }
                break;
            case 'audioInputMute': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'audioInputSources', cvalue.srcId, 'mute'], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'audioInputVolume': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'audioInputSources', cvalue.srcId, 'volume'], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'busy': {
                if (((_c = this.caps.displays) === null || _c === void 0 ? void 0 : _c[dev.eqId]) !== undefined) {
                    Sutl.setProp(roomValues, ['states', 'displays', dev.eqId, 'busy'], value);
                }
                if (((_d = this.caps.cameraSwitchers) === null || _d === void 0 ? void 0 : _d[dev.eqId]) !== undefined) {
                    Sutl.setProp(roomValues, ['states', 'cameraSwitchers', dev.eqId, 'busy'], value);
                }
                if (((_e = this.caps.cameraSwitchers) === null || _e === void 0 ? void 0 : _e['-virtualCameraSwitcher-']) !== undefined) {
                    for (const cameraId of this.unassignedCameraIds) {
                        if (((_f = this.devicesByCameraId[cameraId]) === null || _f === void 0 ? void 0 : _f.eqId) === dev.eqId) {
                            Sutl.setProp(roomValues, ['states', 'cameraSwitchers', '-virtualCameraSwitcher-', 'busy'], value);
                            break;
                        }
                    }
                }
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'calendar':
                this.room.$setCalendar(value);
                break;
            case 'callInList': {
                const cvalue = value;
                if (cvalue.values === null) {
                    (_h = (_g = roomValues.states) === null || _g === void 0 ? void 0 : _g.callInLists) === null || _h === void 0 ? true : delete _h[cvalue.id];
                }
                else {
                    Sutl.setProp(roomValues, ['states', 'callInLists', cvalue.id], cvalue.values);
                }
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'callParticipant': {
                const cvalue = value;
                const calls = this.room.$getCalls(defaults.inara.numRecentCalls);
                const call = calls.$recordAt(cvalue.callId);
                if (call) {
                    if (call.callState !== 'disconnected') {
                        // Set isSynchronizedCall property if necessary.
                        let participant = cvalue.participant;
                        if (participant) {
                            for (const childCall of Object.values(this.synchronizedCalls)) {
                                if (childCall.parentCallId === call.$id) {
                                    participant = Object.assign(Object.assign({}, participant), { isSynchronizedCall: true });
                                    break;
                                }
                            }
                        }
                        // Update participant.
                        this.callUpdateManager.updateParticipant(cvalue.callId, cvalue.participantId, participant);
                    }
                }
                else if ((_j = cvalue.participant) === null || _j === void 0 ? void 0 : _j.isMe) {
                    const childCall = this.synchronizedCalls[cvalue.callId];
                    if (childCall && childCall.callState !== 'disconnected') {
                        if (this.augmentChildSynchronizedCall(dev, childCall, (_k = cvalue.participant) === null || _k === void 0 ? void 0 : _k.userId)) {
                            this.updateSynchronizedCallParticipants((_l = childCall.parentCallId) !== null && _l !== void 0 ? _l : '', cvalue.callId);
                        }
                    }
                }
                break;
            }
            case 'callParticipants': {
                const cvalue = value;
                const calls = this.room.$getCalls(defaults.inara.numRecentCalls);
                const call = calls.$recordAt(cvalue.callId);
                if (call) {
                    if (call.callState !== 'disconnected') {
                        // Set isSynchronizedCall property on corresponding participants.
                        let participants;
                        if (cvalue.participants) {
                            let modified = false;
                            for (const childCall of Object.values(this.synchronizedCalls)) {
                                if (childCall.parentCallId === call.$id) {
                                    if (participants === undefined) {
                                        participants = Object.assign({}, cvalue.participants);
                                        modified = true;
                                    }
                                    for (const [participantId, participant] of Object.entries(participants)) {
                                        if (participant.userId === childCall.userId) {
                                            participants[participantId] = Object.assign(Object.assign({}, participant), { isSynchronizedCall: true });
                                        }
                                    }
                                }
                            }
                            if (!modified)
                                participants = cvalue.participants;
                        }
                        // Update participants.
                        this.callUpdateManager.updateParticipants(cvalue.callId, participants);
                    }
                }
                else {
                    const childCall = this.synchronizedCalls[cvalue.callId];
                    if (childCall && childCall.callState !== 'disconnected') {
                        if (this.augmentChildSynchronizedCall(dev, childCall, cvalue.participants)) {
                            this.updateSynchronizedCallParticipants((_m = childCall.parentCallId) !== null && _m !== void 0 ? _m : '', cvalue.callId);
                        }
                    }
                }
                break;
            }
            case 'callStatus': {
                const cvalue = Sutl.deepCopy(value);
                if (cvalue.callState === 'disconnected') {
                    delete this.devicesByCallId[cvalue.callId];
                    delete this.dialStringOverrides[cvalue.callId];
                    delete this.initialSpotlightSent[cvalue.callId];
                    delete this.synchronizedCallsPlaced[cvalue.callId];
                }
                else {
                    this.devicesByCallId[cvalue.callId] = dev;
                }
                if (((_o = dev.eqId) !== null && _o !== void 0 ? _o : '') in this.synchronizedCallDevices[cvalue.callType]) {
                    if (cvalue.callState === 'disconnected') {
                        delete this.synchronizedCalls[cvalue.callId];
                    }
                    else {
                        const prevCallStatus = this.synchronizedCalls[cvalue.callId];
                        const parentCallId = prevCallStatus === null || prevCallStatus === void 0 ? void 0 : prevCallStatus.parentCallId;
                        const userId = prevCallStatus === null || prevCallStatus === void 0 ? void 0 : prevCallStatus.userId;
                        const parentConsentedToRecording = prevCallStatus === null || prevCallStatus === void 0 ? void 0 : prevCallStatus.parentConsentedToRecording;
                        const prevRecordingConsentRequired = (prevCallStatus === null || prevCallStatus === void 0 ? void 0 : prevCallStatus.callExtendedState) === 'recordingConsentRequired';
                        const newCallStatus = Object.assign({}, cvalue);
                        if (parentCallId) {
                            newCallStatus.parentCallId = parentCallId;
                        }
                        if (userId) {
                            newCallStatus.userId = userId;
                        }
                        if (parentConsentedToRecording) {
                            newCallStatus.parentConsentedToRecording = parentConsentedToRecording;
                        }
                        if (prevRecordingConsentRequired) {
                            newCallStatus.callExtendedState = 'recordingConsentRequired';
                        }
                        this.synchronizedCalls[cvalue.callId] = newCallStatus;
                        // Consent to recording for the synchronized call device if its parent
                        // has consented. We have to implement this both here and in the
                        // consentToRecording room control command handler in case the user grants
                        // consent to the parent call before we get the recordingConsentRequired
                        // status for the synchronized call.
                        if (newCallStatus.callExtendedState === 'recordingConsentRequired' &&
                            !prevRecordingConsentRequired) {
                            dev.set('consentToRecording', { callId: cvalue.callId, value: true });
                        }
                    }
                }
                else {
                    this.room
                        .$getCalls(defaults.inara.numRecentCalls)
                        .$loaded()
                        .then(calls => {
                        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
                        if (!this.room)
                            throw new Error(`room undefined`);
                        const call = calls.$recordAt(cvalue.callId);
                        const extra = this.extraCallInfo[cvalue.callId];
                        if (extra) {
                            if (extra.dialStringOverride !== undefined) {
                                this.dialStringOverrides[cvalue.callId] =
                                    extra.dialStringOverride;
                                delete extra.dialStringOverride;
                            }
                            if (extra.dialPrefix !== undefined) {
                                const delaySeconds = (_b = (_a = this.kayleeService.getSite(this.room.siteId).settings) === null || _a === void 0 ? void 0 : _a.telephonyDialingPrefixDelay) !== null && _b !== void 0 ? _b : 2;
                                this.dialNumberAfterPrefixTimeouts.set(() => {
                                    if (this.room) {
                                        this.dialNumberAfterPrefix(cvalue.callId, this.dialStringOverrides[cvalue.callId]);
                                    }
                                }, 1000 * delaySeconds);
                            }
                            delete this.extraCallInfo[cvalue.callId];
                        }
                        if (this.pendingCallExtendedTypes[(_c = dev.eqId) !== null && _c !== void 0 ? _c : '']) {
                            if (Date.now() -
                                this.pendingCallExtendedTypes[(_d = dev.eqId) !== null && _d !== void 0 ? _d : ''].timestamp <
                                5000) {
                                this.callExtendedTypeOverrides[cvalue.callId] = this.pendingCallExtendedTypes[(_e = dev.eqId) !== null && _e !== void 0 ? _e : ''].callExtendedType;
                            }
                            delete this.pendingCallExtendedTypes[(_f = dev.eqId) !== null && _f !== void 0 ? _f : ''];
                        }
                        if (this.callExtendedTypeOverrides[cvalue.callId] !== undefined) {
                            cvalue.callExtendedType = this.callExtendedTypeOverrides[cvalue.callId];
                        }
                        if (this.dialStringOverrides[cvalue.callId] !== undefined) {
                            cvalue.dialString = this.dialStringOverrides[cvalue.callId];
                        }
                        if (cvalue.callState === 'disconnected') {
                            delete this.callExtendedTypeOverrides[cvalue.callId];
                            delete this.extraCallInfo[cvalue.callId];
                            delete this.dialStringOverrides[cvalue.callId];
                            // Delete call participants when call ends.
                            this.callUpdateManager.updateParticipants(cvalue.callId, undefined);
                            // Reset receive gain to 0 when call ends.
                            if ((!call || call.callState !== 'disconnected') &&
                                ((_g = cvalue.receiveGain) !== null && _g !== void 0 ? _g : 0) !== 0) {
                                dev.set('receiveGain', { callId: cvalue.callId, value: 0 });
                            }
                            // Hangup synchronized calls (in case call was terminated remotely).
                            this.hangupSynchronizedCallTimeouts.set(() => {
                                var _a;
                                this.hangupSynchronizedCalls(cvalue.callType, (_a = dev.eqId) !== null && _a !== void 0 ? _a : '');
                            }, 2000);
                        }
                        // Send host key if required.
                        if (cvalue.callType === 'zoom' &&
                            cvalue.callState === 'connected' &&
                            (call === null || call === void 0 ? void 0 : call.callState) !== 'connected' &&
                            cvalue.dialString &&
                            ((_h = dev.config.synchronizedCalls) === null || _h === void 0 ? void 0 : _h.zoom) === undefined &&
                            dev.config.zoomHostKeys &&
                            dev.config.zoomHostKeys[cvalue.dialString]) {
                            const hostKey = dev.config.zoomHostKeys[cvalue.dialString];
                            dev.set('sendHostKey', { callId: cvalue.callId, value: hostKey });
                        }
                        // Place call on synchronized devices if necessary.
                        if (cvalue.direction === 'outgoing' &&
                            cvalue.callState === 'connected' &&
                            !this.synchronizedCallsPlaced[cvalue.callId] &&
                            cvalue.dialString !== undefined) {
                            const syncDevs = this.synchronizedCallDevices[cvalue.callType];
                            const password = (_k = (_j = call === null || call === void 0 ? void 0 : call.meetingCodes) === null || _j === void 0 ? void 0 : _j[0]) !== null && _k !== void 0 ? _k : cvalue.meetingPassword;
                            const callInfo = Object.assign({ callId: '', callType: cvalue.callType, dialString: cvalue.dialString }, (password ? { meetingCodes: [password] } : undefined));
                            for (const syncDev of Object.values(syncDevs)) {
                                if (((_m = (_l = syncDev.config.synchronizedCalls) === null || _l === void 0 ? void 0 : _l[cvalue.callType]) === null || _m === void 0 ? void 0 : _m.parentEquipmentId) === dev.eqId) {
                                    syncDev.set('call', Object.assign(Object.assign({}, callInfo), { callId: Sutl.generateFirebaseId() }));
                                }
                            }
                            this.synchronizedCallsPlaced[cvalue.callId] = true;
                        }
                        const kvalue = Object.assign(Object.assign({}, cvalue), { meetingCodes: {} });
                        if (cvalue.meetingCodes !== undefined) {
                            kvalue.meetingCodes = Sutl.arrayToObj(cvalue.meetingCodes, 'a');
                        }
                        else {
                            delete kvalue.meetingCodes;
                        }
                        if (!call) {
                            this.room.$addCall(kvalue);
                        }
                        else {
                            // The drivers aren't aware of the active meeting code property, so
                            // we have to explicitly add it to the update.
                            if (call.activeMeetingCode !== undefined) {
                                kvalue.activeMeetingCode = call.activeMeetingCode;
                            }
                            this.callUpdateManager.updateCall(cvalue.callId, kvalue);
                            if (cvalue.callState === 'connected') {
                                this.sendInitialSpotlight(call);
                            }
                        }
                    });
                }
                const onAir = Object.keys(this.devicesByCallId).length > 0;
                if (onAir !== ((_p = this.room.states) === null || _p === void 0 ? void 0 : _p.onAir)) {
                    Sutl.setProp(roomValues, 'states.onAir', onAir);
                    this.room.$set(roomValues, false, false);
                }
                break;
            }
            case 'cameraPosition':
                const eq = this.equipment.$recordAt(dev.eqId);
                if (!eq)
                    throw new Error(`no equipment for id ${dev.eqId}`);
                const eqStatesValues = eq.$getStates().$getValues(); // assume states already $loaded()
                let cameraPositionResults = eqStatesValues.cameraPositionResults;
                // Remove old responses. For now, just keep the most recent three responses and
                // remove the rest.
                if (cameraPositionResults) {
                    cameraPositionResults = Object.assign({}, cameraPositionResults);
                    let n = 0;
                    for (const requestId of Object.keys(eqStatesValues.cameraPositionResults).sort()) {
                        if (++n >= 3)
                            delete cameraPositionResults[requestId];
                    }
                }
                else {
                    cameraPositionResults = {};
                }
                const cvalue = value;
                if ('error' in cvalue) {
                    cameraPositionResults[cvalue.requestId] = {
                        cameraId: cvalue.cameraId,
                        error: cvalue.error,
                    };
                }
                else {
                    cameraPositionResults[cvalue.requestId] = Object.assign({ cameraId: cvalue.cameraId, pan: cvalue.pan, tilt: cvalue.tilt }, ('zoom' in cvalue ? { zoom: cvalue.zoom } : undefined));
                }
                eqStatesValues.cameraPositionResults = cameraPositionResults;
                eq.$getStates().$set(eqStatesValues, false);
                break;
            case 'cameraPreset':
                const cameraSwitcherIds = this.getCameraSwitcherId(dev.eqId);
                for (const cameraSwitcherId of cameraSwitcherIds) {
                    Sutl.setProp(roomValues, ['states', 'cameraSwitchers', cameraSwitcherId, 'cameraPreset'], value);
                }
                if (cameraSwitcherIds.length > 0)
                    this.room.$set(roomValues, false, false);
                break;
            case 'cameraSelected': {
                if (value !== null &&
                    typeof value !== 'string' &&
                    'cameraId' in value) {
                    // old way
                    const cvalue = value; // old type has same properties
                    Sutl.setProp(roomValues, [
                        'states',
                        'cameraSwitchers',
                        dev.eqId,
                        'cameras',
                        cvalue.cameraId,
                        'selected',
                    ], cvalue.value);
                }
                else {
                    // new way (new driver framework)
                    const cvalue = value;
                    const cameraSwitcherId = (_q = this.getCameraSwitcherId(dev.eqId)) === null || _q === void 0 ? void 0 : _q[0];
                    if (cameraSwitcherId !== undefined) {
                        const cameras = (_u = (_t = (_s = (_r = this.room.states) === null || _r === void 0 ? void 0 : _r.cameraSwitchers) === null || _s === void 0 ? void 0 : _s[cameraSwitcherId]) === null || _t === void 0 ? void 0 : _t.cameras) !== null && _u !== void 0 ? _u : {};
                        for (const cameraId of Object.keys(cameras)) {
                            Sutl.setProp(roomValues, [
                                'states',
                                'cameraSwitchers',
                                cameraSwitcherId,
                                'cameras',
                                cameraId,
                                'selected',
                            ], false);
                        }
                        Sutl.setProp(roomValues, [
                            'states',
                            'cameraSwitchers',
                            cameraSwitcherId,
                            'cameras',
                            cvalue,
                            'selected',
                        ], true);
                    }
                    this.room.$set(roomValues, false, false);
                }
                break;
            }
            case 'cameraSelfView': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'cameraSwitchers', dev.eqId, 'selfView'], cvalue);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'cameraSelfViewAuto': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'cameraSwitchers', dev.eqId, 'selfViewAuto'], cvalue);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'cameraSelfViewAvailable': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'cameraSwitchers', dev.eqId, 'selfViewAvailable'], cvalue);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'cameraSelfViewPosition': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'cameraSwitchers', dev.eqId, 'selfViewPosition'], cvalue);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'cameraTracking': {
                const cvalue = value;
                const { cameraSwitcherId, cameraId } = this.getCameraInSwitcher(dev.eqId, cvalue.cameraId);
                Sutl.setProp(roomValues, [
                    'states',
                    'cameraSwitchers',
                    cameraSwitcherId,
                    'cameras',
                    cameraId,
                    'tracking',
                ], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'caps':
                if (this.didOnlyVideoInputIncidentalsChange(dev)) {
                    // Some devices (e.g. Zoom) may broadcast new caps just to update the
                    // unavailableReason or video input instructions (e.g., when a meeting starts
                    // and the meeting id changes). Instead of updating all the caps and re-querying
                    // every device (which can be a big performance hit with very large
                    // directories), we just update the incidentals.
                    this.updateVideoInputIncidentals(dev);
                }
                else if (this.didOnlyDisabledCallsChange(dev)) {
                    this.updateDisabledCalls(dev);
                }
                else {
                    this.updateCapsAndOnlineStatus();
                }
                break;
            case 'channel': {
                const cvalue = value;
                this.updateDisplaySourceState(dev, 'channel', cvalue, roomValues);
                break;
            }
            case 'channelTuner': {
                const cvalue = value;
                this.updateDisplaySourceState(dev, 'channelTuner', cvalue, roomValues);
                break;
            }
            case 'configDetected': // fall through
            case 'configValidated': {
                // The value contains a path array and either a 'detected' or 'validated' property.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const cvalue = value;
                const eq = this.equipment.$recordAt(dev.eqId);
                if (!eq)
                    throw new Error(`no equipment for id ${dev.eqId}`);
                const obj = cvalue[param.replace(/^(config)/, '').toLowerCase()];
                Sutl.setProp(eq, ['config'].concat(cvalue.path), Sutl.deepCopy(obj));
                // Delete detected channels in equipmentStates. This is for drivers that sometimes
                // write detected channels to equipmentStates (e.g., videoInputs) and other times
                // write detected channels to equipment config (e.g., DSP channels), such as the
                // Extron video switcher and AVPro Edge video switchers. We assume no driver needs
                // to write to both.
                const deleteEquipmentStatesDetectedOrValidated = (_x = (param === 'configDetected'
                    ? (_v = this.equipment.$recordAt(dev.eqId)) === null || _v === void 0 ? void 0 : _v.$setDetectedState({})
                    : (_w = this.equipment.$recordAt(dev.eqId)) === null || _w === void 0 ? void 0 : _w.$setValidatedState({}))) !== null && _x !== void 0 ? _x : Promise.resolve();
                deleteEquipmentStatesDetectedOrValidated
                    .then(() => __awaiter(this, void 0, void 0, function* () { return eq.$save(false, false); }))
                    .then(() => this.updateConnectStates())
                    .catch(this.handleSaveError);
                break;
            }
            case 'contentSource':
                this.updateVideoSwitcherStates(dev.eqId, value);
                break;
            case 'contentState': {
                const cvalue = value;
                this.updateContentStates(dev.eqId, cvalue.id, cvalue.state);
                break;
            }
            case 'detected': {
                // If necessary, delete detected channels in the equipment config. This is for
                // drivers that sometimes write detected channels to the equipment config (DSP
                // channels) and other times the equipment states (video inputs), such as the
                // Extron video switchers and AVPro Edge video switchers. We assume no driver needs
                // to write to both.
                let deleteDetectedControlChannels = false;
                const eq = this.equipment.$recordAt(dev.eqId);
                if (!eq)
                    throw new Error(`no equipment for id: ${dev.eqId}`);
                for (const key of Object.keys((_y = eq.config) !== null && _y !== void 0 ? _y : {})) {
                    if (key.endsWith('ControlChannels') &&
                        Sutl.getProp(eq, ['config', key, 'detected'])) {
                        Sutl.deleteNode(eq, ['config', key, 'detected']);
                        deleteDetectedControlChannels = true;
                    }
                }
                if (deleteDetectedControlChannels)
                    eq.$save(false, false);
                // Save the new detected channels.
                (_z = this.equipment.$recordAt(dev.eqId)) === null || _z === void 0 ? void 0 : _z.$setDetectedState(value);
                if (value &&
                    typeof value === 'object' &&
                    ('videoInputs' in value || 'videoOutputs' in value)) {
                    // Do partial update for just video switchers.
                    this.updateCapsAndOnlineStatus(false);
                }
                break;
            }
            case 'directory':
                this.directoryManager.updateEquipmentDirectory(dev.eqId, value.entries, dev.config.presenceEnabled);
                break;
            case 'directoryEntry': {
                const cvalue = value;
                let directoryEntry = Object.assign({}, cvalue.value);
                delete directoryEntry.$id;
                this.directoryManager.updateEquipmentDirectoryEntry(dev.eqId, cvalue.id, cvalue.value, dev.config.presenceEnabled);
                break;
            }
            case 'displayBlank':
                Sutl.setProp(roomValues, ['states', 'displays', dev.eqId, 'blank'], value);
                this.room.$set(roomValues, false, false);
                break;
            case 'displayFreeze':
                Sutl.setProp(roomValues, ['states', 'displays', dev.eqId, 'freeze'], value);
                this.room.$set(roomValues, false, false);
                break;
            case 'equipmentInfo':
                (_0 = this.equipment.$recordAt(dev.eqId)) === null || _0 === void 0 ? void 0 : _0.$setEquipmentInfoState(value);
                break;
            case 'firmwareVersion': {
                const cvalue = value;
                if (cvalue !== 'unknown') {
                    const eq = this.equipment.$recordAt(dev.eqId);
                    if (!eq)
                        throw new Error(`no equipment for id ${dev.eqId}`);
                    eq.firmwareVersion = cvalue;
                    eq.$save(false, false).catch(this.handleSaveError);
                }
                break;
            }
            case 'lampOperationTime': {
                const eq = this.equipment.$recordAt(dev.eqId);
                if (!eq)
                    throw new Error(`no equipment for id ${dev.eqId}`);
                const eqStatesValues = eq.$getStates().$getValues();
                if (!eqStatesValues.equipmentInfo)
                    eqStatesValues.equipmentInfo = {};
                eqStatesValues.equipmentInfo.lampOperationTime = value;
                eq.$getStates().$set(eqStatesValues, false);
                break;
            }
            case 'lightingLevel': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'lightingControls', cvalue.lightingControlId, 'level'], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'lightingPower': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'lightingControls', cvalue.lightingControlId, 'power'], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'lightingPreset': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'lightingPresets', cvalue.lightingPresetId, 'active'], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'mediaPlayerState': {
                const cvalue = value;
                this.updateDisplaySourceState(dev, 'mediaPlayerState', cvalue, roomValues);
                break;
            }
            case 'mediaRecorderStartTime': {
                const cvalue = value;
                this.updateDisplaySourceState(dev, 'mediaRecorderStartTime', cvalue, roomValues);
                break;
            }
            case 'mediaRecorderState': {
                const cvalue = value;
                this.updateDisplaySourceState(dev, 'mediaRecorderState', cvalue, roomValues);
                if (cvalue.value === 'stopped') {
                    this.updateDisplaySourceState(dev, 'mediaRecorderStartTime', {
                        videoInputId: cvalue.videoInputId,
                        value: 0, // 0 means delete the start time
                    });
                }
                break;
            }
            case 'mediaStreamerState': {
                const cvalue = value;
                this.updateDisplaySourceState(dev, 'mediaStreamerState', cvalue, roomValues);
                break;
            }
            case 'mute': {
                const cvalue = value;
                if (dev === this.roomMuteDevice) {
                    Sutl.setProp(roomValues, 'states.roomMute', value);
                    Sutl.setProp(roomValues, 'states.mute', value);
                    this.room.$set(roomValues, false, false);
                    this.mutes[dev.eqId] = cvalue;
                    for (const roomMuteDev of this.muteSyncDevices) {
                        if (!roomMuteDev.eqId) {
                            throw new Error(`eqId not defined on ${roomMuteDev}`);
                        }
                        if (this.mutes[roomMuteDev.eqId] !== cvalue) {
                            roomMuteDev.set('mute', cvalue);
                        }
                    }
                }
                else {
                    let fromMuteSyncDevice = false;
                    if (((_1 = dev.caps.roomMutePowerOffRestrictions) !== null && _1 !== void 0 ? _1 : []).includes('get') &&
                        !this.powerByEqId[dev.eqId]) {
                        // If the device doesn't support mute status while powered off and is
                        // currently powered off, then ignore it.
                        break;
                    }
                    for (const roomMuteDev of this.muteSyncDevices) {
                        if (!roomMuteDev.eqId) {
                            throw new Error(`eqId not defined on ${roomMuteDev}`);
                        }
                        if (roomMuteDev.eqId === dev.eqId) {
                            fromMuteSyncDevice = true;
                            this.mutes[dev.eqId] = cvalue;
                            break;
                        }
                    }
                    const roomMuteDeviceEquipmentId = (_2 = this.roomMuteDevice) === null || _2 === void 0 ? void 0 : _2.eqId;
                    if (roomMuteDeviceEquipmentId !== undefined) {
                        const roomMuteDeviceMute = this.mutes[roomMuteDeviceEquipmentId];
                        if (roomMuteDeviceMute !== undefined) {
                            if (fromMuteSyncDevice && cvalue !== roomMuteDeviceMute) {
                                // The status broadcast by the mute device will result in setting
                                // the other muteSyncDevices.
                                (_3 = this.roomMuteDevice) === null || _3 === void 0 ? void 0 : _3.set('mute', cvalue);
                            }
                        }
                        else {
                            this.log.error(`mute device mute not defined`);
                        }
                    }
                    else {
                        this.log.error(`mute device not defined`);
                    }
                }
                break;
            }
            case 'name': {
                const cvalue = value;
                if (cvalue !== 'unknown') {
                    const eq = this.equipment.$recordAt(dev.eqId);
                    if (!eq)
                        throw new Error(`no equipment for id ${dev.eqId}`);
                    const eqStatesValues = eq.$getStates().$getValues();
                    if (!eqStatesValues.equipmentInfo)
                        eqStatesValues.equipmentInfo = {};
                    eqStatesValues.equipmentInfo.name = cvalue;
                    eq.$getStates().$set(eqStatesValues, false);
                }
                break;
            }
            case 'overflowRoomMute': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'overflowRooms', cvalue.roomId, 'mute'], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'overflowRoomVolume': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'overflowRooms', cvalue.roomId, 'volume'], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'power': {
                const cvalue = value;
                this.powerByEqId[dev.eqId] = cvalue;
                if (((_4 = this.caps.displays) === null || _4 === void 0 ? void 0 : _4[dev.eqId]) !== undefined) {
                    Sutl.setProp(roomValues, ['states', 'displays', dev.eqId, 'power'], cvalue);
                }
                if (((_5 = this.caps.usbSwitchers) === null || _5 === void 0 ? void 0 : _5[dev.eqId]) !== undefined) {
                    Sutl.setProp(roomValues, ['states', 'usbSwitchers', dev.eqId, 'power'], cvalue);
                }
                if (dev.caps.videoInputs !== undefined) {
                    // TODO: remove this check. instead, have equipment service track when it is
                    // updating versus controlling, and then don't process incoming commands while
                    // updating.
                    if (((_6 = this.videoSwitcherStates[dev.eqId]) === null || _6 === void 0 ? void 0 : _6.power) !== undefined) {
                        this.videoSwitcherStates[dev.eqId].power = cvalue;
                    }
                }
                for (const id of Object.keys((_8 = (_7 = this.room.caps) === null || _7 === void 0 ? void 0 : _7.cameraSwitchers) !== null && _8 !== void 0 ? _8 : {})) {
                    if (id === dev.eqId) {
                        Sutl.setProp(roomValues, ['states', 'cameraSwitchers', id, 'power'], cvalue);
                        break;
                    }
                }
                for (const cameraId of this.unassignedCameraIds) {
                    if (dev.eqId === this.devicesByCameraId[cameraId].eqId) {
                        Sutl.setProp(roomValues, ['states', 'cameraSwitchers', '-virtualCameraSwitcher-', 'power'], cvalue);
                        break;
                    }
                }
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'serialNumber': {
                const cvalue = value;
                if (cvalue !== 'unknown') {
                    const eq = this.equipment.$recordAt(dev.eqId);
                    if (!eq)
                        throw new Error(`no equipment for id ${dev.eqId}`);
                    const eqStatesValues = eq.$getStates().$getValues();
                    if (!eqStatesValues.equipmentInfo)
                        eqStatesValues.equipmentInfo = {};
                    eqStatesValues.equipmentInfo.serialNumber = cvalue;
                    eq.$getStates().$set(eqStatesValues, false);
                }
                break;
            }
            case 'shadePosition': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'shadeControls', cvalue.shadeControlId, 'position'], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'shadePreset': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'shadePresets', cvalue.shadePresetId, 'active'], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'shadeTilt': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'shadeControls', cvalue.shadeControlId, 'tilt'], cvalue.value);
                break;
            }
            case 'storage': {
                const eq = this.equipment.$recordAt(dev.eqId);
                if (!eq)
                    throw new Error(`no equipment for id ${dev.eqId}`);
                eq.$setStorage(value);
                break;
            }
            case 'soundReinforcementMute': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'soundReinforcement', cvalue.zoneId, 'mute'], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'soundReinforcementVolume': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'soundReinforcement', cvalue.zoneId, 'volume'], cvalue.value);
                this.room.$set(roomValues, false, false);
                break;
            }
            case 'usbHost': {
                const cvalue = value;
                Sutl.setProp(roomValues, ['states', 'usbSwitchers', dev.eqId, 'host'], cvalue);
                break;
            }
            case 'validated': {
                // If necessary, delete validated channels in the equipment config. This is for
                // drivers that sometimes write validated channels to the equipment config (DSP
                // channels) and other times the equipment states (video inputs), such as the
                // Extron video switchers and AVPro Edge video switchers. We assume no driver needs
                // to write to both.
                let deleteValidatedControlChannels = false;
                const eq = this.equipment.$recordAt(dev.eqId);
                if (!eq)
                    throw new Error(`no equipment for id: ${dev.eqId}`);
                for (const key of Object.keys((_9 = eq.config) !== null && _9 !== void 0 ? _9 : {})) {
                    if (key.endsWith('ControlChannels') &&
                        Sutl.getProp(eq, ['config', key, 'validated'])) {
                        Sutl.deleteNode(eq, ['config', key, 'validated']);
                        deleteValidatedControlChannels = true;
                    }
                }
                if (deleteValidatedControlChannels)
                    eq.$save(false, false);
                // Handle the new validated channels.
                this.driverValidated[dev.eqId] = Sutl.deepCopy(value);
                const equipmentValidated = Object.assign({}, this.driverValidated[dev.eqId]);
                let needsToUpdateCapsAndOnlineStatus = false;
                if (equipmentValidated.contactClosures) {
                    delete equipmentValidated.contactClosures; // included in display equipment
                    needsToUpdateCapsAndOnlineStatus = true;
                }
                if (equipmentValidated.shadeControls) {
                    delete equipmentValidated.shadeControls; // included in room config validated
                    needsToUpdateCapsAndOnlineStatus = true;
                }
                if (equipmentValidated.shadePresets) {
                    delete equipmentValidated.shadePresets; // included in room config validated
                    needsToUpdateCapsAndOnlineStatus = true;
                }
                const numDriverValidatedKeys = Object.keys(value).length;
                const numEquipmentValidatedKeys = Object.keys(equipmentValidated).length;
                if (needsToUpdateCapsAndOnlineStatus) {
                    this.updateCapsAndOnlineStatus(true);
                }
                else if (numEquipmentValidatedKeys > 0 || numDriverValidatedKeys === 0) {
                    (_10 = this.equipment
                        .$recordAt(dev.eqId)) === null || _10 === void 0 ? void 0 : _10.$setValidatedState(equipmentValidated, { ignoreError: true }).then(() => this.updateConnectStates());
                }
                break;
            }
            case 'videoInput':
                this.updateVideoSwitcherStates(dev.eqId, value);
                break;
            case 'videoMatrixRoute':
                for (const [outId, inId] of Object.entries(value)) {
                    this.updateVideoSwitcherStates(dev.eqId, inId, outId);
                }
                break;
            case 'volume': {
                const cvalue = value;
                if (dev === this.roomVolumeDevice) {
                    this.roomVolumeSaveVolume = cvalue;
                    this.roomVolumeSaveTimeout.set(this.onRoomVolumeSaveTimeout, 300);
                }
                if (dev.caps.hasVolume && ((_11 = this.caps.displays) === null || _11 === void 0 ? void 0 : _11[dev.eqId])) {
                    Sutl.setProp(roomValues, ['states', 'displays', dev.eqId, 'volume'], cvalue);
                    this.room.$set(roomValues, false, false);
                }
                break;
            }
            case 'volumeMute': {
                const cvalue = value;
                if (dev === this.roomVolumeDevice) {
                    this.roomVolumeSaveMute = cvalue;
                    this.roomVolumeSaveTimeout.set(this.onRoomVolumeSaveTimeout, 300);
                }
                if (dev.caps.hasVolume && ((_12 = this.caps.displays) === null || _12 === void 0 ? void 0 : _12[dev.eqId])) {
                    Sutl.setProp(roomValues, ['states', 'displays', dev.eqId, 'volumeMute'], cvalue);
                    this.room.$set(roomValues, false, false);
                }
                break;
            }
            default:
                break;
        }
    }
    onDeviceShutdown() {
        if (!this.isShutdown())
            this.shutdown();
    }
    onEquipmentAdded(equipmentId) {
        this.log.debug(`onEquipmentAdded: ${equipmentId}`);
        // We add the device after a timeout. This is because of the timeout we need for removing
        // devices (see onEquipmentRemoved). If the controller goes from offline to online
        // operation, all the equipment gets removed and then added back. Without addDevice in a
        // timeout, removeDevice can get called after we added the new device.
        this.addEquipmentTimeouts.set(() => {
            this.log.debug(`onEquipmentAdded (after timeout): ${equipmentId}`);
            if (!this.room) {
                this.log.debug(`onEquipmentAdded: ignoring since room is offline`);
            }
            else {
                this.addDevice(equipmentId);
            }
        }, 500);
    }
    onEquipmentChanged(equipmentId) {
        this.log.debug(`onEquipmentChanged: ${equipmentId}`);
        this.updateDevice(equipmentId);
    }
    onEquipmentRemoved(equipmentId) {
        this.log.debug(`onEquipmentRemoved: ${equipmentId}`);
        // When a controller is unpaired from a room, the permission side effects can propogate
        // before the data. If that happens, we may be told that a piece of equipment is removed
        // (due to the controller being unpaired) before we get notified from RoomService that we
        // are unpaired. That would cause us to remove the equipment, and if it is the last piece
        // of equipment, try to set the room's online state to false. The $save can kick off before
        // we get notified of the state change by RoomServce, and then the save will try to
        // complete, and we will get a permission denied error. To prevent this, we schedule the
        // onEquipmentRemoved behavior to happen after a short timeout and only if the room is
        // still online.
        // Fix for SNTY-1351. When removeDevice in the timeout below gets called, it will
        // rescheudule the updateCapsAndOnlineStatusTimeout.
        this.updateCapsAndOnlineStatusTimeout.clear();
        this.updateCapsAndOnlineStatusFull = false;
        this.removeEquipmentTimeouts.set(() => {
            this.log.debug(`onEquipmentRemoved (after timeout): ${equipmentId}`);
            delete this.driverValidated[equipmentId];
            if (!this.room) {
                this.log.debug(`onEquipmentRemoved: ignoring since room is offline`);
            }
            else {
                // Make sure it's still there; it is possible it got removed through another path.
                if (this.devices[equipmentId] !== undefined)
                    this.removeDevice(equipmentId);
            }
        }, 500);
    }
    onPairedControllersChanged() {
        if (!this.pairedControllers)
            throw new Error(`pairedControllers undefined`);
        if (this.pairedControllers.length === 0 && this.isShutdown() && !this.uiPreviewRan) {
            this.startup();
        }
        else if (this.pairedControllers.length > 0) {
            if (!this.isShutdown())
                this.shutdown();
            // Put this here so if we start up attempting a ui preview, but a controller is paired,
            // then we appear expired; but don't run ui preview later if the controller is unpaired.
            this.uiPreviewRan = true;
        }
        if (this.pairedControllers.length === 0 && this.isShutdown() && !this.uiPreviewRan) {
            this.startup();
        }
        else if (this.pairedControllers.length > 0 && !this.isShutdown()) {
            this.shutdown();
        }
    }
    onRoomChanged() {
        var _a, _b, _c, _d, _e, _f;
        if (!this.room)
            throw new Error(`room undefined`);
        if (this.isBrowser) {
            if (((_a = this.room.uiPreview) === null || _a === void 0 ? void 0 : _a.controllerId) !== this.uiPreviewControllerId) {
                this.log.debug(`another controller is handling the ui preview; shutting down`);
                if (!this.isShutdown())
                    this.shutdown();
            }
        }
        if (this.room.name !== ((_b = this.room.info) === null || _b === void 0 ? void 0 : _b.roomName)) {
            const roomValues = this.room.$getValues();
            Sutl.setNode(roomValues, ['info', 'roomName'], this.room.name);
            this.room.$set(roomValues, false, false);
        }
        this.parsedRoomControlCommands = parseInterpolatedRoomControlCommands((_e = (_d = (_c = this.room) === null || _c === void 0 ? void 0 : _c.settings) === null || _d === void 0 ? void 0 : _d.macros) !== null && _e !== void 0 ? _e : {});
        if (((_f = this.room.states) === null || _f === void 0 ? void 0 : _f.onlineState) !== 'offline' &&
            !Sutl.objEquals(this.room.settings, this.prevRoomSettings)) {
            this.updateCapsAndOnlineStatus();
        }
        this.prevRoomSettings = Sutl.deepCopy(this.room.settings);
    }
    onRoomConfigChanged() {
        var _a, _b, _c, _d, _e, _f;
        const equipmentIds = {};
        const roomConfig = this.roomConfig;
        if (roomConfig === undefined)
            throw new Error(`roomConfig undefined`);
        // Search room config to find all referenced equipment IDs.
        for (const shadeControl of Object.values((_a = roomConfig === null || roomConfig === void 0 ? void 0 : roomConfig.shadeControls) !== null && _a !== void 0 ? _a : {})) {
            if (shadeControl.type === 'emittedAction') {
                for (const motion of Object.values((_b = shadeControl.position) !== null && _b !== void 0 ? _b : {})) {
                    for (const action of Object.values((_c = motion === null || motion === void 0 ? void 0 : motion.actions) !== null && _c !== void 0 ? _c : {})) {
                        if (action.type === 'contactClosure') {
                            equipmentIds[action.equipmentId] = true;
                        }
                        else {
                            this.log.error(`unhandled action type`);
                        }
                    }
                }
                for (const motion of Object.values((_d = shadeControl.tilt) !== null && _d !== void 0 ? _d : {})) {
                    for (const action of Object.values((_e = motion === null || motion === void 0 ? void 0 : motion.actions) !== null && _e !== void 0 ? _e : {})) {
                        if (action.type === 'contactClosure') {
                            equipmentIds[action.equipmentId] = true;
                        }
                        else {
                            this.log.error(`unhandled action type`);
                        }
                    }
                }
            }
            else if (shadeControl.type === 'equipment') {
                equipmentIds[shadeControl.equipmentId] = true;
            }
            else {
                this.log.error(`unhandled shade control type`);
            }
        }
        for (const shadePreset of Object.values((_f = roomConfig === null || roomConfig === void 0 ? void 0 : roomConfig.shadePresets) !== null && _f !== void 0 ? _f : {})) {
            if (shadePreset.type === 'equipment') {
                equipmentIds[shadePreset.equipmentId] = true;
            }
            else {
                this.log.error(`unhandled shade preset type`);
            }
        }
        // Reconfigure any devices that are present or were removed.
        const newEquipmentIdsInRoomConfig = Object.keys(equipmentIds);
        for (const equipmentId of this.equipmentIdsInRoomConfig) {
            equipmentIds[equipmentId] = true;
        }
        for (const equipmentId of Object.keys(equipmentIds)) {
            // ignore undefined device, may not be online yet
            this.updateDevice(equipmentId, true);
        }
        this.equipmentIdsInRoomConfig = newEquipmentIdsInRoomConfig;
        // If equipment configs changed, then updateEquipment will run updateCapsAndOnlineStatus.
        // We include an extra call here in case only non-driver changes were made (such as
        // changing an icon).
        this.updateCapsAndOnlineStatus(true);
    }
    onRoomControlCommandAdded(commandId) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53, _54, _55, _56, _57, _58, _59, _60, _61, _62, _63, _64, _65, _66, _67, _68, _69, _70, _71, _72, _73, _74, _75, _76, _77, _78, _79, _80, _81, _82, _83, _84, _85, _86, _87, _88, _89, _90, _91;
        if (!this.roomControlCommands)
            throw new Error(`roomControlCommands undefined`);
        const cmd = this.roomControlCommands.$recordAt(commandId);
        this.roomControlCommands.$remove(commandId);
        this.log.debug(`room control command: ${JSON.stringify(cmd)}`);
        if (!cmd) {
            this.log.error(`onRoomControlCommandAdded: no command for commandId ${commandId}`);
            return;
        }
        if (!this.isOnline &&
            cmd.type !== 'startInteractiveAuthentication' &&
            cmd.type !== 'discoverEquipment') {
            this.log.debug(`room offline, ignoring command`);
            return;
        }
        switch (cmd.type) {
            case 'answer':
            case 'hangup':
            case 'reject': {
                if (!this.room)
                    throw new Error(`room undefined`);
                const recentCalls = this.room.$getCalls(defaults.inara.numRecentCalls);
                const callIds = cmd.callId === '*' || cmd.callId === 'all'
                    ? Object.keys(this.devicesByCallId)
                    : [cmd.callId];
                for (const callId of callIds) {
                    const call = recentCalls.$recordAt(callId);
                    if (!call)
                        continue;
                    if ((cmd.type === 'answer' || cmd.type === 'reject') &&
                        (call.callState !== 'ringing' || call.direction !== 'incoming')) {
                        this.log.info(`ignoring ${cmd.type} since call "${callId}" is ${call.callState}`);
                        continue;
                    }
                    if (cmd.type === 'hangup' && call.callState === 'disconnected') {
                        this.log.info(`ignoring ${cmd.type} since call "${callId}" is ${call.callState}`);
                        continue;
                    }
                    const dev = this.devicesByCallId[callId];
                    if (!dev) {
                        this.log.warning(`no device for callId (${callId})`);
                        continue;
                    }
                    const value = 'endMeeting' in cmd ? { callId, endMeeting: cmd.endMeeting } : callId;
                    if (cmd.type === 'answer')
                        this.setupDisplaysForCall(dev);
                    dev.set(cmd.type, value);
                    // Answer, hangup, or reject call on synchronized devices if necessary.
                    for (const syncDev of Object.values(this.synchronizedCallDevices[call.callType])) {
                        if (((_b = (_a = syncDev.config.synchronizedCalls) === null || _a === void 0 ? void 0 : _a[call.callType]) === null || _b === void 0 ? void 0 : _b.parentEquipmentId) !==
                            dev.eqId) {
                            continue;
                        }
                        for (const [syncCallId, callDev] of Object.entries(this.devicesByCallId)) {
                            if (callDev.eqId !== syncDev.eqId)
                                continue;
                            if (((_c = this.synchronizedCalls[syncCallId]) === null || _c === void 0 ? void 0 : _c.callType) !== call.callType) {
                                continue;
                            }
                            const syncValue = typeof value === 'string' ? syncCallId : Object.assign(Object.assign({}, value), { syncCallId });
                            syncDev.set(cmd.type, syncValue);
                        }
                    }
                }
                break;
            }
            case 'assistiveListeningMute':
            case 'assistiveListeningVolume':
                if (this.assistiveListeningDevice) {
                    this.assistiveListeningDevice.set(cmd.type, cmd.value);
                }
                break;
            case 'assistiveListeningMuteToggle':
                if (this.assistiveListeningDevice) {
                    const value = (_g = (_f = (_e = (_d = this.room) === null || _d === void 0 ? void 0 : _d.states) === null || _e === void 0 ? void 0 : _e.assistiveListening) === null || _f === void 0 ? void 0 : _f.mute) !== null && _g !== void 0 ? _g : false;
                    this.assistiveListeningDevice.set('assistiveListeningMute', !value);
                }
                break;
            case 'assistiveListeningVolumeInc':
                if (this.assistiveListeningDevice) {
                    const value = (_l = (_k = (_j = (_h = this.room) === null || _h === void 0 ? void 0 : _h.states) === null || _j === void 0 ? void 0 : _j.assistiveListening) === null || _k === void 0 ? void 0 : _k.volume) !== null && _l !== void 0 ? _l : 0;
                    this.assistiveListeningDevice.set('assistiveListeningVolume', value + cmd.value);
                }
                break;
            case 'audioInputMute': {
                const dev = this.devicesByAudioInputSourceId[cmd.srcId];
                if (!dev) {
                    this.log.warning(`invalid audio input source id: ${cmd.srcId}`);
                }
                else {
                    dev.set(cmd.type, { srcId: cmd.srcId, value: cmd.value });
                }
                break;
            }
            case 'audioInputMuteToggle': {
                const dev = this.devicesByAudioInputSourceId[cmd.srcId];
                if (!dev) {
                    this.log.warning(`invalid audio input source id: ${cmd.srcId}`);
                }
                else {
                    const value = (_r = (_q = (_p = (_o = (_m = this.room) === null || _m === void 0 ? void 0 : _m.states) === null || _o === void 0 ? void 0 : _o.audioInputSources) === null || _p === void 0 ? void 0 : _p[cmd.srcId]) === null || _q === void 0 ? void 0 : _q.mute) !== null && _r !== void 0 ? _r : false;
                    dev.set('audioInputMute', { srcId: cmd.srcId, value: !value });
                }
                break;
            }
            case 'audioInputVolume': {
                const dev = this.devicesByAudioInputSourceId[cmd.srcId];
                if (!dev) {
                    this.log.warning(`invalid audio input source id: ${cmd.srcId}`);
                }
                else {
                    dev.set(cmd.type, { srcId: cmd.srcId, value: cmd.value });
                }
                break;
            }
            case 'audioInputVolumeInc': {
                const dev = this.devicesByAudioInputSourceId[cmd.srcId];
                if (!dev) {
                    this.log.warning(`invalid audio input source id: ${cmd.srcId}`);
                }
                else {
                    const value = (_w = (_v = (_u = (_t = (_s = this.room) === null || _s === void 0 ? void 0 : _s.states) === null || _t === void 0 ? void 0 : _t.audioInputSources) === null || _u === void 0 ? void 0 : _u[cmd.srcId]) === null || _v === void 0 ? void 0 : _v.volume) !== null && _w !== void 0 ? _w : 0;
                    dev.set('audioInputVolume', { srcId: cmd.srcId, value: value + cmd.value });
                }
                break;
            }
            case 'call': {
                const callDevices = this.devicesByCallType[cmd.callType];
                let callPlaced = false;
                if (!callDevices) {
                    this.log.warning(`unsupported call type: ${cmd.callType}`);
                    break;
                }
                const siteSettings = (_z = this.kayleeService.getSite((_y = (_x = this.room) === null || _x === void 0 ? void 0 : _x.siteId) !== null && _y !== void 0 ? _y : '')) === null || _z === void 0 ? void 0 : _z.settings;
                const synergySkyEnabled = siteSettings === null || siteSettings === void 0 ? void 0 : siteSettings.synergySkyEnabled;
                const synergySkyCallTypes = Object.keys((_0 = siteSettings === null || siteSettings === void 0 ? void 0 : siteSettings.synergySkyCallTypes) !== null && _0 !== void 0 ? _0 : {});
                const synergySkyManualProvisioningEnabled = siteSettings === null || siteSettings === void 0 ? void 0 : siteSettings.synergySkyManualProvisioningEnabled;
                const synergySkyDomain = (_1 = siteSettings === null || siteSettings === void 0 ? void 0 : siteSettings.synergySkyDomain) !== null && _1 !== void 0 ? _1 : defaults.mal.synergySkyDomain;
                let synergySkyWasProvisioned = false;
                for (const dev of callDevices) {
                    let synergySkyTranslated = false;
                    let effectiveCallType = cmd.callType;
                    if (synergySkyEnabled && synergySkyCallTypes.includes(cmd.callType)) {
                        effectiveCallType = 'video';
                        synergySkyTranslated = true;
                    }
                    const maxCalls = (_3 = (_2 = dev.caps.maxCalls) === null || _2 === void 0 ? void 0 : _2[effectiveCallType]) !== null && _3 !== void 0 ? _3 : 0;
                    const disabledCalls = (_5 = (_4 = dev.caps.disabledCalls) === null || _4 === void 0 ? void 0 : _4[effectiveCallType]) !== null && _5 !== void 0 ? _5 : 0;
                    const activeCalls = dev.getNumActiveCalls(effectiveCallType);
                    const availableCalls = maxCalls - disabledCalls - activeCalls;
                    if (availableCalls > 0) {
                        const excludeProps = ['type', 'time', 'callType', 'dialString'];
                        const callId = Sutl.generateFirebaseId();
                        for (const [prop, value] of Object.entries(cmd)) {
                            if (prop.charAt(0) === '$')
                                continue;
                            if (excludeProps.includes(prop))
                                continue;
                            Sutl.setProp(this.extraCallInfo, [callId, prop], Sutl.deepCopy(value));
                        }
                        const callInfo = {
                            callId: callId,
                            callType: effectiveCallType,
                            // none for Zoom PMI calls
                            dialString: ((_6 = cmd.dialString) !== null && _6 !== void 0 ? _6 : 'none').replace(/\s+/g, ''),
                        };
                        let readyToCallPromise = Promise.resolve(undefined);
                        if (synergySkyManualProvisioningEnabled && synergySkyTranslated) {
                            const tenantId = siteSettings === null || siteSettings === void 0 ? void 0 : siteSettings.synergySkyTenantId;
                            const clientSecret = siteSettings === null || siteSettings === void 0 ? void 0 : siteSettings.synergySkyClientSecret;
                            if (tenantId === undefined) {
                                this.log.error('synergySkyTenantId not defined in site settings');
                            }
                            if (clientSecret === undefined) {
                                this.log.error('synergySkyClientSecret not defined in site settings');
                            }
                            if (tenantId !== undefined && clientSecret !== undefined) {
                                let dialString = callInfo.dialString;
                                if (cmd.calendarEventId !== undefined &&
                                    ((_7 = cmd.meetingCodes) === null || _7 === void 0 ? void 0 : _7[0]) !== undefined &&
                                    !callInfo.dialString.includes('##')) {
                                    dialString = `${dialString}##${cmd.meetingCodes[0]}`;
                                }
                                readyToCallPromise = manuallyProvisionMeeting((_8 = siteSettings === null || siteSettings === void 0 ? void 0 : siteSettings.synergySkyApiDomain) !== null && _8 !== void 0 ? _8 : defaults.mal.synergySkyApiDomain, tenantId, clientSecret, cmd.callType, dialString, cmd.displayDialString, (_9 = cmd.meetingCodes) === null || _9 === void 0 ? void 0 : _9['a0']).then(dialString => {
                                    if (dialString !== undefined) {
                                        callInfo.dialString = dialString;
                                        synergySkyWasProvisioned = true;
                                    }
                                    return dialString;
                                });
                            }
                        }
                        if (synergySkyTranslated) {
                            readyToCallPromise = readyToCallPromise.then(dialString => {
                                if (!synergySkyWasProvisioned) {
                                    callInfo.dialString = translateSynergySkyCall(synergySkyDomain, cmd.callType, dialString !== null && dialString !== void 0 ? dialString : callInfo.dialString, cmd.displayDialString);
                                    return callInfo.dialString;
                                }
                                return undefined;
                            });
                        }
                        // Add synergySky call extended type if this is a Synergy Sky call. There
                        // are two ways this can happen. First, through dial string translation or
                        // manual provisioning. Second, if the dial string came from a device's
                        // calendar already in the Synergy Sky format. Adding this call extended
                        // type makes the Synergy Sky meeting controls show up on the call card.
                        if (synergySkyTranslated ||
                            (synergySkyEnabled &&
                                dialStringIsInSynergySkyFormat(synergySkyDomain, callInfo.dialString))) {
                            this.pendingCallExtendedTypes[(_10 = dev.eqId) !== null && _10 !== void 0 ? _10 : ''] = {
                                callExtendedType: {
                                    type: 'synergySky',
                                    callType: cmd.callType,
                                },
                                timestamp: Date.now(),
                            };
                        }
                        if (cmd.invite !== undefined) {
                            callInfo.invite = Sutl.deepCopy(Sutl.objToArray(cmd.invite, 'a'));
                        }
                        if (cmd.durationMinutes !== undefined) {
                            callInfo.durationMinutes = cmd.durationMinutes;
                        }
                        if (cmd.calendarEventId !== undefined) {
                            callInfo.calendarEventId = cmd.calendarEventId;
                        }
                        if (cmd.meetingCodes !== undefined) {
                            callInfo.meetingCodes = Sutl.objToArray(cmd.meetingCodes, 'a');
                        }
                        if (cmd.alternateMeetingCodes !== undefined) {
                            callInfo.alternateMeetingCodes = Sutl.objToArray(cmd.alternateMeetingCodes, 'a');
                        }
                        if (!this.room)
                            throw new Error(`room undefined`);
                        if (cmd.callType === 'pstn' &&
                            (siteSettings === null || siteSettings === void 0 ? void 0 : siteSettings.telephonyDialingPrefix) !== undefined &&
                            ((_12 = (_11 = cmd.dialString) === null || _11 === void 0 ? void 0 : _11.length) !== null && _12 !== void 0 ? _12 : 0) >= 7) {
                            Sutl.setProp(this.extraCallInfo, [callId, 'dialPrefix'], siteSettings.telephonyDialingPrefix);
                            Sutl.setProp(this.extraCallInfo, [callId, 'dialStringOverride'], cmd.dialString);
                            callInfo.dialString = siteSettings.telephonyDialingPrefix;
                        }
                        readyToCallPromise.then(dialString => {
                            this.setupDisplaysForCall(dev);
                            dev.set(cmd.type, callInfo);
                        });
                        callPlaced = true;
                        break;
                    }
                }
                if (!callPlaced) {
                    const error = `can't place ${cmd.callType} call to "${cmd.dialString}"`;
                    const reason = `, no call resources available`;
                    this.log.warning(error + reason);
                }
                break;
            }
            case 'callLayout': {
                const dev = this.devicesByCallId[cmd.callId];
                if (!dev) {
                    this.log.warning(`no device for callId (${cmd.callId})`);
                    break;
                }
                dev.set('callLayout', cmd);
                break;
            }
            case 'cameraMove': {
                const cameraInfo = this.getActualCamera(cmd.cameraId, 'move');
                if (cameraInfo) {
                    const value = Object.assign(Object.assign(Object.assign(Object.assign({ cameraId: cameraInfo.cameraId }, ('action' in cmd && { action: cmd.action })), ('direction' in cmd && { direction: cmd.direction })), ('holdRepeatMs' in cmd && { holdRepeatMs: cmd.holdRepeatMs })), ('value' in cmd && { value: cmd.value }));
                    cameraInfo.dev.set(cmd.type, value);
                }
                break;
            }
            case 'cameraPosition': {
                const dev = this.devices[cmd.equipmentId];
                if (!dev) {
                    this.log.error(`unknown equipmentId (${cmd.equipmentId}) in ${cmd.type}  command`);
                    break;
                }
                dev.set(cmd.type, { cameraId: cmd.cameraId, requestId: cmd.requestId });
                break;
            }
            case 'cameraPreset':
                (_13 = this.devicesByCameraPresetId[cmd.presetId]) === null || _13 === void 0 ? void 0 : _13.set(cmd.type, cmd.presetId);
                break;
            case 'cameraSelectByName':
                this.log.warning(`cameraSelectByName room control command is deprecated, use cameraSelected instead`);
            case 'cameraSelected': {
                let cameraId;
                if (cmd.type === 'cameraSelectByName') {
                    cameraId = this.findCameraByName(cmd.cameraSwitcherId, cmd.cameraName);
                    if (cameraId === undefined) {
                        this.log.warning(`no camera named "${cmd.cameraName}" on camera switcher "${cmd.cameraSwitcherId}"`);
                        break;
                    }
                }
                else {
                    cameraId = cmd.cameraId;
                }
                const dev = this.devicesByCameraId[cameraId];
                if (!dev) {
                    this.log.warning(`invalid cameraId ${cameraId}`);
                }
                else {
                    dev.set('cameraSelected', cameraId);
                }
                break;
            }
            case 'cameraTracking': {
                const cameraInfo = this.getActualCamera(cmd.cameraId, 'tracking');
                if (cameraInfo) {
                    const value = {
                        cameraId: cameraInfo.cameraId,
                        value: cmd.value,
                    };
                    cameraInfo.dev.set(cmd.type, value);
                }
                break;
            }
            case 'consentToRecording': {
                const dev = this.devicesByCallId[cmd.callId];
                if (!dev) {
                    this.log.warning(`no device for callId (${cmd.callId})`);
                    break;
                }
                dev.set(cmd.type, cmd);
                // Also consent to recording for any synchronized calls who are also in the
                // recordingConsentRequired callExtendedState. If they aren't, we will grant their
                // consent in the callStatus handler, so we augment their call status with the
                // parentConsentedToRecording property.
                for (const [syncCallId, syncCallStatus] of Object.entries(this.synchronizedCalls)) {
                    if (syncCallStatus.parentCallId !== cmd.callId)
                        continue;
                    if (cmd.value) {
                        syncCallStatus.parentConsentedToRecording = true;
                    }
                    else {
                        delete syncCallStatus.parentConsentedToRecording;
                    }
                    if (syncCallStatus.callExtendedState === 'recordingConsentRequired') {
                        const syncDev = this.devicesByCallId[syncCallId];
                        syncDev === null || syncDev === void 0 ? void 0 : syncDev.set('consentToRecording', {
                            callId: syncCallId,
                            value: cmd.value,
                        });
                    }
                }
                break;
            }
            case 'dial': {
                const dev = this.devicesByCallId[cmd.callId];
                if (!dev) {
                    this.log.warning(`no device for callId (${cmd.callId})`);
                    break;
                }
                dev.set(cmd.type, { callId: cmd.callId, dialString: cmd.dialString });
                // Dial call on synchronized devices if necessary.
                if (!this.room)
                    throw new Error(`room undefined`);
                const recentCalls = this.room.$getCalls(defaults.inara.numRecentCalls);
                const call = recentCalls.$recordAt(cmd.callId);
                if (!call)
                    break;
                for (const syncDev of Object.values(this.synchronizedCallDevices[call.callType])) {
                    if (((_15 = (_14 = syncDev.config.synchronizedCalls) === null || _14 === void 0 ? void 0 : _14[call.callType]) === null || _15 === void 0 ? void 0 : _15.parentEquipmentId) !==
                        dev.eqId) {
                        continue;
                    }
                    for (const [callId, callDev] of Object.entries(this.devicesByCallId)) {
                        if (callDev.eqId !== syncDev.eqId)
                            continue;
                        if (((_16 = this.synchronizedCalls[callId]) === null || _16 === void 0 ? void 0 : _16.callType) !== call.callType)
                            continue;
                        syncDev.set(cmd.type, { callId, dialString: cmd.dialString });
                    }
                }
                break;
            }
            case 'discoverEquipment': {
                const driverInfo = getDriver(cmd.driverId);
                if (driverInfo === undefined) {
                    this.log.warning(`no driver found for equipment`);
                    return;
                }
                if (driverInfo.Driver.discoverEquipment === undefined) {
                    this.log.warning(`equipment discovery not supported for this device`);
                    return;
                }
                const time = Date.now();
                driverInfo.Driver.discoverEquipment().then((equipment) => __awaiter(this, void 0, void 0, function* () {
                    if (!this.room)
                        return;
                    const discoveredEquipment = this.room.$getDiscoveredEquipment();
                    yield discoveredEquipment.$loaded();
                    const values = discoveredEquipment.$getValues();
                    Sutl.setNode(values, ['responses', cmd.driverId], { time, equipment });
                    discoveredEquipment.$set(values);
                }));
                break;
            }
            case 'displayBlank':
            case 'displayFreeze':
                (_17 = this.devices[cmd.displayId]) === null || _17 === void 0 ? void 0 : _17.set(cmd.type, cmd.value);
                break;
            case 'displaySource':
                if (!((_18 = this.caps.displays) === null || _18 === void 0 ? void 0 : _18[cmd.displayId])) {
                    this.log.warning(`unknown display id (${cmd.displayId})`);
                }
                if (this.devices[cmd.displayId] === undefined) {
                    this.log.warning(`unknown equipment id (${cmd.displayId}`);
                    break;
                }
                this.setDisplaySource(cmd.displayId, cmd.value);
                break;
            case 'displayVolume': {
                const dev = this.devices[cmd.displayId];
                if (!dev) {
                    this.log.error(`unknown displayId (${cmd.displayId}) in ${cmd.type} command`);
                    break;
                }
                if (dev.caps.hasPowerControl && !this.powerByEqId[cmd.displayId]) {
                    dev.set('power', true);
                }
                dev.set('volume', cmd.value);
                break;
            }
            case 'displayVolumeInc': {
                const dev = this.devices[cmd.displayId];
                if (!dev) {
                    this.log.error(`unknown displayId (${cmd.displayId}) in ${cmd.type} command`);
                    break;
                }
                if (dev.caps.hasPowerControl && !this.powerByEqId[cmd.displayId]) {
                    dev.set('power', true);
                }
                if (dev.caps.isVolumeIncDec) {
                    dev.set('volume', cmd.value);
                }
                else {
                    // FIXME: we convert 0-100 to 0-25. this should be handled in the framework to
                    // preserve resolution.
                    const value = (_23 = (_21 = (_20 = (_19 = this.room) === null || _19 === void 0 ? void 0 : _19.states) === null || _20 === void 0 ? void 0 : _20.displays) === null || _21 === void 0 ? void 0 : _21[(_22 = dev.eqId) !== null && _22 !== void 0 ? _22 : ''].volume) !== null && _23 !== void 0 ? _23 : 0;
                    dev.set('volume', value + cmd.value);
                }
                break;
            }
            case 'displayVolumeMute': {
                const dev = this.devices[cmd.displayId];
                if (!dev) {
                    this.log.error(`unknown displayId (${cmd.displayId}) in ${cmd.type} command`);
                    break;
                }
                if (dev.caps.hasPowerControl && !this.powerByEqId[cmd.displayId]) {
                    dev.set('power', true);
                }
                dev.set('volumeMute', cmd.value);
                break;
            }
            case 'displayVolumeMuteToggle': {
                const dev = this.devices[cmd.displayId];
                if (!dev) {
                    this.log.error(`unknown displayId (${cmd.displayId}) in ${cmd.type} command`);
                    break;
                }
                if (dev.caps.hasPowerControl && !this.powerByEqId[cmd.displayId]) {
                    dev.set('power', true);
                }
                const value = (_29 = (_28 = (_26 = (_25 = (_24 = this.room) === null || _24 === void 0 ? void 0 : _24.states) === null || _25 === void 0 ? void 0 : _25.displays) === null || _26 === void 0 ? void 0 : _26[(_27 = dev.eqId) !== null && _27 !== void 0 ? _27 : '']) === null || _28 === void 0 ? void 0 : _28.volumeMute) !== null && _29 !== void 0 ? _29 : false;
                dev.set('volumeMute', !value);
                break;
            }
            case 'equipmentSetting': {
                const dev = this.devices[cmd.equipmentId];
                if (!dev) {
                    this.log.warning(`unknown equipmentId (${cmd.equipmentId}) in ${cmd.type}  command`);
                    break;
                }
                dev.set(cmd.type, { name: cmd.name, value: cmd.value });
                break;
            }
            case 'hideDisplay':
                this.doDisplayMotion(cmd.displayId, cmd.displayMotionId, 'hide', cmd.manual);
                break;
            case 'inviteToCall': {
                const dev = this.devicesByCallId[cmd.callId];
                if (!dev) {
                    this.log.warning(`no device for callId (${cmd.callId})`);
                    break;
                }
                dev.set(cmd.type, cmd);
                break;
            }
            case 'lightingLevel': {
                const dev = this.devicesByLightingControlId[cmd.lightingControlId];
                if (!dev) {
                    this.log.error(`invalid lighting control id: ${cmd.lightingControlId}`);
                }
                else {
                    dev.set('lightingLevel', {
                        lightingControlId: cmd.lightingControlId,
                        value: cmd.value,
                    });
                }
                break;
            }
            case 'lightingLevelInc': {
                const dev = this.devicesByLightingControlId[cmd.lightingControlId];
                if (!dev) {
                    this.log.error(`invalid lighting control id: ${cmd.lightingControlId}`);
                }
                else {
                    const value = (_34 = (_33 = (_32 = (_31 = (_30 = this.room) === null || _30 === void 0 ? void 0 : _30.states) === null || _31 === void 0 ? void 0 : _31.lightingControls) === null || _32 === void 0 ? void 0 : _32[cmd.lightingControlId]) === null || _33 === void 0 ? void 0 : _33.level) !== null && _34 !== void 0 ? _34 : 0;
                    dev.set('lightingLevel', {
                        lightingControlId: cmd.lightingControlId,
                        value: value + cmd.value,
                    });
                }
                break;
            }
            case 'lightingPower': {
                const dev = this.devicesByLightingControlId[cmd.lightingControlId];
                if (!dev) {
                    this.log.error(`invalid lighting control id: ${cmd.lightingControlId}`);
                }
                else {
                    dev.set('lightingPower', {
                        lightingControlId: cmd.lightingControlId,
                        value: cmd.value,
                    });
                }
                break;
            }
            case 'lightingPowerToggle': {
                const dev = this.devicesByLightingControlId[cmd.lightingControlId];
                if (!dev) {
                    this.log.error(`invalid lighting control id: ${cmd.lightingControlId}`);
                }
                else {
                    const value = (_39 = (_38 = (_37 = (_36 = (_35 = this.room) === null || _35 === void 0 ? void 0 : _35.states) === null || _36 === void 0 ? void 0 : _36.lightingControls) === null || _37 === void 0 ? void 0 : _37[cmd.lightingControlId]) === null || _38 === void 0 ? void 0 : _38.power) !== null && _39 !== void 0 ? _39 : false;
                    dev.set('lightingPower', {
                        lightingControlId: cmd.lightingControlId,
                        value: !value,
                    });
                }
                break;
            }
            case 'lightingPreset': {
                const dev = this.devicesByLightingPresetId[cmd.lightingPresetId];
                if (!dev) {
                    this.log.error(`invalid lighting preset id: ${cmd.lightingPresetId}`);
                }
                else {
                    dev.set('lightingPreset', {
                        lightingPresetId: cmd.lightingPresetId,
                        value: true,
                    });
                }
                break;
            }
            case 'mediaOperation': {
                const dev = this.devices[cmd.equipmentId];
                if (!dev) {
                    this.log.error(`unknown equipmentId (${cmd.equipmentId}) in ${cmd.type} command`);
                    break;
                }
                if (dev.caps.hasPowerControl &&
                    !dev.caps.isPowerToggle &&
                    !this.powerByEqId[cmd.equipmentId]) {
                    dev.set('power', true);
                }
                dev.set(cmd.type, cmd);
                break;
            }
            case 'muteParticipant': {
                const dev = this.devicesByCallId[cmd.callId];
                if (!dev) {
                    this.log.warning(`no device for callId (${cmd.callId})`);
                    break;
                }
                dev.set('muteParticipant', cmd);
                break;
            }
            case 'muteParticipantVideo': {
                const dev = this.devicesByCallId[cmd.callId];
                if (!dev) {
                    this.log.warning(`no device for callId (${cmd.callId})`);
                    break;
                }
                dev.set('muteParticipantVideo', cmd);
                break;
            }
            case 'overflowRoomMute': {
                const dev = this.devicesByOverflowRoomId[cmd.roomId];
                if (!dev) {
                    this.log.warning(`invalid overflow roomId: ${cmd.roomId}`);
                }
                else {
                    dev.set(cmd.type, { roomId: cmd.roomId, value: cmd.value });
                }
                break;
            }
            case 'overflowRoomMuteToggle': {
                const dev = this.devicesByOverflowRoomId[cmd.roomId];
                if (!dev) {
                    this.log.warning(`invalid overflow roomId: ${cmd.roomId}`);
                }
                else {
                    const value = (_44 = (_43 = (_42 = (_41 = (_40 = this.room) === null || _40 === void 0 ? void 0 : _40.states) === null || _41 === void 0 ? void 0 : _41.overflowRooms) === null || _42 === void 0 ? void 0 : _42[cmd.roomId]) === null || _43 === void 0 ? void 0 : _43.mute) !== null && _44 !== void 0 ? _44 : false;
                    dev.set('overflowRoomMute', { roomId: cmd.roomId, value: !value });
                }
                break;
            }
            case 'overflowRoomVolume': {
                const dev = this.devicesByOverflowRoomId[cmd.roomId];
                if (!dev) {
                    this.log.warning(`invalid overflow roomId: ${cmd.roomId}`);
                }
                else {
                    dev.set(cmd.type, { roomId: cmd.roomId, value: cmd.value });
                }
                break;
            }
            case 'overflowRoomVolumeInc': {
                const dev = this.devicesByOverflowRoomId[cmd.roomId];
                if (!dev) {
                    this.log.warning(`invalid overflow roomId: ${cmd.roomId}`);
                }
                else {
                    const value = (_49 = (_48 = (_47 = (_46 = (_45 = this.room) === null || _45 === void 0 ? void 0 : _45.states) === null || _46 === void 0 ? void 0 : _46.overflowRooms) === null || _47 === void 0 ? void 0 : _47[cmd.roomId]) === null || _48 === void 0 ? void 0 : _48.volume) !== null && _49 !== void 0 ? _49 : 0;
                    dev.set('overflowRoomVolume', { roomId: cmd.roomId, value: value + cmd.value });
                }
                break;
            }
            case 'power': {
                if (cmd.eqId === '--virtualCameraSwitcher--') {
                    this.unassignedCameraIds
                        .map(cameraId => this.devicesByCameraId[cameraId])
                        .filter(dev => dev === null || dev === void 0 ? void 0 : dev.caps.hasPowerControl)
                        .forEach(dev => dev.set(cmd.type, cmd.value));
                }
                else if (cmd.value && ((_50 = this.caps.displays) === null || _50 === void 0 ? void 0 : _50[cmd.eqId]) !== undefined) {
                    const sourceId = (_54 = (_53 = (_52 = (_51 = this.room) === null || _51 === void 0 ? void 0 : _51.states) === null || _52 === void 0 ? void 0 : _52.displays) === null || _53 === void 0 ? void 0 : _53[cmd.eqId]) === null || _54 === void 0 ? void 0 : _54.videoInput;
                    const source = (_55 = this.sourceMenus[cmd.eqId]) === null || _55 === void 0 ? void 0 : _55[sourceId !== null && sourceId !== void 0 ? sourceId : ''];
                    const outId = this.getVideoOutputIds(cmd.eqId)[0];
                    const path = (_56 = (source !== undefined && outId !== undefined
                        ? this.getPathToDisplaySource(source, cmd.eqId, outId)
                        : undefined)) !== null && _56 !== void 0 ? _56 : [];
                    if (path.length > 0) {
                        const dev = this.devices[cmd.eqId];
                        if (!dev) {
                            this.log.warning(`unknown equipmentId (${cmd.eqId}) in ${cmd.type} command`);
                            break;
                        }
                        if (dev.caps.isPowerStateless)
                            dev.set(cmd.type, cmd.value);
                        this.setDisplayPath(path, true, true);
                    }
                    else {
                        const dev = this.devices[cmd.eqId];
                        if (!dev) {
                            this.log.warning(`unknown equipmentId (${cmd.eqId}) in ${cmd.type} commnd`);
                            break;
                        }
                        dev.set(cmd.type, cmd.value);
                    }
                }
                else {
                    const dev = this.devices[cmd.eqId];
                    if (!dev) {
                        this.log.warning(`unknown equipmentId (${cmd.eqId}) in ${cmd.type} commnd`);
                        break;
                    }
                    dev.set(cmd.type, cmd.value);
                }
                break;
            }
            case 'powerToggle': {
                if (cmd.eqId === '--virtualCameraSwitcher--') {
                    this.unassignedCameraIds
                        .map(cameraId => this.devicesByCameraId[cameraId])
                        .filter(dev => dev === null || dev === void 0 ? void 0 : dev.caps.hasPowerControl)
                        .forEach(dev => {
                        var _a, _b;
                        const value = dev.caps.isPowerToggle
                            ? false
                            : (_b = this.powerByEqId[(_a = dev.eqId) !== null && _a !== void 0 ? _a : '']) !== null && _b !== void 0 ? _b : false;
                        dev.set('power', !value);
                    });
                }
                else {
                    const dev = this.devices[cmd.eqId];
                    if (!dev) {
                        this.log.warning(`unknown equipmentId (${cmd.eqId}) in ${cmd.type} commnd`);
                        break;
                    }
                    const value = dev.caps.isPowerToggle
                        ? false
                        : (_58 = this.powerByEqId[(_57 = dev.eqId) !== null && _57 !== void 0 ? _57 : '']) !== null && _58 !== void 0 ? _58 : false;
                    dev.set('power', !value);
                }
                break;
            }
            case 'reboot': {
                const dev = this.devices[cmd.eqId];
                if (!dev) {
                    this.log.warning(`unknown equipmentId (${cmd.eqId}) in ${cmd.type} command`);
                    break;
                }
                if (!(cmd.eqId in this.onlineDevices)) {
                    this.log.warning(`equipment (${cmd.eqId}) offline`);
                    break;
                }
                dev.set('reboot');
                break;
            }
            case 'receiveGain': {
                const dev = this.devicesByCallId[cmd.callId];
                if (!dev) {
                    this.log.warning(`no device for callId (${cmd.callId})`);
                    break;
                }
                dev.set(cmd.type, { callId: cmd.callId, value: cmd.value });
                break;
            }
            case 'mute':
                this.log.warning(`mute room control command is deprecated, use roomMute instead`);
            case 'roomMute':
                if (this.roomMuteDevice) {
                    this.roomMuteDevice.set('mute', cmd.value);
                }
                break;
            case 'muteToggle':
                this.log.warning(`muteToggle room control command is deprecated, use roomMuteToggle instead`);
            case 'roomMuteToggle':
                if (this.roomMuteDevice) {
                    const value = this.roomMuteDevice.caps.isRoomMuteToggle
                        ? false
                        : (_61 = (_60 = (_59 = this.room) === null || _59 === void 0 ? void 0 : _59.states) === null || _60 === void 0 ? void 0 : _60.roomMute) !== null && _61 !== void 0 ? _61 : false;
                    this.roomMuteDevice.set('mute', !value);
                }
                break;
            case 'roomOff':
                this.powerOffRoom();
                break;
            case 'volume':
                this.log.warning(`volume room control command is deprecated, use roomVolume instead`);
            case 'roomVolume':
                if (this.roomVolumeDevice) {
                    if (!this.equipment)
                        throw new Error(`equipment undefined`);
                    const equipmentId = (_62 = this.roomVolumeDevice.eqId) !== null && _62 !== void 0 ? _62 : '';
                    if (this.roomVolumeDevice.caps.hasPowerControl &&
                        !this.roomVolumeDevice.caps.isPowerToggle &&
                        !this.powerByEqId[equipmentId]) {
                        this.roomVolumeDevice.set('power', true);
                    }
                    // roomVolume range is 0-50 dB, volume (deprecated) range is 0-25 (arbitrary units)
                    const volume = cmd.type === 'roomVolume' ? cmd.value : 2 * cmd.value;
                    this.roomVolumeDevice.set('volume', volume);
                }
                break;
            case 'volumeInc':
                this.log.warning(`volumeInc room control command is deprecated, use roomVolumeInc instead`);
            case 'roomVolumeInc':
                if (this.roomVolumeDevice) {
                    if (!this.equipment)
                        throw new Error(`equipment undefined`);
                    const equipmentId = (_63 = this.roomVolumeDevice.eqId) !== null && _63 !== void 0 ? _63 : '';
                    if (this.roomVolumeDevice.caps.hasPowerControl &&
                        !this.roomVolumeDevice.caps.isPowerToggle &&
                        !this.powerByEqId[equipmentId]) {
                        this.roomVolumeDevice.set('power', true);
                    }
                    const value = cmd.type === 'roomVolumeInc' ? cmd.value : 2 * cmd.value;
                    if (this.roomVolumeDevice.caps.isRoomVolumeIncDec) {
                        this.roomVolumeDevice.set('volume', value);
                    }
                    else {
                        const volume = (_66 = (_65 = (_64 = this.room) === null || _64 === void 0 ? void 0 : _64.states) === null || _65 === void 0 ? void 0 : _65.roomVolume) !== null && _66 !== void 0 ? _66 : 0;
                        this.roomVolumeDevice.set('volume', volume + value);
                    }
                }
                break;
            case 'runRoomMacro':
                this.runRoomMacro(cmd.macroId);
                break;
            case 'selfView': {
                const dev = this.devices[cmd.cameraSwitcherId];
                if (!dev) {
                    this.log.warning(`unknown cameraSwitcherId (${cmd.cameraSwitcherId}) in ${cmd.type} command`);
                    break;
                }
                dev.set('cameraSelfView', cmd.value);
                break;
            }
            case 'selfViewAuto': {
                const dev = this.devices[cmd.cameraSwitcherId];
                if (!dev) {
                    this.log.warning(`unknown cameraSwitcherId (${cmd.cameraSwitcherId}) in ${cmd.type} command`);
                    break;
                }
                dev.set('cameraSelfViewAuto', cmd.value);
                break;
            }
            case 'selfViewPosition': {
                const dev = this.devices[cmd.cameraSwitcherId];
                if (!dev) {
                    this.log.warning(`unknown cameraSwitcherId (${cmd.cameraSwitcherId}) in ${cmd.type} command`);
                    break;
                }
                dev.set('cameraSelfViewPosition', cmd.value);
                break;
            }
            case 'sendMeetingCode': {
                const dev = this.devicesByCallId[cmd.callId];
                if (!dev) {
                    this.log.warning(`no device for callId (${cmd.callId})`);
                    break;
                }
                dev.set('sendMeetingCode', cmd);
                // Send meeting code on synchronized devices if necessary.
                if (!this.room)
                    throw new Error(`room undefined`);
                const recentCalls = this.room.$getCalls(defaults.inara.numRecentCalls);
                const call = recentCalls.$recordAt(cmd.callId);
                if (!call)
                    break;
                for (const syncDev of Object.values(this.synchronizedCallDevices[call.callType])) {
                    if (((_68 = (_67 = syncDev.config.synchronizedCalls) === null || _67 === void 0 ? void 0 : _67[call.callType]) === null || _68 === void 0 ? void 0 : _68.parentEquipmentId) !==
                        dev.eqId) {
                        continue;
                    }
                    for (const [callId, callDev] of Object.entries(this.devicesByCallId)) {
                        if (callDev.eqId !== syncDev.eqId)
                            continue;
                        if (((_69 = this.synchronizedCalls[callId]) === null || _69 === void 0 ? void 0 : _69.callType) !== call.callType)
                            continue;
                        syncDev.set(cmd.type, Object.assign(Object.assign({}, cmd), { callId }));
                    }
                }
                break;
            }
            case 'sendVideo': {
                if (cmd.synchronizedCallParticipantUserId) {
                    const calls = (_70 = this.room) === null || _70 === void 0 ? void 0 : _70.$getCalls(defaults.inara.numRecentCalls);
                    const parentCall = calls === null || calls === void 0 ? void 0 : calls.$recordAt(cmd.callId);
                    let childCallFound = false;
                    for (const participant of Object.values((_71 = parentCall === null || parentCall === void 0 ? void 0 : parentCall.$getParticipants()) !== null && _71 !== void 0 ? _71 : {})) {
                        if (participant.userId === cmd.synchronizedCallParticipantUserId) {
                            childCallFound = true;
                        }
                    }
                    if (!childCallFound) {
                        this.log.warning(`sendVideo: no participant in parent call with userId = "${cmd.synchronizedCallParticipantUserId}"`);
                        break;
                    }
                    childCallFound = false;
                    for (const [childCallId, childCallStatus] of Object.entries(this.synchronizedCalls)) {
                        if (childCallStatus.userId === cmd.synchronizedCallParticipantUserId) {
                            const childDev = this.devicesByCallId[childCallId];
                            if (childDev) {
                                childDev.set(cmd.type, {
                                    callId: childCallId,
                                    value: cmd.value,
                                });
                                childCallFound = true;
                                break;
                            }
                            else {
                                this.log.warning(`no device for callId ${childCallId}`);
                            }
                        }
                    }
                    if (!childCallFound)
                        this.log.error(`child call not found`);
                }
                else if (cmd.synchronizedCallParticipantId) {
                    const calls = (_72 = this.room) === null || _72 === void 0 ? void 0 : _72.$getCalls(defaults.inara.numRecentCalls);
                    const parentCall = calls === null || calls === void 0 ? void 0 : calls.$recordAt(cmd.callId);
                    const participants = parentCall === null || parentCall === void 0 ? void 0 : parentCall.$getParticipants();
                    const childParticipant = participants === null || participants === void 0 ? void 0 : participants.$recordAt(cmd.synchronizedCallParticipantId);
                    let childCallFound = false;
                    if (!childParticipant) {
                        this.log.warning(`sendVideo: couldn't find child participant`);
                        break;
                    }
                    for (const [childCallId, childCallStatus] of Object.entries(this.synchronizedCalls)) {
                        if (childCallStatus.userId === childParticipant.userId) {
                            const childDev = this.devicesByCallId[childCallId];
                            if (childDev) {
                                childDev.set(cmd.type, {
                                    callId: childCallId,
                                    value: cmd.value,
                                });
                                childCallFound = true;
                                break;
                            }
                            else {
                                this.log.warning(`no device for callId ${childCallId}`);
                            }
                        }
                    }
                    if (!childCallFound)
                        this.log.error(`child call not found`);
                }
                else {
                    const dev = this.devicesByCallId[cmd.callId];
                    if (!dev) {
                        this.log.warning(`no device for callId (${cmd.callId})`);
                        break;
                    }
                    dev.set(cmd.type, cmd);
                }
                break;
            }
            case 'shadePosition':
            case 'shadeTilt': {
                const roomConfig = this.roomConfig;
                if (roomConfig === undefined)
                    throw new Error(`roomConfig undefined`);
                const shadeControl = (_73 = roomConfig === null || roomConfig === void 0 ? void 0 : roomConfig.shadeControls) === null || _73 === void 0 ? void 0 : _73[cmd.shadeControlId];
                if (shadeControl === undefined) {
                    this.log.warning(`unknown shadeControlId (${cmd.shadeControlId}) in ${cmd.type} command`);
                    break;
                }
                if (shadeControl.type === 'equipment') {
                    const dev = this.devices[shadeControl.equipmentId];
                    if (!dev) {
                        this.log.error(`unknown equipmentId (${shadeControl.equipmentId}) in shade control`);
                        break;
                    }
                    if (cmd.type === 'shadePosition') {
                        dev.set('shadeOperation', {
                            event: cmd.event,
                            motion: cmd.motion,
                            shadeControlId: cmd.shadeControlId,
                            type: 'position',
                        });
                    }
                    else {
                        dev.set('shadeOperation', {
                            event: cmd.event,
                            motion: cmd.motion,
                            shadeControlId: cmd.shadeControlId,
                            type: 'tilt',
                        });
                    }
                }
                else if (shadeControl.type === 'emittedAction') {
                    let actions;
                    if (cmd.type === 'shadePosition') {
                        actions = (_76 = (_75 = (_74 = shadeControl.position) === null || _74 === void 0 ? void 0 : _74[cmd.motion]) === null || _75 === void 0 ? void 0 : _75.actions) !== null && _76 !== void 0 ? _76 : {};
                    }
                    else {
                        actions = (_79 = (_78 = (_77 = shadeControl.tilt) === null || _77 === void 0 ? void 0 : _77[cmd.motion]) === null || _78 === void 0 ? void 0 : _78.actions) !== null && _79 !== void 0 ? _79 : {};
                    }
                    for (const [actionId, action] of Object.entries(actions)) {
                        if (action.type !== 'contactClosure') {
                            this.log.error(`unhandled emitted action type`);
                            continue;
                        }
                        const dev = this.devices[action.equipmentId];
                        if (!dev) {
                            this.log.warning(`unknown equipmentId (${actions.equipmentId}) in emitted action`);
                            continue;
                        }
                        this.doContactClosureBehavior(dev, cmd.shadeControlId, actionId, action.behavior, cmd.event === 'press' ? 'start' : 'stop');
                    }
                }
                else {
                    this.log.error(`unhandled shade control type`);
                    break;
                }
                break;
            }
            case 'shadePreset': {
                const roomConfig = this.roomConfig;
                if (roomConfig === undefined)
                    throw new Error(`roomConfig undefined`);
                const shadePreset = (_80 = roomConfig === null || roomConfig === void 0 ? void 0 : roomConfig.shadePresets) === null || _80 === void 0 ? void 0 : _80[cmd.shadePresetId];
                if (shadePreset === undefined) {
                    this.log.warning(`unknown shadePresetId (${cmd.shadePresetId}) in ${cmd.type} command`);
                    break;
                }
                if (shadePreset.type === 'equipment') {
                    const dev = this.devices[shadePreset.equipmentId];
                    if (!dev) {
                        this.log.error(`unknown equipmentId (${shadePreset.equipmentId}) in shade preset`);
                    }
                    dev.set(cmd.type, { shadePresetId: cmd.shadePresetId, value: true });
                }
                else {
                    this.log.error(`unhandled shade preset type`);
                }
                break;
            }
            case 'soundReinforcementMute': {
                const dev = this.devicesBySoundReinforcementId[cmd.zoneId];
                if (!dev) {
                    this.log.warning(`invalid sound reinforcement zoneId: ${cmd.zoneId}`);
                }
                else {
                    dev.set(cmd.type, { zoneId: cmd.zoneId, value: cmd.value });
                }
                break;
            }
            case 'showDisplay':
                this.doDisplayMotion(cmd.displayId, cmd.displayMotionId, 'show', cmd.manual);
                break;
            case 'soundReinforcementMuteToggle': {
                const dev = this.devicesBySoundReinforcementId[cmd.zoneId];
                if (!dev) {
                    this.log.warning(`invalid sound reinforcement zoneId: ${cmd.zoneId}`);
                }
                else {
                    const value = (_85 = (_84 = (_83 = (_82 = (_81 = this.room) === null || _81 === void 0 ? void 0 : _81.states) === null || _82 === void 0 ? void 0 : _82.soundReinforcement) === null || _83 === void 0 ? void 0 : _83[cmd.zoneId]) === null || _84 === void 0 ? void 0 : _84.mute) !== null && _85 !== void 0 ? _85 : false;
                    dev.set('soundReinforcementMute', { zoneId: cmd.zoneId, value: !value });
                }
                break;
            }
            case 'soundReinforcementVolume': {
                const dev = this.devicesBySoundReinforcementId[cmd.zoneId];
                if (!dev) {
                    this.log.warning(`invalid sound reinforcement zoneId: ${cmd.zoneId}`);
                }
                else {
                    dev.set(cmd.type, { zoneId: cmd.zoneId, value: cmd.value });
                }
                break;
            }
            case 'soundReinforcementVolumeInc': {
                const dev = this.devicesBySoundReinforcementId[cmd.zoneId];
                if (!dev) {
                    this.log.warning(`invalid sound reinforcement zoneId: ${cmd.zoneId}`);
                }
                else {
                    const value = (_90 = (_89 = (_88 = (_87 = (_86 = this.room) === null || _86 === void 0 ? void 0 : _86.states) === null || _87 === void 0 ? void 0 : _87.soundReinforcement) === null || _88 === void 0 ? void 0 : _88[cmd.zoneId]) === null || _89 === void 0 ? void 0 : _89.volume) !== null && _90 !== void 0 ? _90 : 0;
                    dev.set('soundReinforcementVolume', {
                        zoneId: cmd.zoneId,
                        value: value + cmd.value,
                    });
                }
                break;
            }
            case 'spotlightParticipant': {
                const dev = this.devicesByCallId[cmd.callId];
                if (!dev) {
                    this.log.warning(`no device for callId (${cmd.callId})`);
                    break;
                }
                dev.set('spotlightParticipant', cmd);
                break;
            }
            case 'startInteractiveAuthentication':
                if (!(cmd.eqId in this.devices)) {
                    this.log.error(`invalid equipment id (${cmd.eqId})`);
                }
                else {
                    this.removeDevice(cmd.eqId);
                    this.addDevice(cmd.eqId, true);
                }
                break;
            case 'stopContent':
                if (((_91 = this.caps.displays) === null || _91 === void 0 ? void 0 : _91[cmd.displayId]) === undefined) {
                    this.log.warning(`unknown display id (${cmd.displayId})`);
                }
                if (this.devices[cmd.displayId] === undefined) {
                    this.log.warning(`unknown equipment id (${cmd.displayId})`);
                    break;
                }
                this.stopContent(cmd.displayId, cmd.value);
                break;
            case 'stopDisplay':
                this.doDisplayMotion(cmd.displayId, cmd.displayMotionId, 'stop', cmd.manual);
                break;
            case 'usbHost': {
                const dev = this.devices[cmd.usbSwitcherId];
                if (dev === undefined) {
                    this.log.warning(`no device for usbSwitcherId (${cmd.usbSwitcherId})`);
                    break;
                }
                dev.set(cmd.type, cmd.value);
                break;
            }
            case 'wakeCameras': {
                const cameraDevices = {};
                for (const [cameraId, dev] of Object.entries(this.devicesByCameraId)) {
                    if (!dev.eqId) {
                        this.log.error(`eqId undefined for ${dev}`);
                        continue;
                    }
                    // Add the camera device if it has power control. It could be the camera itself
                    // or a device managing an externally controlled camera.
                    if (dev.caps.hasPowerControl) {
                        cameraDevices[dev.eqId] = dev;
                    }
                    // Get the actual camera info. If it is different, then add the externally
                    // controlled camera if it has power control.
                    const cameraInfo = this.getActualCamera(cameraId);
                    if (cameraInfo &&
                        cameraInfo.dev.eqId &&
                        cameraInfo.dev.eqId !== dev.eqId &&
                        cameraInfo.dev.caps.hasPowerControl) {
                        cameraDevices[cameraInfo.dev.eqId] = cameraInfo.dev;
                    }
                }
                // Power on all the devices we found.
                for (const dev of Object.values(cameraDevices)) {
                    dev.set('power', true);
                }
                break;
            }
            default:
                this.log.error(`unhandled room control command type: ${cmd.type}`);
                break;
        }
    }
    onRoomServiceStateChanged(state) {
        var _a, _b, _c;
        this.log.debug(`onRoomServiceStateChanged: ${state}`);
        if (this.roomService.isRoomLoaded() && this.room === undefined) {
            this.room = (_a = this.roomService.getRoom()) !== null && _a !== void 0 ? _a : undefined;
            if (!this.room)
                throw new Error(`room undefined`);
            if (this.isBrowser) {
                if (this.roomService.isUiPreview()) {
                    if (!this.pairedControllers) {
                        this.pairedControllers = this.room.$getControllers();
                        this.pairedControllers.$onChildRemoved(this.onPairedControllersChanged);
                        this.pairedControllers.$onChildChanged(this.onPairedControllersChanged);
                        this.pairedControllers.$onChildAdded(this.onPairedControllersChanged);
                        this.onPairedControllersChanged(); // TODO: remvove this? should be called by $onChildAdded
                    }
                }
                else {
                    // remote control: don't start EquipmentService
                }
            }
            else {
                this.startup();
            }
        }
        if (!this.roomService.isRoomLoaded() && this.room !== undefined) {
            if (!this.isShutdown())
                this.shutdown();
            if (this.isBrowser &&
                ((_c = (_b = this.room) === null || _b === void 0 ? void 0 : _b.uiPreview) === null || _c === void 0 ? void 0 : _c.controllerId) === this.uiPreviewControllerId) {
                this.room.$deactivateUiPreview();
            }
            this.room = undefined;
        }
    }
    onRoomVolumeSaveTimeout() {
        if (!this.room)
            throw new Error(`room undefined`);
        const roomValues = this.room.$getValues();
        if (this.roomVolumeSaveVolume !== undefined) {
            Sutl.setNode(roomValues, 'states.roomVolume', this.roomVolumeSaveVolume);
            Sutl.setNode(roomValues, 'states.volume', this.roomVolumeSaveVolume >> 1);
            this.roomVolumeSaveVolume = undefined;
        }
        if (this.roomVolumeSaveMute !== undefined) {
            Sutl.setNode(roomValues, 'states.roomVolumeMute', this.roomVolumeSaveMute);
            Sutl.setNode(roomValues, 'states.volumeMute', this.roomVolumeSaveMute);
            this.roomVolumeSaveMute = undefined;
        }
        if (!this.isOnline)
            return;
        this.room.$set(roomValues, false, false);
    }
    onSiteChanged(site) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (this.room && ((_a = this.room.info) === null || _a === void 0 ? void 0 : _a.organizationName) !== site.name) {
            const roomValues = this.room.$getValues();
            Sutl.setNode(roomValues, ['info', 'siteName'], site.name);
            this.room.$set(roomValues, false, false);
        }
        if (!Sutl.objEquals((_b = site.settings) === null || _b === void 0 ? void 0 : _b.synergySkyCallTypes, (_c = this.prevSiteSettings) === null || _c === void 0 ? void 0 : _c.synergySkyCallTypes) ||
            ((_d = site.settings) === null || _d === void 0 ? void 0 : _d.synergySkyDomain) !== ((_e = this.prevSiteSettings) === null || _e === void 0 ? void 0 : _e.synergySkyDomain) ||
            ((_f = site.settings) === null || _f === void 0 ? void 0 : _f.synergySkyEnabled) !== ((_g = this.prevSiteSettings) === null || _g === void 0 ? void 0 : _g.synergySkyEnabled)) {
            this.updateCapsAndOnlineStatus();
        }
        this.prevSiteSettings = Sutl.deepCopy(site.settings);
    }
    onUiPreviewTimeout() {
        if (!this.roomService.isRoomLoaded()) {
            this.log.error(`onUiPreviewTimeout: room not loaded`);
            return;
        }
        if (!this.room)
            throw new Error(`room undefined`);
        this.room.$activateUiPreview(this.uiPreviewControllerId);
        this.uiPreviewTimeout.set(this.onUiPreviewTimeout, defaults.inara.uiPreviewTimeoutMs);
    }
    onUpdateCapsAndOnlineStatusTimeout() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            this.log.debug(`onUpdateCapsAndOnlineStatusTimeout (full = ${this.updateCapsAndOnlineStatusFull})`);
            if (!this.updateCapsAndOnlineStatusFull) {
                return this.updateVideoSwitcherCaps();
            }
            if (!this.allDevicesHaveReceivedConnectStatus()) {
                this.log.debug(`still waiting for all devices to respond`);
                return;
            }
            this.savedCaps = {};
            for (const [equipmentId, dev] of Object.entries(this.devices)) {
                this.savedCaps[equipmentId] = Sutl.deepCopy(dev.caps);
            }
            yield this.updateValidatedStates(); // must happen before computing caps
            this.caps = yield this.computeCaps(true);
            this.configuredCaps = yield this.computeCaps(false);
            this.isOnline = Object.keys(this.onlineDevices).length > 0;
            let onlineState = 'offline';
            if (this.isOnline) {
                if (Object.keys(this.offlineDevices).length > 0) {
                    onlineState = 'limited';
                }
                else {
                    onlineState = 'online';
                }
            }
            this.log.debug(`isOnline = ${this.isOnline}`);
            this.log.debug(`onlineState = ${onlineState}`);
            if (this.room) {
                const roomValues = this.room.$getValues();
                Sutl.setProp(roomValues, 'states.onlineState', onlineState);
                if (this.isOnline) {
                    roomValues.caps = Sutl.deepCopy(this.caps);
                    roomValues.configuredCaps = Sutl.deepCopy(this.configuredCaps);
                    this.log.debug(`caps = ${JSON.stringify(this.caps)}`);
                    this.log.debug(`mute device = ${this.roomMuteDevice ? this.roomMuteDevice : 'none'}`);
                    this.log.debug(`volume device = ${this.roomVolumeDevice ? this.roomVolumeDevice : 'none'}`);
                    if (this.muteSyncDevices.length > 0) {
                        for (const dev of this.muteSyncDevices) {
                            this.log.debug(`mute-synced device = ${dev}`);
                        }
                    }
                }
                const organizationName = (_a = this.kayleeService.getCompany(this.room.companyId)) === null || _a === void 0 ? void 0 : _a.name;
                const siteName = (_b = this.kayleeService.getSite(this.room.siteId)) === null || _b === void 0 ? void 0 : _b.name;
                roomValues.info = Object.assign(Object.assign(Object.assign(Object.assign({}, (((_c = roomValues.settings) === null || _c === void 0 ? void 0 : _c.contacts)
                    ? { contacts: Sutl.objToArray((_d = roomValues.settings) === null || _d === void 0 ? void 0 : _d.contacts, 'a') }
                    : undefined)), (organizationName ? { organizationName } : undefined)), { roomName: roomValues.name }), (siteName ? { siteName } : undefined));
                yield this.room.$set(roomValues, false);
                this.automationService.triggerEvent({ type: 'roomCapsChanged' });
                if (this.isOnline)
                    this.queryStates();
            }
        });
    }
    powerOffRoom() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
        if (!this.room)
            throw new Error(`room undefined`);
        const roomValues = this.room.$getValues();
        Sutl.setProp(roomValues, 'states.powerOffTimestamp', Date.now());
        this.room.$set(roomValues, false, false);
        // Reset volume and mics mute.
        if (this.roomVolumeDevice)
            this.roomVolumeDevice.set('volume', 25);
        if (this.roomMuteDevice) {
            const micsMuteBehavior = (_b = (_a = this.kayleeService.getSite(this.room.siteId).settings) === null || _a === void 0 ? void 0 : _a.micsMuteBehaviorOnRoomPowerOff) !== null && _b !== void 0 ? _b : 'mute';
            if (micsMuteBehavior === 'mute') {
                this.roomMuteDevice.set('mute', true);
            }
            else if (micsMuteBehavior === 'unmute') {
                this.roomMuteDevice.set('mute', false);
            }
            else {
                this.log.error(`unknown value for micsMuteBehaviorOnRoomPowerOff: ${micsMuteBehavior}`);
            }
        }
        // Hangup calls.
        for (const [callId, dev] of Object.entries(this.devicesByCallId)) {
            dev.set('hangup', callId);
        }
        // Stop any content sources that are sending and any media sources that are playing.
        for (const [displayId, display] of Object.entries((_d = (_c = this.room.states) === null || _c === void 0 ? void 0 : _c.displays) !== null && _d !== void 0 ? _d : {})) {
            for (const [sourceId, source] of Object.entries((_e = display.sources) !== null && _e !== void 0 ? _e : {})) {
                const sourceCaps = (_j = (_h = (_g = (_f = this.room.caps) === null || _f === void 0 ? void 0 : _f.displays) === null || _g === void 0 ? void 0 : _g[displayId]) === null || _h === void 0 ? void 0 : _h.sources) === null || _j === void 0 ? void 0 : _j[sourceId];
                if (!sourceCaps) {
                    this.log.error(`source caps undefined`);
                    continue;
                }
                const dev = this.devices[sourceCaps.equipmentId];
                if (!dev) {
                    this.log.error(`device undefined`);
                    continue;
                }
                if (source.state) {
                    dev.set('stopContent', sourceCaps.videoInputId);
                }
                // Use Object.values because Firebase returns arrays as objects.
                const mediaOperations = Object.values((_k = sourceCaps.mediaOperations) !== null && _k !== void 0 ? _k : []);
                if (mediaOperations.includes('mediaPlayerStop')) {
                    dev.set('mediaOperation', {
                        videoInputId: sourceCaps.videoInputId,
                        operation: 'mediaPlayerStop',
                    });
                }
                if (mediaOperations.includes('mediaRecorderStop')) {
                    dev.set('mediaOperation', {
                        videoInputId: sourceCaps.videoInputId,
                        operation: 'mediaRecorderStop',
                    });
                }
                if (mediaOperations.includes('mediaStreamerStop')) {
                    dev.set('mediaOperation', {
                        videoInputId: sourceCaps.videoInputId,
                        operation: 'mediaStreamerStop',
                    });
                }
            }
        }
        // Set displays to their default source (the first source) in their menus.
        for (const [displayId, display] of Object.entries((_l = this.caps.displays) !== null && _l !== void 0 ? _l : {})) {
            let index = 1000;
            let firstSourceId;
            for (const [sourceId, source] of Object.entries((_m = display.sources) !== null && _m !== void 0 ? _m : {})) {
                if (source.index < index) {
                    firstSourceId = sourceId;
                    index = source.index;
                }
            }
            if (firstSourceId) {
                this.setDisplaySource(displayId, firstSourceId, false);
            }
        }
        for (const dev of Object.values(this.onlineDevices)) {
            // Mute assistive listening, and set volume to default.
            if (dev.caps.assistiveListening) {
                if (dev.caps.assistiveListening.hasMute) {
                    dev.set('assistiveListeningMute', true);
                }
                if (dev.caps.assistiveListening.hasVolume) {
                    dev.set('assistiveListeningVolume', 0);
                }
            }
            // Mute overflow rooms, and set volumes to default.
            for (const roomId of Object.keys((_o = dev.caps.overflowRooms) !== null && _o !== void 0 ? _o : {})) {
                if ((_p = dev.caps.overflowRooms) === null || _p === void 0 ? void 0 : _p[roomId].hasMute) {
                    dev.set('overflowRoomMute', { roomId: roomId, value: true });
                }
                if ((_q = dev.caps.overflowRooms) === null || _q === void 0 ? void 0 : _q[roomId].hasVolume) {
                    dev.set('overflowRoomVolume', { roomId: roomId, value: 0 });
                }
            }
            // Mute sound reinforcement zones, and set volumes to default.
            for (const zoneId of Object.keys((_r = dev.caps.soundReinforcement) !== null && _r !== void 0 ? _r : {})) {
                if ((_s = dev.caps.soundReinforcement) === null || _s === void 0 ? void 0 : _s[zoneId].hasMute) {
                    dev.set('soundReinforcementMute', { zoneId: zoneId, value: true });
                }
                if ((_t = dev.caps.soundReinforcement) === null || _t === void 0 ? void 0 : _t[zoneId].hasVolume) {
                    dev.set('soundReinforcementVolume', { zoneId: zoneId, value: 0 });
                }
            }
            // Mute audio input sources, and set volumes to default.
            for (const sourceId of Object.keys((_u = dev.caps.audioInputSources) !== null && _u !== void 0 ? _u : {})) {
                if ((_v = dev.caps.audioInputSources) === null || _v === void 0 ? void 0 : _v[sourceId].hasMute) {
                    dev.set('audioInputMute', { srcId: sourceId, value: true });
                }
                if ((_w = dev.caps.audioInputSources) === null || _w === void 0 ? void 0 : _w[sourceId].hasVolume) {
                    dev.set('audioInputVolume', { srcId: sourceId, value: true });
                }
            }
            // Power off device.
            if (dev.caps.hasPowerControl && !dev.caps.isPowerToggle) {
                dev.set('power', false);
            }
        }
        // Notify automation service. (Eventually this will be done via room events in the database).
        this.automationService.triggerEvent({ type: 'roomPowerOff' });
    }
    queryStates() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6;
        this.log.debug(`queryStates`);
        this.powerByEqId = {};
        if ((_a = this.roomMuteDevice) === null || _a === void 0 ? void 0 : _a.caps.hasRoomMute) {
            this.roomMuteDevice.query('mute');
        }
        if ((_b = this.roomVolumeDevice) === null || _b === void 0 ? void 0 : _b.caps.hasRoomVolume) {
            this.roomVolumeDevice.query('volume');
        }
        if ((_c = this.roomVolumeDevice) === null || _c === void 0 ? void 0 : _c.caps.hasRoomVolumeMute) {
            this.roomVolumeDevice.query('volumeMute');
        }
        if (!this.room)
            throw new Error(`room undefined`);
        let roomValues = this.room.$getValues();
        (_d = roomValues.states) === null || _d === void 0 ? true : delete _d.assistiveListening;
        (_e = roomValues.states) === null || _e === void 0 ? true : delete _e.audioInputSources;
        (_f = roomValues.states) === null || _f === void 0 ? true : delete _f.callInLists;
        (_g = roomValues.states) === null || _g === void 0 ? true : delete _g.displays;
        (_h = roomValues.states) === null || _h === void 0 ? true : delete _h.overflowRooms;
        (_j = roomValues.states) === null || _j === void 0 ? true : delete _j.soundReinforcement;
        (_k = roomValues.states) === null || _k === void 0 ? true : delete _k.cameraSwitchers;
        (_l = roomValues.states) === null || _l === void 0 ? true : delete _l.lightingControls;
        (_m = roomValues.states) === null || _m === void 0 ? true : delete _m.lightingPresets;
        (_o = roomValues.states) === null || _o === void 0 ? true : delete _o.shadeControls;
        (_p = roomValues.states) === null || _p === void 0 ? true : delete _p.shadePresets;
        this.room.$set(roomValues, false, false);
        let anyDeviceCanCall = false;
        // Query video switching topology routes first. This must be done before querying any
        // parameters related to video switching (e.g., contentState).
        this.queryVideoSwitcherRoutes();
        for (const dev of Object.values(this.onlineDevices)) {
            let canCall = false;
            for (const callType of callTypes) {
                if ((_r = (_q = dev.caps.maxCalls) === null || _q === void 0 ? void 0 : _q[callType]) !== null && _r !== void 0 ? _r : 0 > 0) {
                    canCall = true;
                    anyDeviceCanCall = true;
                }
            }
            if (canCall)
                dev.query('callStatus');
            if (dev.caps.hasCallInList)
                dev.query('callInList');
            if (dev.caps.hasCalendar)
                dev.query('calendar');
            if (dev.caps.hasDirectory)
                dev.query('directory');
            if (dev.caps.hasFirmwareVersion)
                dev.query('firmwareVersion');
            if (dev.caps.hasName)
                dev.query('name');
            if (dev.caps.hasPowerControl)
                dev.query('power');
            if (dev.caps.hasSerialNumber)
                dev.query('serialNumber');
            if (dev.caps.hasBusy)
                dev.query('busy');
            if (dev === this.assistiveListeningDevice) {
                if ((_s = dev.caps.assistiveListening) === null || _s === void 0 ? void 0 : _s.hasMute)
                    dev.query('assistiveListeningMute');
                if ((_t = dev.caps.assistiveListening) === null || _t === void 0 ? void 0 : _t.hasVolume)
                    dev.query('assistiveListeningVolume');
            }
            if (dev.caps.audioInputSources) {
                let hasMute = false;
                let hasVolume = false;
                for (const source of Object.values(dev.caps.audioInputSources)) {
                    if (source.hasMute)
                        hasMute = true;
                    if (source.hasVolume)
                        hasVolume = true;
                }
                if (hasMute)
                    dev.query('audioInputMute');
                if (hasVolume)
                    dev.query('audioInputVolume');
            }
            if (dev.caps.overflowRooms) {
                let hasMute = false;
                let hasVolume = false;
                for (const room of Object.values(dev.caps.overflowRooms)) {
                    if (room.hasMute)
                        hasMute = true;
                    if (room.hasVolume)
                        hasVolume = true;
                }
                if (hasMute)
                    dev.query('overflowRoomMute');
                if (hasVolume)
                    dev.query('overflowRoomVolume');
            }
            if (dev.caps.soundReinforcement) {
                let hasMute = false;
                let hasVolume = false;
                for (const zone of Object.values(dev.caps.soundReinforcement)) {
                    if (zone.hasMute)
                        hasMute = true;
                    if (zone.hasVolume)
                        hasVolume = true;
                }
                if (hasMute)
                    dev.query('soundReinforcementMute');
                if (hasVolume)
                    dev.query('soundReinforcementVolume');
            }
            if (dev.caps.lightingControls) {
                let hasDimmer = false;
                let hasPower = false;
                for (const ctrl of Object.values(dev.caps.lightingControls)) {
                    if (ctrl.hasDimmer)
                        hasDimmer = true;
                    if (ctrl.hasPower)
                        hasPower = true;
                }
                if (hasDimmer)
                    dev.query('lightingLevel');
                if (hasPower)
                    dev.query('lightingPower');
            }
            if (Object.keys((_u = dev.caps.lightingPresets) !== null && _u !== void 0 ? _u : {}).length > 0) {
                dev.query('lightingPreset');
            }
            if (dev.caps.shadeControls) {
                let hasPosition = false;
                let hasTilt = false;
                for (const ctrl of Object.values(dev.caps.shadeControls)) {
                    if (ctrl.hasPositionState)
                        hasPosition = true;
                    if (ctrl.hasTiltState)
                        hasTilt = true;
                }
                if (hasPosition)
                    dev.query('shadePosition');
                if (hasTilt)
                    dev.query('shadeTilt');
            }
            if (Object.keys((_v = dev.caps.shadePresets) !== null && _v !== void 0 ? _v : {}).length > 0) {
                dev.query('shadePreset');
            }
            if (Object.keys((_w = dev.caps.usbHosts) !== null && _w !== void 0 ? _w : {}).length > 0) {
                dev.query('usbHost');
            }
            if (dev.caps.hasVideoSwitcher &&
                dev.caps.hasVideoInputs &&
                dev.caps.videoInputsAreContent) {
                dev.query('contentState');
            }
            const eq = (_x = this.equipment) === null || _x === void 0 ? void 0 : _x.$recordAt((_y = dev.eqId) !== null && _y !== void 0 ? _y : '');
            const eqType = this.kayleeService.getEquipmentTypes().$recordAt((_z = eq === null || eq === void 0 ? void 0 : eq.type) !== null && _z !== void 0 ? _z : '');
            if (Object.keys((_0 = dev.caps.cameras) !== null && _0 !== void 0 ? _0 : {}).length > 0) {
                if ((eqType === null || eqType === void 0 ? void 0 : eqType.type) !== 'camera')
                    dev.query('cameraSelected');
                for (const camera of Object.values((_1 = dev.caps.cameras) !== null && _1 !== void 0 ? _1 : {})) {
                    if (camera.hasCameraTracking) {
                        dev.query('cameraTracking');
                        break;
                    }
                }
                if (dev.caps.hasSelfView)
                    dev.query('cameraSelfView');
                if (dev.caps.hasSelfViewAuto)
                    dev.query('cameraSelfViewAuto');
                if (dev.caps.selfViewPositions !== undefined)
                    dev.query('cameraSelfViewPosition');
            }
            if (dev.caps.hasDisplayBlank && !dev.caps.isDisplayBlankStateless) {
                dev.query('displayBlank');
            }
            if (dev.caps.hasDisplayFreeze && !dev.caps.isDisplayFreezeStateless) {
                dev.query('displayFreeze');
            }
            if (((_2 = eq === null || eq === void 0 ? void 0 : eq.config) === null || _2 === void 0 ? void 0 : _2.displayVolumeUiEnabled) &&
                dev.caps.hasVolume &&
                !dev.caps.isVolumeStateless) {
                dev.query('volume');
                if (dev.caps.hasVolumeMute)
                    dev.query('volumeMute');
            }
            let queryChannel = false;
            let queryClosedCaptioning = false;
            let queryChannelTuner = false;
            let queryMediaPlayerState = false;
            let queryMediaRecorderState = false;
            let queryMediaStreamerState = false;
            for (const videoInput of Object.values((_3 = dev.caps.videoInputs) !== null && _3 !== void 0 ? _3 : {})) {
                if (!('mediaOperations' in videoInput))
                    continue;
                // All the is*Stateless values are true if stateful, false if stateless, and
                // undefined if there are no caps for that feature.
                if (videoInput.isChannelStateless === false)
                    queryChannel = true;
                if (videoInput.isClosedCaptioningStateless === false)
                    queryClosedCaptioning = true;
                if (videoInput.isChannelTunerStateless === false)
                    queryChannelTuner = true;
                if (videoInput.isMediaPlayerStateless === false)
                    queryMediaPlayerState = true;
                if (videoInput.isMediaRecorderStateless === false)
                    queryMediaRecorderState = true;
                if (videoInput.isMediaStreamerStateless === false)
                    queryMediaStreamerState = true;
            }
            if (queryChannel)
                dev.query('channel');
            if (queryClosedCaptioning)
                dev.query('closedCaptioning');
            if (queryChannelTuner)
                dev.query('channelTuner');
            if (queryMediaPlayerState)
                dev.query('mediaPlayerState');
            if (queryMediaRecorderState) {
                dev.query('mediaRecorderState');
                dev.query('mediaRecorderStartTime');
            }
            if (queryMediaStreamerState)
                dev.query('mediaStreamerState');
        }
        roomValues = this.room.$getValues();
        let changed = false;
        if (!anyDeviceCanCall) {
            Sutl.setProp(roomValues, 'states.onAir', false);
            changed = true;
        }
        if ((_5 = (_4 = this.room.caps) === null || _4 === void 0 ? void 0 : _4.cameraSwitchers) === null || _5 === void 0 ? void 0 : _5['-virtualCameraSwitcher-']) {
            let firstCamera = true;
            for (const id of Object.keys((_6 = this.room.caps.cameraSwitchers['-virtualCameraSwitcher-'].cameras) !== null && _6 !== void 0 ? _6 : {})) {
                Sutl.setNode(roomValues, [
                    'states',
                    'cameraSwitchers',
                    '-virtualCameraSwitcher-',
                    'cameras',
                    id,
                    'selected',
                ], firstCamera);
                firstCamera = false;
                changed = true;
            }
        }
        if (changed) {
            this.room.$set(roomValues, false, false);
        }
        this.cleanupCallsTimeout.set(this.cleanupCalls, 5000);
    }
    queryVideoSwitcherRoutes() {
        var _a;
        for (const [equipmentId, dev] of Object.entries(this.onlineDevices)) {
            if (dev.caps.hasVideoInputs) {
                if (dev.caps.videoInputsAreContent) {
                    dev.query('contentSource');
                }
                else if (dev.caps.hasVideoOutputs) {
                    if (dev.caps.hasVideoSwitcher) {
                        dev.query('videoMatrixRoute');
                    }
                    else {
                        if (!dev.caps.videoInputs)
                            throw new Error(`videoInputs undefined`);
                        for (const outId of Object.keys((_a = dev.caps.videoOutputs) !== null && _a !== void 0 ? _a : {})) {
                            const inId = Object.keys(dev.caps.videoInputs)[0];
                            this.updateVideoSwitcherStates(equipmentId, inId, outId);
                        }
                    }
                }
                else {
                    dev.query('videoInput');
                }
            }
        }
    }
    removeDevice(equipmentId) {
        this.log.debug(`removeDevice: ${equipmentId}`);
        if (!(equipmentId in this.devices)) {
            this.log.error(`not watching equipment (${equipmentId})`);
            return;
        }
        this.log.debug(`removeDevice: dev = ${this.devices[equipmentId]}`);
        // Clean up device info.
        this.devices[equipmentId].destroy();
        delete this.connectStates[equipmentId];
        delete this.devices[equipmentId];
        // Clean up calls info.
        for (const callType of callTypes) {
            delete this.synchronizedCallDevices[callType][equipmentId];
        }
        for (const [callId, callDev] of Object.entries(this.devicesByCallId)) {
            if (callDev.eqId !== equipmentId)
                continue;
            delete this.devicesByCallId[callId];
            delete this.synchronizedCalls[callId];
        }
        // Clean up contact closure info.
        const equipmentTimeouts = this.contactClosureTimeouts[equipmentId];
        if (equipmentTimeouts !== undefined) {
            for (const [displayMotionId, motionTimeouts] of Object.entries(equipmentTimeouts)) {
                for (const [actionId, timeout] of Object.entries(motionTimeouts)) {
                    timeout.destroy();
                    delete motionTimeouts[actionId];
                }
                delete equipmentTimeouts[displayMotionId];
            }
            delete this.contactClosureTimeouts[equipmentId];
        }
        // Clean up equipment directory.
        this.directoryManager.updateEquipmentDirectory(equipmentId, undefined);
        this.updateCapsAndOnlineStatus();
    }
    runRoomMacro(macroId) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return __awaiter(this, void 0, void 0, function* () {
            let roomWentOffline = ((_c = (_b = (_a = this.room) === null || _a === void 0 ? void 0 : _a.states) === null || _b === void 0 ? void 0 : _b.onlineState) !== null && _c !== void 0 ? _c : 'offline') === 'offline';
            if (!this.room || roomWentOffline) {
                this.log.warning(`can't run macro when room is offline`);
                return;
            }
            const handleRoomChanged = () => {
                var _a, _b, _c;
                const isRoomOffline = ((_c = (_b = (_a = this.room) === null || _a === void 0 ? void 0 : _a.states) === null || _b === void 0 ? void 0 : _b.onlineState) !== null && _c !== void 0 ? _c : 'offline') === 'offline';
                if (isRoomOffline)
                    roomWentOffline = true;
            };
            this.room.$onChange(handleRoomChanged);
            let macroFound = false;
            for (const [roomMacroId, macro] of Object.entries((_e = (_d = this.room.settings) === null || _d === void 0 ? void 0 : _d.macros) !== null && _e !== void 0 ? _e : {})) {
                if (macroId !== roomMacroId)
                    continue;
                this.log.info(`running macro ${macro.name} (${macroId})`);
                macroFound = true;
                const actions = Object.entries((_f = macro.actions) !== null && _f !== void 0 ? _f : {})
                    .map(([key, action]) => (Object.assign(Object.assign({}, action), { key })))
                    .sort((a, b) => a.index - b.index);
                if (!this.room) {
                    this.log.info(`stopping macro ${macro.name} (${macroId}) because room is offline`);
                    break;
                }
                else if (roomWentOffline) {
                    this.log.info(`stopping macro ${macro.name} (${macroId}) because room went offline`);
                    break;
                }
                for (const action of actions) {
                    if (action.type === 'delay') {
                        yield this.timeouts.delay(action.delayMs);
                    }
                    else if (action.type === 'interpolatedRoomControlCommand') {
                        const command = evaluateParsedRoomControlCommand(this.parsedRoomControlCommands, action.key, (_g = this.room) === null || _g === void 0 ? void 0 : _g.states);
                        if (command !== undefined) {
                            // Don't await -- see comment below.
                            this.room.$pushCommand(command);
                        }
                    }
                    else if (action.type === 'roomControlCommand') {
                        // Don't await because there are some conditions where we can be disconnected
                        // from the WAN, but the Firebase SDK doesn't consider itself disconnected. In
                        // that case, pushing room control commands hangs for a long time or maybe even
                        // forever.
                        //
                        // Copying the command is important since the command is modified by
                        // $pushCommand, and we don't want to modify the command within the macro.
                        this.room.$pushCommand(Sutl.deepCopy(action.command));
                    }
                }
                this.log.info(`macro ${macro.name} (${macroId}) finished`);
            }
            if (!macroFound)
                this.log.warning(`macro (${macroId}) not found`);
            (_h = this.room) === null || _h === void 0 ? void 0 : _h.$offChange(handleRoomChanged);
        });
    }
    sendInitialSpotlight(call) {
        var _a;
        const dev = this.devicesByCallId[call.$id];
        if (dev === undefined)
            return;
        const participants = call.$getParticipants();
        let isHost = false;
        if (participants.length < 3)
            return;
        // Find ourselves, make sure we're the host, and send our initial spotlight if necessary.
        for (const participant of participants) {
            if (participant.isMe) {
                if (participant.isHost) {
                    isHost = true;
                    if (dev.config.initialSpotlight && !this.initialSpotlightSent[call.$id]) {
                        this.initialSpotlightSent[call.$id] = true;
                        dev.set('spotlightParticipant', {
                            callId: call.$id,
                            participantId: participant.$id,
                            value: true,
                        });
                    }
                    break;
                }
            }
        }
        if (!isHost)
            return;
        // Send initial spotlight for child devices.
        for (const participant of participants) {
            if (!participant.isSynchronizedCall)
                continue;
            for (const [childCallId, childCall] of Object.entries(this.synchronizedCalls)) {
                const childDev = this.devicesByCallId[childCallId];
                const syncInfo = (_a = childDev === null || childDev === void 0 ? void 0 : childDev.config.synchronizedCalls) === null || _a === void 0 ? void 0 : _a[call.callType];
                if (this.initialSpotlightSent[childCallId] ||
                    childCall.callState !== 'connected' ||
                    syncInfo === undefined ||
                    syncInfo.parentEquipmentId !== dev.eqId ||
                    !syncInfo.initialSpotlight) {
                    continue;
                }
                this.initialSpotlightSent[childCallId] = true;
                dev.set('spotlightParticipant', {
                    callId: call.$id,
                    participantId: participant.$id,
                    value: true,
                });
            }
        }
    }
    setDisplayPath(sourcePath, powerOnDevices = true, onlyPower = false) {
        for (const entry of sourcePath) {
            const dev = this.devices[entry.equipmentId];
            if (!dev) {
                this.log.error(`equipmentId = ${entry.equipmentId} not present in devices`);
                continue;
            }
            if (powerOnDevices &&
                dev.caps.hasPowerControl &&
                !dev.caps.isPowerToggle &&
                !this.videoSwitcherStates[entry.equipmentId].power) {
                dev.set('power', true);
            }
            if (onlyPower)
                continue;
            if (powerOnDevices ||
                !dev.caps.hasPowerControl ||
                dev.caps.isPowerToggle ||
                this.videoSwitcherStates[entry.equipmentId].power) {
                if (dev.caps.videoInputsAreContent) {
                    dev.set('contentSource', entry.videoInputId);
                }
                else if (entry.videoOutputId === '-display-') {
                    dev.set('videoInput', entry.videoInputId);
                }
                else {
                    if (dev.caps.hasVideoSwitcher) {
                        dev.set('videoMatrixRoute', {
                            videoInputId: entry.videoInputId,
                            videoOutputId: entry.videoOutputId,
                        });
                    }
                    else {
                        if (!dev.eqId)
                            throw new Error(`dev.eqId undefined`);
                        this.updateVideoSwitcherStates(dev.eqId, entry.videoInputId, entry.videoOutputId);
                    }
                }
            }
        }
    }
    setDisplaySource(equipmentId, sourceId, powerOnDevices = true, onlyPower = false) {
        var _a, _b;
        const display = (_a = this.caps.displays) === null || _a === void 0 ? void 0 : _a[equipmentId];
        if (!display) {
            this.log.warning(`equipmentId ${equipmentId} not present in displays`);
            return;
        }
        const source = (_b = display.sources) === null || _b === void 0 ? void 0 : _b[sourceId];
        if (!source) {
            this.log.warning(`sourceId ${sourceId} not present in sources for display ` +
                `with equipmentId ${equipmentId}`);
            return;
        }
        const outId = this.getVideoOutputIds(equipmentId)[0];
        const sourcePath = this.getPathToDisplaySource(source, equipmentId, outId);
        if (!sourcePath) {
            this.log.error(`no path to source sourceId = ${sourceId}, equipmentId = ${source.equipmentId}, ` +
                `videoInputId = ${source.videoInputId} for equipmentId = ${equipmentId}`);
            return;
        }
        this.setDisplayPath(sourcePath, powerOnDevices, onlyPower);
    }
    setupDisplaysForCall(dev) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        for (const [displayId, display] of Object.entries((_c = (_b = (_a = this.room) === null || _a === void 0 ? void 0 : _a.caps) === null || _b === void 0 ? void 0 : _b.displays) !== null && _c !== void 0 ? _c : {})) {
            // If we're already on an output for the device, then we don't want to do any switching,
            // but we do want to make sure all the necessary devices are powered on.
            let sourceId = (_g = (_f = (_e = (_d = this.room) === null || _d === void 0 ? void 0 : _d.states) === null || _e === void 0 ? void 0 : _e.displays) === null || _f === void 0 ? void 0 : _f[displayId]) === null || _g === void 0 ? void 0 : _g.videoInput;
            if (sourceId) {
                const equipmentId = (_j = (_h = display.sources) === null || _h === void 0 ? void 0 : _h[sourceId]) === null || _j === void 0 ? void 0 : _j.equipmentId;
                if (!equipmentId)
                    throw new Error(`equipment undefined`);
                if (equipmentId === dev.eqId) {
                    // Call setDisplaySource with onlyPower true to power on all necessary devices.
                    this.setDisplaySource(displayId, sourceId, true, true);
                    continue;
                }
            }
            // We're not already on an output for the device, so try to find one.
            let foundSourceId;
            for (sourceId of Object.keys((_k = display.sources) !== null && _k !== void 0 ? _k : {})) {
                const source = (_l = display.sources) === null || _l === void 0 ? void 0 : _l[sourceId];
                if (!source)
                    throw new Error(`source undefined`);
                if (source.equipmentId === dev.eqId) {
                    if (this.isMustInclude(displayId, sourceId)) {
                        foundSourceId = sourceId;
                        break;
                    }
                    else if (foundSourceId === undefined ||
                        source.index < ((_o = (_m = display.sources) === null || _m === void 0 ? void 0 : _m[foundSourceId].index) !== null && _o !== void 0 ? _o : 1000)) {
                        foundSourceId = sourceId;
                    }
                }
            }
            if (foundSourceId) {
                this.setDisplaySource(displayId, foundSourceId);
            }
        }
    }
    shutdown() {
        var _a, _b, _c;
        this.log.debug(`shutdown`);
        this.uiPreviewTimeout.clear();
        if (this.room !== undefined) {
            this.room.$offChange(this.onRoomChanged);
        }
        this.callUpdateManager.disable();
        this.directoryManager.disable();
        if (this.pairedControllers !== undefined) {
            this.pairedControllers.$offChildAdded(this.onPairedControllersChanged);
            this.pairedControllers.$offChildChanged(this.onPairedControllersChanged);
            this.pairedControllers.$offChildRemoved(this.onPairedControllersChanged);
            this.pairedControllers = undefined;
        }
        if (this.roomControlCommands !== undefined) {
            this.roomControlCommands.$offChildAdded(this.onRoomControlCommandAdded);
            this.roomControlCommands = undefined;
        }
        if (this.equipment !== undefined) {
            this.equipment.$offChildAdded(this.onEquipmentAdded);
            this.equipment.$offChildChanged(this.onEquipmentChanged);
            this.equipment.$offChildRemoved(this.onEquipmentRemoved);
            this.equipment = undefined;
        }
        for (const dev of Object.values(this.devices)) {
            dev.destroy();
        }
        this.callExtendedTypeOverrides = {};
        this.caps = {};
        this.devices = {};
        this.dialStringOverrides = {};
        this.driverValidated = {};
        this.initialSpotlightSent = {};
        this.pendingCallExtendedTypes = {};
        this.synchronizedCallDevices = {
            googleMeet: {},
            pstn: {},
            teams: {},
            video: {},
            voip: {},
            zoom: {},
        };
        this.synchronizedCalls = {};
        this.synchronizedCallsPlaced = {};
        (_a = this.company) === null || _a === void 0 ? void 0 : _a.$offChange(this.onCompanyChanged);
        this.company = undefined;
        (_b = this.site) === null || _b === void 0 ? void 0 : _b.$offChange(this.onSiteChanged);
        this.site = undefined;
        (_c = this.roomConfig) === null || _c === void 0 ? void 0 : _c.$offChange(this.onRoomConfigChanged);
        this.roomConfig = undefined;
        this.cleanupCallsTimeout.clear();
        this.updateCapsAndOnlineStatus();
        this.roomVolumeSaveTimeout.clear();
        this.dialNumberAfterPrefixTimeouts.clearAll();
        this.hangupSynchronizedCallTimeouts.clearAll();
        this.equipmentIdsInRoomConfig.length = 0;
        for (const displayMotionTimeouts of Object.values(this.contactClosureTimeouts)) {
            for (const actionTimeouts of Object.values(displayMotionTimeouts)) {
                for (const contactClosureTimeout of Object.values(actionTimeouts)) {
                    contactClosureTimeout.destroy();
                }
            }
        }
        this.contactClosureTimeouts = {};
        // Clear remaining timeouts. The named timeouts should have all been cleared explicitly,
        // so this will clear the anonymous timeouts and any named timeouts we forgot to
        // explicitly clear.
        this.timeouts.clearAll();
    }
    startup() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.log.debug(`startup`);
            if (!this.room)
                throw new Error(`room undefined`);
            this.uiPreviewRan = true;
            if (this.uiPreviewTimeout.isRunning) {
                this.log.error(`startup: uiPreviewTimeout is running`);
                this.uiPreviewTimeout.clear();
            }
            if (this.isBrowser) {
                yield this.room.$activateUiPreview(this.uiPreviewControllerId);
                this.uiPreviewTimeout.set(this.onUiPreviewTimeout, defaults.inara.uiPreviewTimeoutMs);
            }
            // Pre-load roomConfig and roomConfigValidated so they are ready when we need them.
            const roomId = (_b = (_a = this.roomService.getRoom()) === null || _a === void 0 ? void 0 : _a.$id) !== null && _b !== void 0 ? _b : '';
            try {
                this.roomConfig = yield this.kayleeService.getRoomConfig(roomId).$loaded();
                yield this.kayleeService.getRoomConfigValidated(roomId).$loaded();
            }
            catch (error) {
                this.log.info(`loading of roomConfig and roomConfigValidated cancelled`);
                return;
            }
            this.roomConfig.$onChange(this.onRoomConfigChanged);
            this.room.$onChange(this.onRoomChanged);
            this.devices = {};
            this.synchronizedCallDevices = {
                googleMeet: {},
                pstn: {},
                teams: {},
                video: {},
                voip: {},
                zoom: {},
            };
            this.synchronizedCalls = {};
            this.updateCapsAndOnlineStatus();
            this.equipment = this.room.$getEquipment();
            this.equipment
                .$loaded()
                .then(resolvedEquipment => {
                if (!this.equipment)
                    throw new Error(`equipment undefined`);
                if (!this.room)
                    throw new Error(`room undefined`);
                this.equipment.$onChildRemoved(this.onEquipmentRemoved);
                this.equipment.$onChildChanged(this.onEquipmentChanged);
                this.equipment.$onChildAdded(this.onEquipmentAdded);
                this.roomControlCommands = this.room.$getCommands();
                this.roomControlCommands.$onChildAdded(this.onRoomControlCommandAdded);
            })
                .catch(error => {
                this.log.info(`loading of equipment cancelled`);
            });
            this.callUpdateManager.enable(this.room);
            yield this.directoryManager.enable(this.room);
            this.company = this.kayleeService.getCompany(this.room.companyId);
            this.company.$onChange(this.onCompanyChanged);
            this.site = this.kayleeService.getSite(this.room.siteId);
            this.site.$onChange(this.onSiteChanged);
        });
    }
    stopContent(equipmentId, sourceId) {
        var _a, _b, _c;
        const display = (_a = this.caps.displays) === null || _a === void 0 ? void 0 : _a[equipmentId];
        if (!display) {
            this.log.warning(`equipmentId ${equipmentId} not present in displays`);
            return;
        }
        const source = (_b = display.sources) === null || _b === void 0 ? void 0 : _b[sourceId];
        if (!source) {
            this.log.error(`sourceId ${sourceId} not present in sources ` +
                `for display with equipmentId ${equipmentId}`);
            return;
        }
        const outId = this.getVideoOutputIds(equipmentId)[0];
        const sourcePath = (_c = this.getPathToDisplaySource(source, equipmentId, outId)) !== null && _c !== void 0 ? _c : [];
        for (const entry of sourcePath) {
            if (entry.farContent) {
                this.devices[entry.equipmentId].set('stopContent', entry.videoInputId);
            }
        }
    }
    updateAllContentStates() {
        var _a, _b, _c;
        if (!this.room)
            throw new Error(`room undefined`);
        const roomValues = this.room.$getValues();
        const displays = (_b = (_a = roomValues.states) === null || _a === void 0 ? void 0 : _a.displays) !== null && _b !== void 0 ? _b : {};
        for (const display of Object.values(displays)) {
            for (const source of Object.values((_c = display.sources) !== null && _c !== void 0 ? _c : {})) {
                delete source.state;
            }
        }
        for (const value of this.contentStateValues) {
            this.updateContentState(value.equipmentId, value.inputId, value.state, roomValues);
        }
        this.room.$set(roomValues, false, false);
    }
    updateCapsAndOnlineStatus(full = true) {
        this.updateCapsAndOnlineStatusFull || (this.updateCapsAndOnlineStatusFull = full);
        this.log.debug(`updateCapsAndOnlineStatus (full = ${full})`);
        this.updateCapsAndOnlineStatusTimeout.set(this.onUpdateCapsAndOnlineStatusTimeout, 3000);
    }
    updateConnectState(dev) {
        var _a, _b;
        const connectState = this.computeConnectState(dev);
        const eq = (_a = this.equipment) === null || _a === void 0 ? void 0 : _a.$recordAt((_b = dev.eqId) !== null && _b !== void 0 ? _b : '');
        if (!eq) {
            // this can happen right after equipment is removed
            this.log.info(`equipment undefined for id ${dev.eqId}`);
            return;
        }
        if (connectState !== eq.connectState) {
            eq.connectState = connectState;
            if (connectState !== 'connecting') {
                eq.finalConnectState = eq.connectState;
            }
            eq.$save(false, false).catch(this.handleSaveError);
        }
    }
    updateConnectStates() {
        for (const dev of Object.values(this.devices)) {
            this.updateConnectState(dev);
        }
    }
    updateContentState(equipmentId, inputId, state, roomValues) {
        // goal: set room.states.displays.<displayId>.sources.<sourceId>.state = state
        // strategy: look at room.caps.displays.<displayId>.sources.<sourceId> to find one that
        // matches the specified equipmentId and inputId
        var _a, _b, _c, _d, _e, _f, _g;
        if (!inputId)
            return;
        if (!((_a = roomValues.caps) === null || _a === void 0 ? void 0 : _a.displays))
            return;
        // First, traverse the video switching topology to find the leaf node corresponding to this
        // equipmentId and inputId.
        let dev = this.devices[equipmentId];
        if (!dev)
            throw new Error(`device undefined`);
        let source = (_c = (_b = dev.caps.videoInputs) === null || _b === void 0 ? void 0 : _b[inputId]) === null || _c === void 0 ? void 0 : _c.source;
        let leafEquipmentId = equipmentId;
        let leafInputId = inputId;
        while (source) {
            leafEquipmentId = source.equipmentId;
            dev = this.devices[leafEquipmentId];
            if (!dev)
                throw new Error(`device undefined`);
            leafInputId = this.videoSwitcherStates[leafEquipmentId].routes[source.videoOutputId];
            if (!leafInputId)
                return;
            source = (_e = (_d = dev.caps.videoInputs) === null || _d === void 0 ? void 0 : _d[leafInputId]) === null || _e === void 0 ? void 0 : _e.source;
        }
        // Then, find the matching display(s) and source(s).
        for (const [displayId, display] of Object.entries(roomValues.caps.displays)) {
            const outId = this.getVideoOutputIds(displayId)[0];
            for (const [sourceId, source] of Object.entries((_f = display.sources) !== null && _f !== void 0 ? _f : {})) {
                if (leafEquipmentId === source.equipmentId &&
                    leafInputId === source.videoInputId &&
                    this.displayPathContainsInput((_g = this.getPathToDisplaySource(source, displayId, outId)) !== null && _g !== void 0 ? _g : [], equipmentId, inputId)) {
                    const path = ['states', 'displays', displayId, 'sources', sourceId, 'state'];
                    const currentState = Sutl.getProp(roomValues, path);
                    if (currentState !== state) {
                        Sutl.setNode(roomValues, path, state);
                    }
                }
            }
        }
    }
    updateContentStates(equipmentId, inputId, state) {
        // Maintain contentStateValues so we can use them to recompute the content state in
        // updateVideoSwitcherStates when the video switcher states change.
        this.contentStateValues = this.contentStateValues.filter(value => {
            return !(value.equipmentId === equipmentId && value.inputId === inputId);
        });
        if (state) {
            this.contentStateValues.push({
                equipmentId: equipmentId,
                inputId: inputId,
                state: state,
            });
        }
        // Recompute and set all content state values.
        this.updateAllContentStates();
    }
    updateDevice(equipmentId, ignoreUndefinedDevice = false) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12;
        if (!this.equipment) {
            if (ignoreUndefinedDevice)
                return;
            throw new Error(`equipment undefined`);
        }
        const eq = this.equipment.$recordAt(equipmentId);
        if (!eq) {
            this.log.error(`got equipment changed for ${equipmentId}, but eq is undefined`);
            return;
        }
        this.log.debug(`updateDevice: eq = ${eq}`);
        const dev = this.devices[equipmentId];
        if (dev === undefined) {
            if (!ignoreUndefinedDevice) {
                this.log.debug(`ignoring equipment changed event since device hasn't been added yet`);
            }
            return;
        }
        this.log.debug(`updateDevice: dev = ${dev}`);
        if (eq.type !== dev.eqType || (!this.isBrowser && eq.simulate !== dev.simulate)) {
            this.removeDevice(equipmentId);
            this.addDevice(equipmentId);
        }
        else {
            for (const callType of callTypes) {
                if ((_a = dev.config.synchronizedCalls) === null || _a === void 0 ? void 0 : _a[callType]) {
                    this.synchronizedCallDevices[callType][equipmentId] = dev;
                }
                else {
                    delete this.synchronizedCallDevices[callType][equipmentId];
                }
            }
            const ipAddressChanged = eq.ipAddress !== dev.ipAddress;
            const passwordChanged = (eq.password === undefined && dev.password !== undefined) ||
                (eq.password !== undefined && dev.password === undefined) ||
                (eq.password !== undefined &&
                    dev.password !== undefined &&
                    eq.password !== dev.password);
            const usernameChanged = (eq.username === undefined && dev.username !== undefined) ||
                (eq.username !== undefined && dev.username === undefined) ||
                (eq.username !== undefined &&
                    dev.username !== undefined &&
                    eq.username !== dev.username);
            const newPort = (_b = eq.config) === null || _b === void 0 ? void 0 : _b.port;
            const oldPort = (_c = dev.config) === null || _c === void 0 ? void 0 : _c.port;
            const portChanged = (newPort === undefined && oldPort !== undefined) ||
                (newPort !== undefined && oldPort === undefined) ||
                (newPort !== undefined && oldPort !== undefined && newPort !== oldPort);
            const driverIdChanged = dev.key !== this.getDriverId(eq);
            const modelNumberChanged = eq.modelNumber !== dev.config.modelNumber;
            const channelFavoritesChanged = !Sutl.objEquals((_d = eq.config) === null || _d === void 0 ? void 0 : _d.channelFavorites, dev.config.channelFavorites);
            const cameraTrackingUiEnabledChanged = ((_e = eq.config) === null || _e === void 0 ? void 0 : _e.cameraTrackingUiEnabled) !== dev.config.cameraTrackingUiEnabled;
            const displayInfoChanged = ((_f = eq.config) === null || _f === void 0 ? void 0 : _f.displayIndex) !== dev.config.displayIndex ||
                ((_g = eq.config) === null || _g === void 0 ? void 0 : _g.displayBlankIndex) !== dev.config.displayBlankIndex ||
                ((_h = eq.config) === null || _h === void 0 ? void 0 : _h.displayBlankUiEnabled) !== dev.config.displayBlankUiEnabled ||
                ((_j = eq.config) === null || _j === void 0 ? void 0 : _j.displayFreezeIndex) !== dev.config.displayFreezeIndex ||
                ((_k = eq.config) === null || _k === void 0 ? void 0 : _k.displayFreezeUiEnabled) !== dev.config.displayFreezeUiEnabled ||
                ((_l = eq.config) === null || _l === void 0 ? void 0 : _l.displayVolumeUiEnabled) !== dev.config.displayVolumeUiEnabled ||
                !Sutl.objEquals((_m = eq.config) === null || _m === void 0 ? void 0 : _m.sourceMenu, dev.config.sourceMenu) ||
                ((_o = eq.config) === null || _o === void 0 ? void 0 : _o.name) !== dev.config.name;
            const displayMotionControlsChanged = Object.keys((_q = (_p = eq.config) === null || _p === void 0 ? void 0 : _p.displayMotion) !== null && _q !== void 0 ? _q : {})
                .concat(Object.keys((_s = (_r = dev.config) === null || _r === void 0 ? void 0 : _r.displayMotion) !== null && _s !== void 0 ? _s : {}))
                .reduce((changed, key) => {
                var _a, _b, _c;
                const curr = (_b = (_a = eq.config) === null || _a === void 0 ? void 0 : _a.displayMotion) === null || _b === void 0 ? void 0 : _b[key];
                const prev = (_c = dev.config.displayMotion) === null || _c === void 0 ? void 0 : _c[key];
                return (changed ||
                    (prev === null || prev === void 0 ? void 0 : prev.index) !== (curr === null || curr === void 0 ? void 0 : curr.index) ||
                    (prev === null || prev === void 0 ? void 0 : prev.label) !== (curr === null || curr === void 0 ? void 0 : curr.label) ||
                    (prev === null || prev === void 0 ? void 0 : prev.showManualControls) !== (curr === null || curr === void 0 ? void 0 : curr.showManualControls));
            }, false);
            const hasDisplayMotion = ((_t = eq.config) === null || _t === void 0 ? void 0 : _t.displayMotion) !== undefined;
            const hideCameraSwitcherChanged = ((_u = eq.config) === null || _u === void 0 ? void 0 : _u.hideCameraSwitcher) !== dev.config.hideCameraSwitcher;
            const hideDisplayChanged = ((_v = eq.config) === null || _v === void 0 ? void 0 : _v.hideDisplay) !== dev.config.hideDisplay;
            const synchronizedCallsChanged = !Sutl.objEquals((_w = eq.config) === null || _w === void 0 ? void 0 : _w.synchronizedCalls, dev.config.synchronizedCalls);
            // Figure out if the audio controls have changed. Account for bug/weirdness that the
            // audio control channels are in:
            //
            // - dev.config.<prop> for old drivers
            // - dev.config.<configName>.manual.<prop> for new drivers
            const configName = channelsConfigNames.find(name => { var _a; return dev.config[name] !== undefined || ((_a = eq.config) === null || _a === void 0 ? void 0 : _a[name]) !== undefined; });
            let audioControlsChanged = false;
            if (configName) {
                const devConfig = (_y = (_x = dev.config[configName]) === null || _x === void 0 ? void 0 : _x.manual) !== null && _y !== void 0 ? _y : dev.config;
                audioControlsChanged = [
                    'amplifier',
                    'assistiveListening',
                    'audioInputSources',
                    'mics',
                    'overflowRooms',
                    'soundReinforcement',
                    'telephony',
                ].some(prop => {
                    var _a, _b;
                    return !Sutl.objEquals(Sutl.getProp(devConfig, prop), Sutl.getProp((_b = (_a = eq.config) === null || _a === void 0 ? void 0 : _a[configName]) === null || _b === void 0 ? void 0 : _b.manual, prop));
                });
            }
            const userDriverConfigChanged = !Sutl.objEquals((_z = eq.config) === null || _z === void 0 ? void 0 : _z.userDriver, dev.config.userDriver);
            if (ipAddressChanged)
                this.log.debug(`ipAddress changed`);
            if (passwordChanged)
                this.log.debug(`password changed`);
            if (usernameChanged)
                this.log.debug(`username changed`);
            if (portChanged)
                this.log.debug(`port changed`);
            if (driverIdChanged)
                this.log.debug(`driver id changed`);
            if (modelNumberChanged)
                this.log.debug(`model number changed`);
            if (userDriverConfigChanged)
                this.log.debug(`user driver config changed`);
            if (ipAddressChanged ||
                passwordChanged ||
                usernameChanged ||
                portChanged ||
                driverIdChanged ||
                modelNumberChanged ||
                userDriverConfigChanged) {
                this.removeDevice(equipmentId);
                this.addDevice(equipmentId);
            }
            else {
                const type = (_0 = this.kayleeService.getEquipmentTypes().$recordAt(eq.type)) === null || _0 === void 0 ? void 0 : _0.type;
                if (type === undefined)
                    throw new Error(`type undefined`);
                const config = Object.assign(Object.assign({}, Sutl.deepCopy((_1 = eq.config) !== null && _1 !== void 0 ? _1 : {})), { equipmentId, equipmentType: eq.type, ipAddress: eq.ipAddress, simulate: eq.simulate });
                if (eq.modelNumber !== undefined)
                    config.modelNumber = eq.modelNumber;
                // Augment the config with the video outputs from the equipment type definition.
                const videoOutputs = (_2 = eq.$getVideoOutputs(false)) !== null && _2 !== void 0 ? _2 : (_5 = (_4 = (_3 = config.userDriver) === null || _3 === void 0 ? void 0 : _3.controls) === null || _4 === void 0 ? void 0 : _4.videoMatrixRoutes) === null || _5 === void 0 ? void 0 : _5.videoOutputs;
                if (videoOutputs) {
                    if (Array.isArray(videoOutputs)) {
                        config.videoOutputs = Sutl.arrayToObj(videoOutputs);
                    }
                    else {
                        config.videoOutputs = Sutl.deepCopy(videoOutputs);
                    }
                }
                if (!((_7 = (_6 = eq.config) === null || _6 === void 0 ? void 0 : _6.useDirectory) !== null && _7 !== void 0 ? _7 : false)) {
                    this.directoryManager.updateEquipmentDirectory(equipmentId, undefined);
                }
                // Update synchronized call devices.
                for (const callType of callTypes) {
                    if ((_9 = (_8 = eq.config) === null || _8 === void 0 ? void 0 : _8.synchronizedCalls) === null || _9 === void 0 ? void 0 : _9[callType]) {
                        this.synchronizedCallDevices[callType][equipmentId] = dev;
                    }
                    else {
                        delete this.synchronizedCallDevices[callType][equipmentId];
                    }
                }
                // Update any other equipment that has derived config properties from this
                // equipment's config.
                const otherEquipmentIdsToUpdate = [];
                for (const displayMotion of Object.values((_10 = config.displayMotion) !== null && _10 !== void 0 ? _10 : {})) {
                    for (const control of Object.values((_11 = displayMotion.controls) !== null && _11 !== void 0 ? _11 : {})) {
                        for (const action of Object.values((_12 = control === null || control === void 0 ? void 0 : control.actions) !== null && _12 !== void 0 ? _12 : {})) {
                            if (action.type === 'contactClosure') {
                                if (!otherEquipmentIdsToUpdate.includes(action.equipmentId)) {
                                    otherEquipmentIdsToUpdate.push(action.equipmentId);
                                }
                            }
                        }
                    }
                }
                for (const equipmentId of otherEquipmentIdsToUpdate) {
                    const otherDev = this.devices[equipmentId];
                    if (otherDev === undefined)
                        continue;
                    otherDev.updateConfig(Object.assign({}, otherDev.config));
                }
                // TODO: drivers in the new framework always return false for updateConfig since
                // they send their caps asynchronously. Once all drivers have been converted to the
                // new framework, we can remove the check on the return value of updateConfig
                // from the following if statement.
                if (dev.updateConfig(config) ||
                    audioControlsChanged ||
                    channelFavoritesChanged ||
                    cameraTrackingUiEnabledChanged ||
                    displayInfoChanged ||
                    displayMotionControlsChanged ||
                    hasDisplayMotion ||
                    hideCameraSwitcherChanged ||
                    hideDisplayChanged ||
                    synchronizedCallsChanged) {
                    this.updateCapsAndOnlineStatus();
                }
            }
        }
    }
    updateDisabledCalls(dev) {
        var _a, _b, _c, _d;
        if (!this.room)
            throw new Error(`room undefined`);
        const roomValues = this.room.$getValues();
        for (const callType of callTypes) {
            let disabledCalls = 0;
            for (const dev of Object.values(this.onlineDevices)) {
                disabledCalls += (_b = (_a = dev.caps.disabledCalls) === null || _a === void 0 ? void 0 : _a[callType]) !== null && _b !== void 0 ? _b : 0;
            }
            if (((_d = (_c = roomValues.caps) === null || _c === void 0 ? void 0 : _c.disabledCalls) === null || _d === void 0 ? void 0 : _d[callType]) !== disabledCalls) {
                Sutl.setNode(roomValues, ['caps', 'disabledCalls', callType], disabledCalls);
            }
        }
        if (!dev.eqId)
            throw new Error(`dev.eqId undefined`);
        this.savedCaps[dev.eqId] = Sutl.deepCopy(dev.caps);
        this.room.$set(roomValues, false, false);
    }
    updateDisplaySourceState(dev, stateName, value, roomValues) {
        var _a, _b, _c, _d, _e;
        if (!this.room)
            throw new Error(`room undefined`);
        if (!roomValues)
            roomValues = this.room.$getValues();
        const displays = (_b = (_a = this.room.caps) === null || _a === void 0 ? void 0 : _a.displays) !== null && _b !== void 0 ? _b : {};
        let changed = false;
        for (const [displayId, display] of Object.entries(displays)) {
            for (const [sourceId, source] of Object.entries((_c = display.sources) !== null && _c !== void 0 ? _c : {})) {
                if (source.equipmentId === dev.eqId) {
                    if (value === null) {
                        changed = true;
                        const roomValuesSources = (_e = (_d = roomValues.states) === null || _d === void 0 ? void 0 : _d.displays) === null || _e === void 0 ? void 0 : _e[displayId].sources;
                        roomValuesSources === null || roomValuesSources === void 0 ? true : delete roomValuesSources[sourceId][stateName];
                    }
                    else if (typeof value === 'string') {
                        changed = true;
                        Sutl.setProp(roomValues, ['states', 'displays', displayId, 'sources', sourceId, stateName], value);
                    }
                    else if (stateName === 'channel' &&
                        ('channel' in value || 'name' in value || 'program' in value)) {
                        changed = true;
                        Sutl.setProp(roomValues, ['states', 'displays', displayId, 'sources', sourceId, stateName], value);
                    }
                    else if ('videoInputId' in value &&
                        source.videoInputId === value.videoInputId) {
                        changed = true;
                        Sutl.setProp(roomValues, ['states', 'displays', displayId, 'sources', sourceId, stateName], value.value);
                    }
                }
            }
        }
        if (changed)
            this.room.$set(roomValues, false, false);
    }
    updateSynchronizedCallParticipants(parentCallId, childCallId) {
        if (this.room === undefined)
            return;
        const calls = this.room.$getCalls(defaults.inara.numRecentCalls);
        const parentCall = calls.$recordAt(parentCallId);
        if (!parentCall) {
            this.log.error(`no parent synchronized call with id "${parentCallId}"`);
            return;
        }
        const parentDev = this.devicesByCallId[parentCallId];
        if (!parentDev) {
            this.log.error(`no device for call id "${parentCallId}"`);
            return;
        }
        const parentParticipants = parentCall.$getParticipants();
        const childCallIds = [];
        if (childCallId) {
            childCallIds.push(childCallId);
        }
        else {
            for (const [syncCallId, syncCall] of Object.entries(this.synchronizedCalls)) {
                if (syncCall.parentCallId === parentCallId) {
                    childCallIds.push(syncCallId);
                }
            }
        }
        for (const childCallId of childCallIds) {
            const childCall = this.synchronizedCalls[childCallId];
            if (!childCall)
                continue;
            for (const participant of parentParticipants) {
                if (participant.userId === childCall.userId && !participant.isSynchronizedCall) {
                    let participantUpdate = Object.assign(Object.assign({}, participant), { isSynchronizedCall: true });
                    delete participantUpdate.$id;
                    this.callUpdateManager.updateParticipant(parentCallId, participant.$id, participantUpdate);
                }
            }
        }
    }
    updateValidatedStates() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.equipment)
                throw new Error(`equipment undefined`);
            const setPromises = [];
            // Compute equipment validated states.
            for (const [equipmentId, dev] of Object.entries(this.devices)) {
                if (dev.connectState !== 'online' && dev.connectState !== 'controlFailed')
                    continue;
                const tmpValidated = Object.assign({}, ((_a = this.driverValidated[equipmentId]) !== null && _a !== void 0 ? _a : {}));
                delete tmpValidated.contactClosures; // included in display equipment
                delete tmpValidated.shadeControls; // included in room config validated
                delete tmpValidated.shadePresets; // included in room config validated
                const validated = tmpValidated;
                let isModified = false;
                const eq = this.equipment.$recordAt(equipmentId);
                if (!eq) {
                    this.log.error(`eq undefined for id ${dev.eqId}`);
                    continue;
                }
                if (dev.caps.hasVideoInputs && ((_b = eq.config) === null || _b === void 0 ? void 0 : _b.videoInputs)) {
                    validated.videoInputs = {};
                    isModified = true;
                    for (const [id, videoInput] of Object.entries(eq.config.videoInputs)) {
                        if ('source' in videoInput) {
                            const outEqId = videoInput.source.equipmentId;
                            if (outEqId in this.onlineDevices) {
                                const videoOutputId = videoInput.source.videoOutputId;
                                const outEq = this.equipment.$recordAt(outEqId);
                                if (!outEq) {
                                    this.log.error(`no eq for id ${outEqId} in videoInputs config of ${dev.eqId}`);
                                    validated.videoInputs[id] = 'missing';
                                    continue;
                                }
                                validated.videoInputs[id] = ((_c = outEq.$getVideoOutputs()) === null || _c === void 0 ? void 0 : _c[videoOutputId])
                                    ? 'ok'
                                    : 'missing';
                            }
                            else {
                                // If the video output device is offline, assume it will be valid when
                                // it comes online.
                                validated.videoInputs[id] = 'ok';
                            }
                        }
                        else {
                            const availableVideoInputs = eq.$getAvailableVideoInputs();
                            const videoInputExists = Object.values(availableVideoInputs !== null && availableVideoInputs !== void 0 ? availableVideoInputs : {}).find(vi => vi.code === videoInput.code) !== undefined;
                            validated.videoInputs[id] = videoInputExists ? 'ok' : 'missing';
                        }
                    }
                }
                if (dev.caps.hasVideoInputs && ((_d = eq.config) === null || _d === void 0 ? void 0 : _d.sourceMenu)) {
                    // Use configured caps for computing sourceMenu validated results. We use caps
                    // because the caps have already taken into account the dynamic video inputs from
                    // devices like Zoom. We use configured as opposed to current caps because the
                    // current caps removes entries that aren't currently present.
                    validated.sourceMenu = {};
                    isModified = true;
                    for (const [id, displaySource] of Object.entries(eq.config.sourceMenu)) {
                        const sourceEqId = displaySource.equipmentId;
                        const sourceDev = this.devices[sourceEqId];
                        if (sourceDev === undefined) {
                            this.log.error(`no device for eqId ${sourceEqId} in source menu of ${dev.eqId}`);
                            validated.sourceMenu[id] = 'missing';
                        }
                        const videoInputs = sourceDev.caps.videoInputs;
                        validated.sourceMenu[id] = (videoInputs === null || videoInputs === void 0 ? void 0 : videoInputs[displaySource.videoInputId])
                            ? 'ok'
                            : 'missing';
                    }
                }
                for (const [displayMotionId, displayMotion] of Object.entries((_f = (_e = eq.config) === null || _e === void 0 ? void 0 : _e.displayMotion) !== null && _f !== void 0 ? _f : {})) {
                    for (const [controlId, control] of Object.entries((_g = displayMotion.controls) !== null && _g !== void 0 ? _g : {})) {
                        for (const [actionId, action] of Object.entries((_h = control === null || control === void 0 ? void 0 : control.actions) !== null && _h !== void 0 ? _h : {})) {
                            if (action.type !== 'contactClosure')
                                continue;
                            isModified = true;
                            Sutl.setProp(validated, [
                                'displayMotion',
                                displayMotionId,
                                'controls',
                                controlId,
                                'actions',
                                actionId,
                                'config',
                            ], (_l = (_k = (_j = this.driverValidated[action.equipmentId]) === null || _j === void 0 ? void 0 : _j.contactClosures) === null || _k === void 0 ? void 0 : _k[actionId]) !== null && _l !== void 0 ? _l : 'offline');
                        }
                    }
                }
                if (isModified)
                    setPromises.push(eq.$setValidatedState(validated));
            }
            // Compute room validated states.
            const roomConfig = this.roomConfig;
            if (!roomConfig)
                throw new Error(`roomConfig undefined`);
            const roomConfigValidated = {};
            for (const [shadeControlId, shadeControl] of Object.entries((_m = roomConfig.shadeControls) !== null && _m !== void 0 ? _m : {})) {
                if (shadeControl.type === 'emittedAction') {
                    const positionValidated = {};
                    const tiltValidated = {};
                    for (const motion of ['close', 'open', 'stop']) {
                        const positions = shadeControl.position;
                        const emittedActions = positions === null || positions === void 0 ? void 0 : positions[motion];
                        const actionsValidated = {};
                        if (emittedActions === undefined)
                            continue;
                        for (const [actionId, action] of Object.entries((_o = emittedActions.actions) !== null && _o !== void 0 ? _o : {})) {
                            const connectState = (_q = (_p = this.devices[action.equipmentId]) === null || _p === void 0 ? void 0 : _p.connectState) !== null && _q !== void 0 ? _q : 'offline';
                            if (connectState === 'offline') {
                                actionsValidated[actionId] = { config: 'offline' };
                            }
                            else {
                                actionsValidated[actionId] = {
                                    config: (_t = (_s = (_r = this.driverValidated[action.equipmentId]) === null || _r === void 0 ? void 0 : _r.contactClosures) === null || _s === void 0 ? void 0 : _s[actionId]) !== null && _t !== void 0 ? _t : 'offline',
                                };
                            }
                            positionValidated[motion] = { actions: actionsValidated };
                        }
                    }
                    for (const motion of ['decrease', 'increase', 'stop']) {
                        const tilts = shadeControl.tilt;
                        const emittedActions = tilts === null || tilts === void 0 ? void 0 : tilts[motion];
                        const actionsValidated = {};
                        if (emittedActions === undefined)
                            continue;
                        for (const [actionId, action] of Object.entries((_u = emittedActions.actions) !== null && _u !== void 0 ? _u : {})) {
                            const connectState = (_w = (_v = this.devices[action.equipmentId]) === null || _v === void 0 ? void 0 : _v.connectState) !== null && _w !== void 0 ? _w : 'offline';
                            if (connectState === 'offline') {
                                actionsValidated[actionId] = { config: 'offline' };
                            }
                            else {
                                actionsValidated[actionId] = {
                                    config: (_z = (_y = (_x = this.driverValidated[action.equipmentId]) === null || _x === void 0 ? void 0 : _x.contactClosures) === null || _y === void 0 ? void 0 : _y[actionId]) !== null && _z !== void 0 ? _z : 'offline',
                                };
                            }
                            tiltValidated[motion] = { actions: actionsValidated };
                        }
                    }
                    if (!roomConfigValidated.shadeControls)
                        roomConfigValidated.shadeControls = {};
                    roomConfigValidated.shadeControls[shadeControlId] = {
                        position: positionValidated,
                        tilt: tiltValidated,
                    };
                }
                else if (shadeControl.type == 'equipment') {
                    const connectState = (_1 = (_0 = this.devices[shadeControl.equipmentId]) === null || _0 === void 0 ? void 0 : _0.connectState) !== null && _1 !== void 0 ? _1 : 'offline';
                    if (!roomConfigValidated.shadeControls)
                        roomConfigValidated.shadeControls = {};
                    if (connectState === 'offline') {
                        roomConfigValidated.shadeControls[shadeControlId] = 'offline';
                    }
                    else {
                        const driverValidated = (_3 = (_2 = this.driverValidated[shadeControl.equipmentId]) === null || _2 === void 0 ? void 0 : _2.shadeControls) === null || _3 === void 0 ? void 0 : _3[shadeControlId];
                        roomConfigValidated.shadeControls[shadeControlId] =
                            driverValidated === undefined ? 'offline' : { config: driverValidated };
                    }
                }
                else {
                    this.log.error(`unhandled shade control type`);
                }
            }
            for (const [shadePresetId, shadePreset] of Object.entries((_4 = roomConfig.shadePresets) !== null && _4 !== void 0 ? _4 : {})) {
                if (shadePreset.type === 'equipment') {
                    const connectState = (_6 = (_5 = this.devices[shadePreset.equipmentId]) === null || _5 === void 0 ? void 0 : _5.connectState) !== null && _6 !== void 0 ? _6 : 'offline';
                    if (!roomConfigValidated.shadePresets)
                        roomConfigValidated.shadePresets = {};
                    if (connectState === 'offline') {
                        roomConfigValidated.shadePresets[shadePresetId] = 'offline';
                    }
                    else {
                        const driverValidated = (_8 = (_7 = this.driverValidated[shadePreset.equipmentId]) === null || _7 === void 0 ? void 0 : _7.shadePresets) === null || _8 === void 0 ? void 0 : _8[shadePresetId];
                        roomConfigValidated.shadePresets[shadePresetId] =
                            driverValidated === undefined ? 'offline' : { config: driverValidated };
                    }
                }
                else {
                    this.log.error(`unhandled shade preset type`);
                }
            }
            Sutl.pruneEmptyObjects(roomConfigValidated);
            const roomId = (_10 = (_9 = this.roomService.getRoom()) === null || _9 === void 0 ? void 0 : _9.$id) !== null && _10 !== void 0 ? _10 : '';
            const roomConfigValidatedObj = this.kayleeService.getRoomConfigValidated(roomId);
            setPromises.push(roomConfigValidatedObj.$set(roomConfigValidated));
            // Set equipment and room validated states in the database, then update the connect states.
            yield Promise.all(setPromises);
            if (setPromises.length > 0)
                this.updateConnectStates();
        });
    }
    updateVideoInputIncidentals(dev) {
        var _a, _b, _c, _d;
        if (!this.room)
            throw new Error(`room undefined`);
        const displays = (_b = (_a = this.room.caps) === null || _a === void 0 ? void 0 : _a.displays) !== null && _b !== void 0 ? _b : {};
        const inputs = (_c = dev.caps.videoInputs) !== null && _c !== void 0 ? _c : {};
        const roomValues = this.room.$getValues();
        for (const [displayId, display] of Object.entries(displays)) {
            for (const [sourceId, source] of Object.entries((_d = display.sources) !== null && _d !== void 0 ? _d : {})) {
                if (source.equipmentId !== dev.eqId)
                    continue;
                for (const [inputId, input] of Object.entries(inputs)) {
                    if (source.videoInputId !== inputId)
                        continue;
                    const instructions = input.instructions;
                    const instructionsPath = [
                        'caps',
                        'displays',
                        displayId,
                        'sources',
                        sourceId,
                        'instructions',
                    ];
                    if (instructions === undefined) {
                        Sutl.deleteNode(roomValues, instructionsPath);
                    }
                    else {
                        Sutl.setNode(roomValues, instructionsPath, Sutl.deepCopy(instructions));
                    }
                    const unavailableReason = input
                        .unavailableReason;
                    const unavailableReasonPath = [
                        'caps',
                        'displays',
                        displayId,
                        'sources',
                        sourceId,
                        'unavailableReason',
                    ];
                    if (unavailableReason === undefined) {
                        Sutl.deleteNode(roomValues, unavailableReasonPath);
                    }
                    else {
                        Sutl.setNode(roomValues, unavailableReasonPath, Sutl.deepCopy(unavailableReason));
                    }
                }
            }
        }
        if (!dev.eqId)
            throw new Error(`dev.eqId undefined`);
        this.savedCaps[dev.eqId] = Sutl.deepCopy(dev.caps);
        this.room.$set(roomValues, false, false);
    }
    // This is a lightweight version of updateCapsAndOnlineStatus that handles updating the video
    // switching topology. It assumes the online status of the devices hasn't changed, and only
    // the detected video inputs and ouputs have changed.
    updateVideoSwitcherCaps() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.room || !this.isOnline) {
                this.log.error(`expected room to be online`);
                return;
            }
            // Compute new partial caps, then merge them into existing caps.
            let caps = {};
            let configuredCaps = {};
            this.computeDisplayCaps(this.onlineDevices, caps, true);
            this.computeDisplayCaps(this.devices, configuredCaps, false);
            caps = Object.assign(Object.assign({}, this.caps), caps);
            configuredCaps = Object.assign(Object.assign({}, this.configuredCaps), configuredCaps);
            if (Sutl.objEquals(caps, this.caps) &&
                Sutl.objEquals(configuredCaps, this.configuredCaps)) {
                return;
            }
            this.caps = caps;
            this.configuredCaps = configuredCaps;
            // Update the video-related validated states.
            yield this.updateValidatedStates();
            // Copy new caps to room and query video switching topology routes to get values for any
            // new routes that may have been added.
            if (this.room && this.isOnline) {
                const roomValues = this.room.$getValues();
                roomValues.caps = Sutl.deepCopy(this.caps);
                roomValues.configuredCaps = Sutl.deepCopy(this.configuredCaps);
                this.log.debug(`caps = ${JSON.stringify(this.caps)}`);
                yield this.room.$set(roomValues, false, false);
                if (this.room && this.isOnline) {
                    this.queryVideoSwitcherRoutes();
                }
            }
        });
    }
    updateVideoSwitcherStates(equipmentId, inputId, outputId) {
        var _a, _b, _c, _d, _e, _f, _g;
        let changed = false;
        if (!outputId) {
            if (!this.equipment)
                throw new Error(`equipment undefined`);
            const videoOutputs = (_a = this.equipment.$recordAt(equipmentId)) === null || _a === void 0 ? void 0 : _a.$getVideoOutputs();
            if (!videoOutputs) {
                // Case 1: The device is a display with no outputs; we use a special outputId of
                // '-display-' to track the switcher's internal state -- you can think of it like
                // the output to the physical screen.
                if (((_c = (_b = this.videoSwitcherStates[equipmentId]) === null || _b === void 0 ? void 0 : _b.routes) === null || _c === void 0 ? void 0 : _c['-display-']) !== inputId) {
                    const path = [equipmentId, 'routes', '-display-'];
                    Sutl.setProp(this.videoSwitcherStates, path, inputId);
                    changed = true;
                }
            }
            else {
                // Case 2: the device is a codec with outputs defined in the equipment type
                // definition, and the internal switcher effectively routes the selected input to
                // all outputs.
                for (const outId of Object.keys(videoOutputs)) {
                    if (((_e = (_d = this.videoSwitcherStates[equipmentId]) === null || _d === void 0 ? void 0 : _d.routes) === null || _e === void 0 ? void 0 : _e[outId]) !== inputId) {
                        const path = [equipmentId, 'routes', outId];
                        Sutl.setProp(this.videoSwitcherStates, path, inputId);
                        changed = true;
                    }
                }
            }
        }
        else {
            // Case 3: the device is a video switcher with outputs defined in the device's config;
            // inputs can be mapped independently to outputs
            if (((_g = (_f = this.videoSwitcherStates[equipmentId]) === null || _f === void 0 ? void 0 : _f.routes) === null || _g === void 0 ? void 0 : _g[outputId]) !== inputId) {
                const path = [equipmentId, 'routes', outputId];
                Sutl.setProp(this.videoSwitcherStates, path, inputId);
                changed = true;
            }
        }
        if (changed) {
            this.computeDisplaySources();
            this.updateAllContentStates();
        }
    }
}
